// FinTrack — Knjizenja (PDF OCR parser + QuickBooks export)
// Kontni plan se ucitava iz Supabase baze automatski
// Output: EN konta, EN nazivi, vrednosti u RSD ili USD

import { state } from './state.js';
import { sb } from './supabase.js';
import { toast, clMdl, svgIcon, showLoading, hideLoading, openEditModal, showConfirm } from './ui.js';
import { fNum } from './utils.js';
import { lookupKonto } from './kontni-plan.js';
import XLSX from 'xlsx-js-style';
import { bindDatePicker, formatDateForInput } from './datepicker.js';

// MODULE-LOCAL STATE
let kjPdfFiles = [], kjParsedEntries = [], kjWorker = null, kjImbalancedFiles = [];

// CONFIGURABLE DETAILED ACCOUNTS
function getDetailedAccounts() {
  const el = (typeof document !== 'undefined') ? document.getElementById('kjDetailAccts') : null;
  const v = (el && el.value) || '150,153,204,205,430,435,436';
  return v.split(',').map(s => s.trim()).filter(Boolean);
}

// Kada je false: parser NE izmišlja iznose da bi forsirao balans (GAP-FILL/ZERO-FIX/OCR-10x/
// AUTO-FIX/BALANCE-HEADER/raspodela...). Čita SAMO ono što OCR stvarno pročita; ako se ne slaže,
// dokument se prijavi kao neuravnotežen (balans-gard) za ručnu ispravku — bez ubacivanja izmišljenih
// vrednosti koje propuštaju greške. Postavi na true da vratiš staro ponašanje (forsiranje balansa).
const FILL_GAPS = false;

// Currency toggle
export function toggleKursInput() {
  const cur = document.getElementById('kjCurrency')?.value || 'USD';
  const wrap = document.getElementById('kjKursWrap');
  if (wrap) wrap.style.display = cur === 'USD' ? 'block' : 'none';
  if (kjParsedEntries.length) renderKjResults();
}

function getKurs() {
  const cur = document.getElementById('kjCurrency')?.value || 'USD';
  if (cur === 'RSD') return 1;
  return parseFloat(document.getElementById('kjKurs')?.value) || 100.29;
}

function getCurrencyLabel() {
  return (document.getElementById('kjCurrency')?.value || 'USD');
}

// PDF.js worker setup — called from initKnjizenja()
export function initKnjizenja() {
  if (typeof window.pdfjsLib !== 'undefined') {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
}

// FILES UPLOAD — knjiženja stižu kao PDF (skenirani nalog) ili kao Excel (ERP eksport)
const isPdfFile = f => /\.pdf$/i.test(f.name);
const isExcelFile = f => /\.xlsx?$/i.test(f.name);

export function handlePdfFiles(inp) {
  if (!inp.files.length) return;
  const picked = Array.from(inp.files);
  const ok = picked.filter(f => isPdfFile(f) || isExcelFile(f));
  const bad = picked.length - ok.length;
  if (bad) toast(bad + ' fajl(ova) preskočeno — podržani su .pdf, .xlsx i .xls', 'warn');
  kjPdfFiles.push(...ok);
  inp.value = '';
  renderPdfList();
}

function removePdfFile(idx) {
  kjPdfFiles.splice(idx, 1);
  renderPdfList();
}

function renderPdfList() {
  const list = document.getElementById('pdfList');
  list.textContent = '';
  if (!kjPdfFiles.length) { updateKjBtnState(); return; }
  kjPdfFiles.forEach((f, i) => {
    const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:6px;padding:3px 0';
    const name = document.createElement('span'); name.style.cssText = 'color:var(--tx2);font-size:12px;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
    name.textContent = f.name;
    const btn = document.createElement('button'); btn.className = 'bico del'; btn.style.cssText = 'font-size:11px;padding:1px 4px;min-width:auto';
    btn.title = 'Ukloni'; btn.onclick = () => removePdfFile(i);
    // X icon via SVG DOM
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '12'); svg.setAttribute('height', '12'); svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
    const p1 = document.createElementNS('http://www.w3.org/2000/svg', 'path'); p1.setAttribute('d', 'M18 6L6 18M6 6l12 12');
    svg.appendChild(p1); btn.appendChild(svg);
    row.appendChild(name); row.appendChild(btn); list.appendChild(row);
  });
  const nPdf = kjPdfFiles.filter(isPdfFile).length;
  const nXls = kjPdfFiles.length - nPdf;
  const parts = [];
  if (nPdf) parts.push(nPdf + ' PDF');
  if (nXls) parts.push(nXls + ' Excel');
  const sp = document.createElement('div'); sp.style.cssText = 'margin-top:6px;color:var(--suc);font-weight:600;font-size:12px'; sp.textContent = parts.join(' + ') + ' fajl(ova) odabrano';
  list.appendChild(sp);
  // Thumbnails (max 6) — samo za PDF, Excel nema stranicu za render
  const thumbWrap = document.createElement('div'); thumbWrap.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-top:6px';
  kjPdfFiles.filter(isPdfFile).slice(0, 6).forEach(async (f) => {
    try {
      const buf = await f.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
      const page = await pdf.getPage(1);
      const vp = page.getViewport({ scale: 0.3 });
      const canvas = document.createElement('canvas'); canvas.width = vp.width; canvas.height = vp.height;
      canvas.style.cssText = 'border:1px solid var(--border);border-radius:4px;cursor:pointer;max-height:120px';
      canvas.title = f.name;
      canvas.onclick = () => previewPdf(f);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      thumbWrap.appendChild(canvas);
    } catch (e) { /* ignore */ }
  });
  list.appendChild(thumbWrap);
  updateKjBtnState();
}

export function updateKjBtnState() {
  const btn = document.getElementById('btnParsePdf');
  const kpSt = document.getElementById('kpStatus');
  if (btn) btn.disabled = !(state.kontniPlan.length > 0 && kjPdfFiles.length > 0);
  if (kpSt) {
    if (state.kontniPlan.length > 0) {
      kpSt.innerHTML = '';
      const s = document.createElement('span'); s.style.color = 'var(--suc)'; s.textContent = '\u2713 Kontni plan ucitan (' + state.kontniPlan.length + ' konta)'; kpSt.appendChild(s);
    } else {
      kpSt.innerHTML = '';
      const s = document.createElement('span'); s.style.color = 'var(--warn)'; s.textContent = '\u26A0 Kontni plan nije ucitan. Ucitajte ga na tabu "Kontni plan".'; kpSt.appendChild(s);
    }
  }
}

// ============================================================
// OCR + PARSING PIPELINE
// ============================================================

export async function startPdfParsing() {
  console.log('%c[FinTrack OCR] build 2026-06-14 #7 — parent koristi sopstveni total (PLATA 450)', 'color:#0a0;font-weight:bold');
  if (!kjPdfFiles.length) { toast('Odaberite PDF ili Excel fajlove', 'err'); return; }
  if (!state.kontniPlan.length) { toast('Ucitajte kontni plan na tabu Kontni plan', 'err'); return; }

  const prog = document.getElementById('ocrProgress'); prog.style.display = 'block';
  const bar = document.getElementById('ocrBar');
  const status = document.getElementById('ocrStatus');
  const btn = document.getElementById('btnParsePdf'); btn.disabled = true;
  kjParsedEntries = [];
  kjImbalancedFiles = [];
  window.__kjAllVendors = []; // Svi vendor-i pre nego closeParent obriše analitike

  // Analitički dnevnik (ako je ubačen) nije nalog — on je izvor pravog datuma knjiženja.
  // Izdvoji ga iz serije PRE parsiranja i napravi mapu vrsta|broj → datum knjiž.
  let dnevnikDates = null, dnevnikFile = '';
  const docFiles = [];
  for (const f of kjPdfFiles) {
    if (isExcelFile(f)) {
      try {
        const wbD = XLSX.read(new Uint8Array(await f.arrayBuffer()), { type: 'array' });
        const rowsD = XLSX.utils.sheet_to_json(wbD.Sheets[wbD.SheetNames[0]], { header: 1, raw: true, defval: '', blankrows: true });
        if (isDnevnikSheet(rowsD)) {
          dnevnikDates = parseDnevnikDates(rowsD);
          dnevnikFile = f.name;
          continue; // ne parsiraj ga kao nalog
        }
      } catch (e) { /* nije dnevnik — ide kao običan nalog */ }
    }
    docFiles.push(f);
  }

  const hasPdf = docFiles.some(isPdfFile);
  status.textContent = hasPdf ? 'Inicijalizujem OCR engine...' : 'Čitam Excel fajlove...';
  bar.style.width = '0%';

  try {
    kjRawOcrTexts = [];
    // OCR engine se podiže samo ako ima PDF-ova — Excel se čita direktno, bez Tesseract-a
    if (hasPdf) {
      const ocrLang = document.getElementById('kjOcrLang')?.value || 'srp+eng';
      if (kjWorker) { try { await kjWorker.terminate(); } catch (e) { /* ignore */ } }
      kjWorker = await window.Tesseract.createWorker(ocrLang, 1, {
        logger: m => { if (m.status === 'recognizing text') bar.style.width = Math.round(m.progress * 100) + '%'; }
      });
    }

    const total = docFiles.length;
    let datumIzDnevnika = 0, datumIzNaloga = 0;
    for (let fi = 0; fi < total; fi++) {
      const file = docFiles[fi];
      status.textContent = 'Parsiram ' + (fi + 1) + '/' + total + ': ' + file.name;
      bar.style.width = Math.round(fi / total * 100) + '%';

      try {
        const entries = isPdfFile(file) ? await parseSinglePdf(file) : await parseSingleExcel(file);
        // Pravi datum knjiženja iz dnevnika (ERP po njemu računa mesec, ne po datumu naloga)
        if (dnevnikDates && entries.length) {
          const key = entries[0]._docKey || docKeyFromFileName(file.name);
          const dk = key && dnevnikDates.get(key);
          if (dk) { entries.forEach(e => { e.bookingDate = dk; }); datumIzDnevnika++; }
          else { datumIzNaloga++; console.warn('[DATUM] ' + file.name + ': nema ga u dnevniku (ključ ' + (key || '?') + ') — ostaje datum naloga'); }
        }
        kjParsedEntries.push(...entries);
      } catch (e) {
        kjParsedEntries.push({ sourceFile: file.name, bookingDate: '', enCode: '', enName: '', type: '', debit: 0, credit: 0, detailLevel: 'ERROR', parentCode: '', analyticName: '', mappingStatus: 'ERROR', notes: 'Gre\u0161ka: ' + e.message });
      }
      // Excel se parsira sinhrono \u2014 pusti browser da prefarba progres na velikim serijama
      if (!isPdfFile(file) && fi % 10 === 9) await new Promise(r => setTimeout(r, 0));
    }

    let datumInfo;
    if (dnevnikDates) {
      datumInfo = ' | Datum knji\u017eenja iz dnevnika: ' + datumIzDnevnika + '/' + total +
        (datumIzNaloga ? ' (' + datumIzNaloga + ' bez datuma u dnevniku \u2192 datum naloga)' : '');
      toast('Datum knji\u017eenja preuzet iz "' + dnevnikFile + '" za ' + datumIzDnevnika + '/' + total + ' dokumenata');
    } else {
      datumInfo = ' | Datum = datum naloga (dodajte Analiti\u010dki dnevnik za pravi datum knji\u017eenja)';
    }
    status.textContent = 'Gotovo! ' + kjParsedEntries.length + ' stavki iz ' + total + ' fajl(ova).' + datumInfo;
    bar.style.width = '100%';

    // Šifra-based uparivanje + review panel
    const detAccts = getDetailedAccounts();
    try {
      if (!window.__ft_kfData) {
        const { data: kfRows } = await sb.from('komitenti_full').select('naziv,pib,sifra').is('deleted_at', null);
        window.__ft_kfData = kfRows || [];
      }
      const kfBySifra = new Map();
      (window.__ft_kfData || []).forEach(k => { if (k.sifra) kfBySifra.set(String(k.sifra), k); });
      // Dedup šifre: ako isti vendor name ima 2 šifre pod istim kontom, zadrži češću
      const nameCodeCount = new Map(); // "konto|name" → {code→count}
      kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticCode && e.analyticName).forEach(e => {
        const aName = e.analyticName.toLowerCase().trim();
        // Preskoči prekratka imena — "doo", "pg" su sufiksi, ne puna imena
        if (aName.length < 5) return;
        const key = e.rsCode + '|' + aName;
        if (!nameCodeCount.has(key)) nameCodeCount.set(key, new Map());
        const counts = nameCodeCount.get(key);
        counts.set(e.analyticCode, (counts.get(e.analyticCode) || 0) + 1);
      });
      nameCodeCount.forEach((counts, key) => {
        if (counts.size <= 1) return;
        let bestCode = '', bestCount = 0;
        counts.forEach((cnt, code) => { if (cnt > bestCount) { bestCode = code; bestCount = cnt; } });
        counts.forEach((cnt, code) => { if (kfBySifra.has(code) && !kfBySifra.has(bestCode)) bestCode = code; });
        const pipeIdx = key.indexOf('|');
        const konto = key.substring(0, pipeIdx);
        const name = key.substring(pipeIdx + 1);
        kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.rsCode === konto && e.analyticName.toLowerCase().trim() === name && e.analyticCode !== bestCode).forEach(e => {
          console.log('[SIFRA-DEDUP] ' + e.rsCode + '/' + e.analyticName + ': ' + e.analyticCode + ' → ' + bestCode);
          e.analyticCode = bestCode;
        });
      });

      const matchedList = [];
      const unmatchedList = [];
      const seen = new Set();
      const detAnalitike = kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticCode && detAccts.some(da => e.rsCode.startsWith(da)));
      detAnalitike.forEach(e => {
        const key = e.rsCode + '|' + e.analyticCode;
        if (seen.has(key)) return;
        seen.add(key);
        const kf = kfBySifra.get(e.analyticCode);
        const ocrName = (e.analyticName || '').trim();
        if (kf) {
          matchedList.push({ ocrName, dbName: (kf.naziv || '').trim(), sifra: e.analyticCode, konto: e.rsCode });
          e.analyticName = (kf.naziv || '').trim();
          e._komitentMatched = true;
        } else {
          const srcFile = (e.sourceFile || '').replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '');
          unmatchedList.push({ ocrName, code: e.analyticCode, konto: e.rsCode, file: srcFile });
        }
      });
      // Primeni match na sve entries (ne samo unique)
      detAnalitike.forEach(e => { const kf = kfBySifra.get(e.analyticCode); if (kf) { e.analyticName = (kf.naziv || '').trim(); e._komitentMatched = true; } });
      if (matchedList.length || unmatchedList.length) console.log('[VENDOR-MATCH] ' + matchedList.length + ' upareno, ' + unmatchedList.length + ' neupareno');
      // Prikaži review panel
      window.__kjVendorMatched = matchedList;
      window.__kjVendorUnmatched = unmatchedList;
    } catch (e) { console.warn('[VENDOR-MATCH] Greška:', e.message); }

    renderKjResults();
    showVendorReviewPanel();

    // Upozorenje za fajlove gde Duguje ≠ Potražuje — sa dugmetom za ručnu zamenu
    if (kjImbalancedFiles.length) {
      toast('\u26A0 ' + kjImbalancedFiles.length + ' fajlova sa nebalansiranim D/P — proverite pre čuvanja!', 'err');
      const warn = document.createElement('div');
      warn.style.cssText = 'background:rgba(255,92,106,.1);border:1px solid var(--dng);border-radius:var(--rs);padding:12px 16px;margin-bottom:16px;font-size:13px;color:var(--tx)';
      const title = document.createElement('div'); title.style.cssText = 'font-weight:700;color:var(--dng);margin-bottom:8px;font-size:14px';
      title.textContent = '\u26A0 Duguje \u2260 Potražuje u ' + kjImbalancedFiles.length + ' fajl(ova):';
      warn.appendChild(title);
      kjImbalancedFiles.forEach(f => {
        const shortName = f.name.replace(/FINANSIJSKO KNJIZENJE /,'').replace(/_Privredno.*/,'');
        const fileBlock = document.createElement('div'); fileBlock.style.cssText = 'margin-bottom:8px;border-bottom:1px solid rgba(255,92,106,.2);padding-bottom:6px';
        const hdr = document.createElement('div'); hdr.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 0';
        const info = document.createElement('span'); info.style.cssText = 'flex:1;font-size:12px;font-weight:600';
        info.textContent = shortName + ': razlika ' + fNum(Math.abs(f.d - f.c));
        const btn = document.createElement('button'); btn.className = 'bsm'; btn.style.cssText = 'font-size:10px;padding:2px 6px;white-space:nowrap';
        btn.textContent = 'Zameni SVE D\u2194P';
        btn.onclick = () => {
          kjParsedEntries.filter(e => e.sourceFile === f.name ).forEach(e => { const tmp = e.debit; e.debit = e.credit; e.credit = tmp; });
          f.d = kjParsedEntries.filter(e => e.sourceFile === f.name && e.detailLevel !== 'ANALYTIC' ).reduce((s, e) => s + e.debit, 0);
          f.c = kjParsedEntries.filter(e => e.sourceFile === f.name && e.detailLevel !== 'ANALYTIC' ).reduce((s, e) => s + e.credit, 0);
          renderKjResults(); toast('Zamenjeno D\u2194P za ' + shortName);
        };
        hdr.appendChild(info); hdr.appendChild(btn); fileBlock.appendChild(hdr);
        // Per-stavka lista sa individualnim swap dugmićima
        const fileEntries = kjParsedEntries.filter(e => e.sourceFile === f.name && e.detailLevel !== 'ANALYTIC' );
        fileEntries.forEach(e => {
          const eRow = document.createElement('div'); eRow.style.cssText = 'display:flex;align-items:center;gap:6px;padding:2px 0 2px 12px;font-size:11px';
          const eInfo = document.createElement('span'); eInfo.style.cssText = 'flex:1;font-family:var(--mn)';
          eInfo.textContent = e.rsCode + ' ' + (e.rsName||'').substring(0,20) + ' | D=' + fNum(e.debit) + ' P=' + fNum(e.credit);
          const eBtn = document.createElement('button'); eBtn.className = 'bsm'; eBtn.style.cssText = 'font-size:9px;padding:1px 5px';
          eBtn.textContent = 'D\u2194P';
          eBtn.onclick = () => {
            // Zameni D↔P za ovu stavku + njene analitike
            const related = kjParsedEntries.filter(r => r.sourceFile === f.name && (r === e || (r.detailLevel === 'ANALYTIC' && r.parentCode === e.rsCode)));
            related.forEach(r => { const tmp = r.debit; r.debit = r.credit; r.credit = tmp; });
            eInfo.textContent = e.rsCode + ' ' + (e.rsName||'').substring(0,20) + ' | D=' + fNum(e.debit) + ' P=' + fNum(e.credit);
            f.d = kjParsedEntries.filter(x => x.sourceFile === f.name && x.detailLevel !== 'ANALYTIC').reduce((s, x) => s + x.debit, 0);
            f.c = kjParsedEntries.filter(x => x.sourceFile === f.name && x.detailLevel !== 'ANALYTIC').reduce((s, x) => s + x.credit, 0);
            info.textContent = shortName + ': razlika ' + fNum(Math.abs(f.d - f.c));
            if (Math.abs(f.d - f.c) < 1) { fileBlock.style.background = 'rgba(0,200,150,.1)'; info.style.color = 'var(--suc)'; }
            renderKjResults();
          };
          eRow.appendChild(eInfo); eRow.appendChild(eBtn); fileBlock.appendChild(eRow);
        });
        warn.appendChild(fileBlock);
      });
      const out = document.getElementById('kjOutput');
      if (out) out.insertBefore(warn, out.firstChild);
    }

    // Upozorenje za nepoznate konte — modal sa listom i opcijom dodavanja
    const unknownKontos = new Set();
    kjParsedEntries.filter(e => e.mappingStatus === 'UNMAPPED' && e.detailLevel !== 'ANALYTIC').forEach(e => unknownKontos.add(e.rsCode));
    if (unknownKontos.size) {
      const overlay = document.createElement('div'); overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;display:flex;align-items:center;justify-content:center';
      const modal = document.createElement('div'); modal.style.cssText = 'background:var(--raised);border:1px solid var(--acc);border-radius:12px;padding:24px;max-width:500px;width:90%;max-height:80vh;overflow-y:auto';
      const title = document.createElement('div'); title.style.cssText = 'font-size:16px;font-weight:700;color:var(--acc);margin-bottom:12px';
      title.textContent = '\u26A0 ' + unknownKontos.size + ' nepoznatih konta';
      const desc = document.createElement('div'); desc.style.cssText = 'font-size:13px;color:var(--tx2);margin-bottom:16px';
      desc.textContent = 'Ova konta nisu u kontnom planu. Dodajte ih pre čuvanja u bazu:';
      modal.appendChild(title); modal.appendChild(desc);
      const list = document.createElement('div'); list.style.cssText = 'display:flex;flex-direction:column;gap:8px;margin-bottom:20px';
      [...unknownKontos].sort().forEach(k => {
        const entry = kjParsedEntries.find(e => e.rsCode === k && e.detailLevel !== 'ANALYTIC');
        const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--surface);border-radius:8px;border:1px solid var(--border)';
        const info = document.createElement('div');
        const code = document.createElement('span'); code.style.cssText = 'font-family:var(--mn);font-weight:700;color:var(--acc);font-size:15px;margin-right:12px'; code.textContent = k;
        const name = document.createElement('span'); name.style.cssText = 'color:var(--tx);font-size:13px'; name.textContent = entry?.rsName || 'nepoznat';
        info.appendChild(code); info.appendChild(name);
        const btn = document.createElement('button'); btn.className = 'bsm'; btn.style.cssText = 'font-size:11px;padding:4px 12px';
        btn.textContent = '+ Dodaj u plan'; btn.onclick = () => {
          // Otvori kontni plan modal sa pre-popunjenim podacima
          document.getElementById('mkpTitle').textContent = 'Dodaj konto';
          document.getElementById('mkpRs').value = k;
          document.getElementById('mkpSrName').value = entry?.rsName || '';
          document.getElementById('mkpEn').value = '';
          document.getElementById('mkpEnName').value = '';
          document.getElementById('mkpType').value = 'Expenses';
          document.getElementById('mkpIdx').value = '';
          document.getElementById('mdlKonto').classList.add('active');
          // Kad korisnik sačuva, označi dugme kao dodato
          const origSave = document.querySelector('#mdlKonto .bsav');
          const newSave = origSave.cloneNode(true);
          origSave.parentNode.replaceChild(newSave, origSave);
          newSave.onclick = async () => {
            await window.saveKonto();
            btn.textContent = '\u2713 Dodato'; btn.disabled = true; btn.style.background = 'var(--suc)'; btn.style.color = '#fff';
          };
        };
        row.appendChild(info); row.appendChild(btn); list.appendChild(row);
      });
      modal.appendChild(list);
      const closeBtn = document.createElement('button'); closeBtn.className = 'bsm'; closeBtn.style.cssText = 'width:100%';
      closeBtn.textContent = 'Zatvori'; closeBtn.onclick = () => overlay.remove();
      modal.appendChild(closeBtn);
      overlay.appendChild(modal); overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };
      document.body.appendChild(overlay);
    }

    // Prikazi dugme za čuvanje u bazu
    if (kjParsedEntries.length) {
      const expBar = document.getElementById('kjExpBar');
      if (expBar) expBar.style.display = 'flex';
    }
  } catch (e) {
    toast('OCR greska: ' + e.message, 'err');
    status.textContent = 'Gre\u0161ka: ' + e.message;
  }
  btn.disabled = false;
}

// DEBUG: raw OCR text storage
let kjRawOcrTexts = [];

// PARSE SINGLE PDF — koristi word bounding boxes za precizno raspoređivanje kolona
async function parseSinglePdf(file) {
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
  const entries = [];

  // Image preprocessing: pojačaj kontrast i binarizuj za bolji OCR
  function preprocessCanvas(canvas, thresholdOrAggressive) {
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imgData.data;
    let sum = 0, count = d.length / 4;
    for (let i = 0; i < d.length; i += 4) {
      const gray = d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
      d[i] = d[i+1] = d[i+2] = gray;
      sum += gray;
    }
    const mean = sum / count;
    // thresholdOrAggressive: boolean (legacy) ili broj 0-1 (direktan multiplier)
    const mult = typeof thresholdOrAggressive === 'number' ? thresholdOrAggressive : (thresholdOrAggressive ? 0.72 : 0.82);
    const threshold = mean * mult;
    for (let i = 0; i < d.length; i += 4) {
      const v = d[i] < threshold ? 0 : 255;
      d[i] = d[i+1] = d[i+2] = v;
    }
    ctx.putImageData(imgData, 0, 0);
  }

  // OCR sve strane, probaj sa scale 4.0, retry sa 6.0 ako imbalans
  async function ocrAllPages(scale, preprocess) {
    const words = [], texts = [];
    let cw = 0, yo = 0;
    for (let pn = 1; pn <= pdf.numPages; pn++) {
      const page = await pdf.getPage(pn);
      const vp = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = vp.width; canvas.height = vp.height;
      cw = vp.width;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      if (preprocess) preprocessCanvas(canvas, preprocess === 'aggressive');
      const result = await kjWorker.recognize(canvas);
      if (scale === 4.0) { kjRawOcrTexts.push({ file: file.name, page: pn, text: result.data.text }); }
      texts.push(result.data.text);
      const rw = result.data.words || [];
      if (rw.length) { rw.forEach(w => { if (w.text?.trim() && w.bbox) words.push({ text: w.text.trim(), x: w.bbox.x0, x1: w.bbox.x1, y: w.bbox.y0 + yo, y1: w.bbox.y1 + yo }); }); }
      if (!rw.length && result.data.blocks) {
        for (const b of result.data.blocks) for (const p of (b.paragraphs||[])) for (const l of (p.lines||[])) for (const w of (l.words||[])) {
          if (w.text?.trim() && w.bbox) words.push({ text: w.text.trim(), x: w.bbox.x0, x1: w.bbox.x1, y: w.bbox.y0 + yo, y1: w.bbox.y1 + yo });
        }
      }
      yo += vp.height + 50;
    }
    return { words, texts, cw };
  }

  let bestParsed = null, bestDiff = Infinity, bestKontoCount = 0, bestCount = 0;
  {
  const attempts = [
    { scale: 4.0, preprocess: false, label: 'scale4' },
    { scale: 4.0, preprocess: 'aggressive', label: 'scale4+preprocess' },
    { scale: 6.0, preprocess: 'aggressive', label: 'scale6+preprocess' },
  ];

  for (const attempt of attempts) {
    const ocr = await ocrAllPages(attempt.scale, attempt.preprocess);
    const parsed = ocr.words.length > 10 ? parseWithWordPositions(ocr.words, file.name, ocr.cw) : parseOcrText(ocr.texts.join('\n'), file.name);
    // Dedup-aware diff: isključi PARENT koji ima SUMMARY za isti konto
    const sumKontos = new Set();
    parsed.forEach(e => { if (e.detailLevel === 'SUMMARY') sumKontos.add(e.rsCode); });
    const dedupParsed = parsed.filter(e => e.detailLevel !== 'ANALYTIC' && !(e.detailLevel === 'PARENT' && sumKontos.has(e.rsCode)));
    const d = dedupParsed.reduce((s, e) => s + e.debit, 0);
    const c = dedupParsed.reduce((s, e) => s + e.credit, 0);
    const diff = Math.abs(d - c);
    console.log('[OCR ' + attempt.label + '] ' + file.name.substring(20,60) + ': D=' + d.toFixed(0) + ' C=' + c.toFixed(0) + ' diff=' + diff.toFixed(0) + ' entries=' + parsed.length);
    const parsedTotal = Math.abs(d) + Math.abs(c);
    const entryCount = dedupParsed.length;
    // Heuristika selekcije: OCR češće ISPUSTI red nego što fabrikuje novi.
    // Drop reda lažno smanjuje disbalans (vrednost nestaje iz oba reda u zbiru),
    // pa "manji diff" nije sam po sebi indikator boljeg čitanja.
    // Selekcija: preferiraj scale koji je pročitao VIŠE RAZLIČITIH konta (potpunije čitanje),
    // pa više unosa, pa manji diff. NIKAD ne biraj scale sa MANJE podataka samo zbog manjeg diff-a
    // (manji diff često znači da je OCR ispustio red — lažni signal). Disbalans se prijavljuje, ne krije.
    const newKontoCount = new Set(dedupParsed.map(e => e.rsCode)).size;
    const newCount = parsed.length;
    let shouldPick = false;
    if (!bestParsed) shouldPick = true;
    else if (newKontoCount > bestKontoCount) shouldPick = true;
    else if (newKontoCount === bestKontoCount && newCount > bestCount) shouldPick = true;
    else if (newKontoCount === bestKontoCount && newCount === bestCount && diff < bestDiff) shouldPick = true;
    if (shouldPick) {
      console.log('[OCR-PICK] ' + attempt.label + ': kontos=' + newKontoCount + ' entries=' + newCount + ' diff=' + diff.toFixed(0) + ' (izabran)');
      bestDiff = diff; bestParsed = parsed; bestKontoCount = newKontoCount; bestCount = newCount;
    }
    // Break samo ako: balansirano, ima iznosa, i bar 2 konta (1 konto sa D=P je sumnjivo)
    const hasAmounts = parsedTotal > 0.01;
    const enoughEntries = entryCount >= 2 || (entryCount === 1 && (Math.abs(d) < 0.01 || Math.abs(c) < 0.01));
    if (diff < 1 && hasAmounts && enoughEntries) break;
  }

  // Extra OCR pokušaji sa višom rezolucijom ako standardni nisu pronašli iznose
  const bestAmountTotal = bestParsed.reduce((s, e) => s + Math.abs(e.debit) + Math.abs(e.credit), 0);
  if (bestAmountTotal < 0.01 && bestParsed.length > 0) {
    console.log('[OCR-EXTRA] Standardni OCR nije pronašao iznose, pokušavam scale 8...');
    const extraAttempts = [
      { scale: 8.0, preprocess: false, label: 'scale8' },
      { scale: 8.0, preprocess: 0.92, label: 'scale8+light' },
      { scale: 10.0, preprocess: 0.92, label: 'scale10+light' },
    ];
    for (const attempt of extraAttempts) {
      const ocr = await ocrAllPages(attempt.scale, attempt.preprocess);
      const parsed = ocr.words.length > 10 ? parseWithWordPositions(ocr.words, file.name, ocr.cw) : parseOcrText(ocr.texts.join('\n'), file.name);
      const sumKontos = new Set();
      parsed.forEach(e => { if (e.detailLevel === 'SUMMARY') sumKontos.add(e.rsCode); });
      const dedupParsed = parsed.filter(e => e.detailLevel !== 'ANALYTIC' && !(e.detailLevel === 'PARENT' && sumKontos.has(e.rsCode)));
      const d = dedupParsed.reduce((s, e) => s + e.debit, 0);
      const c = dedupParsed.reduce((s, e) => s + e.credit, 0);
      const diff = Math.abs(d - c);
      const total = Math.abs(d) + Math.abs(c);
      console.log('[OCR ' + attempt.label + '] ' + file.name.substring(20, 60) + ': D=' + d.toFixed(0) + ' C=' + c.toFixed(0) + ' diff=' + diff.toFixed(0) + ' entries=' + parsed.length);
      if (total > 0.01) { bestParsed = parsed; bestDiff = diff; break; }
    }
  }

  // Fallback: ako OCR ne pronađe iznose, izvuci tekst iz PDF-a SA pozicijama
  // i parsiraj sa parseWithWordPositions (ista logika za kolone)
  const ocrTotal = bestParsed.reduce((s, e) => s + Math.abs(e.debit) + Math.abs(e.credit), 0);
  if (ocrTotal < 0.01 && bestParsed.length > 0) {
    console.log('[PDF-TEXT] OCR nema iznosa za ' + file.name + ', pokušavam text extraction...');
    try {
      const pdfWords = [];
      let cw2 = 0, yo2 = 0;
      for (let pn = 1; pn <= pdf.numPages; pn++) {
        const page = await pdf.getPage(pn);
        const vp = page.getViewport({ scale: 4.0 });
        cw2 = vp.width;
        const content = await page.getTextContent();
        console.log('[PDF-TEXT] Strana ' + pn + ': ' + content.items.length + ' text items, viewport=' + vp.width + 'x' + vp.height);
        content.items.forEach(item => {
          if (!item.str || !item.str.trim()) return;
          const tx = item.transform;
          const charW = Math.abs(tx[0]) || 6; // font size u PDF units
          const pdfX = tx[4]; // x u PDF units
          const pdfY = tx[5]; // y u PDF units (bottom-up)
          // Konvertuj u canvas koordinate (scale 4.0, top-down)
          const canvasX = pdfX * 4.0;
          const canvasY = vp.height - pdfY * 4.0 + yo2;
          const scaledCharW = charW * 4.0;
          // Podeli na pojedinačne reči
          let xOff = 0;
          item.str.split(/\s+/).filter(Boolean).forEach(word => {
            const wx = canvasX + xOff;
            pdfWords.push({ text: word, x: Math.round(wx), x1: Math.round(wx + word.length * scaledCharW), y: Math.round(canvasY), y1: Math.round(canvasY + 20) });
            xOff += (word.length + 1) * scaledCharW;
          });
        });
        yo2 += vp.height + 50;
      }
      console.log('[PDF-TEXT] Ukupno ' + pdfWords.length + ' reči. Primeri:', pdfWords.slice(0, 10).map(w => '"' + w.text + '"@' + w.x).join(', '));
      if (pdfWords.length > 5) {
        const textParsed = parseWithWordPositions(pdfWords, file.name, cw2);
        const textTotal = textParsed.reduce((s, e) => s + Math.abs(e.debit) + Math.abs(e.credit), 0);
        console.log('[PDF-TEXT] Parsed: ' + textParsed.length + ' entries, total=' + textTotal.toFixed(2));
        if (textTotal > 0) {
          bestParsed = textParsed;
        }
      }
    } catch (e) { console.warn('[PDF-TEXT] Greška:', e.message, e); }
  }

  entries.push(...bestParsed);
  } // end if (bestDiff >= 1) — OCR fallback

  // Čišćenje OCR artefakata iz imena analitika ($ → š, ¢ → ć, itd.)
  entries.forEach(e => {
    if (e.analyticName) e.analyticName = e.analyticName.replace(/\$/g, 'š').replace(/¢/g, 'ć').replace(/©/g, 'ć').replace(/§/g, 'š').replace(/Ð/g, 'Đ').replace(/é/g, 'ć').replace(/É/g, 'Ć');
  });

  // DEDUP 1: Ako isti konto ima i SUMMARY i PARENT — proveri da li je duplikat ili višestruki unos
  // Za konta iz detailedAccts: uvek merge (analitike moraju da ostanu)
  // Koristi while+restart umesto indeksnog Map-a (splice pomera indekse!)
  {
    const detailedAccts = getDetailedAccounts();
    let dedup1Changed = true;
    while (dedup1Changed) {
      dedup1Changed = false;
      // Pronađi PARENT koji ima odgovarajući SUMMARY
      for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].detailLevel !== 'PARENT') continue;
        const parent = entries[i];
        const si = entries.findIndex(e => e.rsCode === parent.rsCode && e.detailLevel === 'SUMMARY');
        if (si < 0) continue;
        const summary = entries[si];
        const grp = parent._group;

        const pD = Math.abs(parent.debit), pC = Math.abs(parent.credit);
        const sD = Math.abs(summary.debit), sC = Math.abs(summary.credit);
        const sameD = pD > 0.01 && sD > 0.01 && Math.abs(pD - sD) / Math.max(pD, sD) < 0.15;
        const sameC = pC > 0.01 && sC > 0.01 && Math.abs(pC - sC) / Math.max(pC, sC) < 0.15;
        const isDuplicate = sameD || sameC;
        const isDetailed = detailedAccts.some(da => parent.rsCode.startsWith(da));

        if (isDuplicate && !isDetailed) {
          console.log('[DEDUP1] Brišem PARENT ' + parent.rsCode + ' D=' + parent.debit.toFixed(2) + ' C=' + parent.credit.toFixed(2) + ' (duplikat SUMMARY)');
          entries.splice(i, 1);
          for (let j = entries.length - 1; j >= 0; j--) {
            if (entries[j]._group === grp && entries[j].detailLevel === 'ANALYTIC') entries.splice(j, 1);
          }
        } else {
          const origD = summary.debit, origC = summary.credit;
          const pD = parent.debit, pC = parent.credit;
          console.log('[DEDUP1] Merge ' + parent.rsCode + ': PARENT D=' + pD.toFixed(2) + ' C=' + pC.toFixed(2) + ' | SUMMARY D=' + origD.toFixed(2) + ' C=' + origC.toFixed(2));
          summary.detailLevel = 'PARENT';
          // Sačuvaj PARENT/SUMMARY header vrednosti — kasniji balance-pass koristi kao alternative ako analitike daju imbalans
          summary._headerD = Math.max(Math.abs(pD), Math.abs(origD)) * (pD < 0 || origD < 0 ? -1 : 1);
          summary._headerC = Math.max(Math.abs(pC), Math.abs(origC)) * (pC < 0 || origC < 0 ? -1 : 1);
          entries.filter(e => e._group === grp && e.detailLevel === 'ANALYTIC').forEach(e => { e._group = summary._group; });
          entries.splice(i, 1);
          // Preračunaj iz analitika, ali zadrži veću vrednost iz {SUMMARY, PARENT} ako analitike ne pokrivaju.
          // Heuristika: OCR ispušta vodeće cifre — ne dodaje ih. Zato ako su analitike sumnjivo male
          // u poređenju sa najvećim "autoritativnim" izvorom (SUMMARY ili PARENT), koristi taj veći.
          const allAnal = entries.filter(e => e._group === summary._group && e.detailLevel === 'ANALYTIC');
          if (allAnal.length) {
            const analD = allAnal.reduce((s, e) => s + e.debit, 0);
            const analC = allAnal.reduce((s, e) => s + e.credit, 0);
            summary.debit = analD;
            summary.credit = analC;
            // ČITANJE (ne izmišljanje): kad su analitike ~0 a sintetička linija (SUMMARY/PARENT)
            // ima STVARNO PROČITAN iznos, koristi taj pročitani iznos. origD/pD su direktan OCR
            // read sintetičke linije (npr. "435 DOBAVLJAČI 4522.52") — to je čitanje, ne popunjavanje rupe.
            const bestD = Math.abs(origD) >= Math.abs(pD) ? origD : pD;
            const bestC = Math.abs(origC) >= Math.abs(pC) ? origC : pC;
            if (Math.abs(bestD) > Math.abs(analD) + 1 && Math.abs(analD) < Math.abs(bestD) * 0.05) {
              summary.debit = bestD;
            }
            if (Math.abs(bestC) > Math.abs(analC) + 1 && Math.abs(analC) < Math.abs(bestC) * 0.05) {
              summary.credit = bestC;
            }
            console.log('[DEDUP1] ' + summary.rsCode + ': analitike D=' + analD.toFixed(2) + ' C=' + analC.toFixed(2) + ' → konačno D=' + summary.debit.toFixed(2) + ' C=' + summary.credit.toFixed(2));
            if (FILL_GAPS) {
              // IZMIŠLJANJE (isključeno): razliku PARENT−analitike dodeli praznoj analitici.
              const finalAnalD = allAnal.reduce((s, e) => s + e.debit, 0);
              const finalAnalC = allAnal.reduce((s, e) => s + e.credit, 0);
              const gapD = summary.debit - finalAnalD;
              const gapC = summary.credit - finalAnalC;
              if (Math.abs(gapD) > 1) { const z = allAnal.find(e => Math.abs(e.debit) < 0.01); if (z) z.debit = gapD; }
              if (Math.abs(gapC) > 1) { const z = allAnal.find(e => Math.abs(e.credit) < 0.01); if (z) z.credit = gapC; }
            }
          }
        }
        dedup1Changed = true;
        break; // restart — indeksi su se promenili
      }
    }
  }

  // DEDUP 2: Pametno rukovanje duplikatima SUMMARY za isti konto
  // Strategija: ako brisanje manjeg duplikata SMANJUJE disbalans → obriši ga (lažni duplikat)
  //             inače → merge svih u jedan (legitimni dupli unos)
  {
    const dupMap = new Map();
    entries.forEach((e, i) => {
      if (e.detailLevel !== 'SUMMARY') return;
      if (!dupMap.has(e.rsCode)) dupMap.set(e.rsCode, []);
      dupMap.get(e.rsCode).push(i);
    });

    // Pre-calculate current balance
    const preD = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
    const preC = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
    const preDiff = preD - preC; // >0 znači višak debita, <0 višak kredita

    const toRemove = [];
    dupMap.forEach((indices, konto) => {
      if (indices.length <= 1) return;
      // Sortiraj po iznosu (manji prvo)
      const sorted = indices.map(i => ({ i, d: entries[i].debit, c: entries[i].credit, total: entries[i].debit + entries[i].credit }));
      sorted.sort((a, b) => a.total - b.total);

      const smallest = sorted[0];
      // Šta bi se desilo sa disbalansom ako obrišemo najmanji?
      const newDiff = preDiff - smallest.d + smallest.c;

      if (Math.abs(newDiff) < Math.abs(preDiff) - 0.5 && smallest.d >= 0 && smallest.c >= 0) {
        // Brisanje smanjuje disbalans I nema negativnih iznosa — lažni duplikat
        // (stavke sa negativnim iznosima su storno/korekcije, ne OCR greške)
        console.log('[DEDUP2] Brišem lažni duplikat ' + konto + ' D=' + smallest.d.toFixed(2) + ' C=' + smallest.c.toFixed(2) + ' (disbalans: ' + preDiff.toFixed(2) + '→' + newDiff.toFixed(2) + ')');
        toRemove.push(smallest.i);
      } else {
        // Smart merge: kombinuj entries, ali ako obe imaju ISTI iznos na istoj strani,
        // zadrži ga jednom (duplikat OCR čitanja, ne dva odvojena unosa)
        const keepIdx = sorted[sorted.length - 1].i;
        const keep = entries[keepIdx];
        for (let s = 0; s < sorted.length - 1; s++) {
          const dup = entries[sorted[s].i];
          // Proveri da li su D/C isti (duplikat) ili različiti (treba sabrati)
          const sameD = keep.debit !== 0 && dup.debit !== 0 && Math.abs(keep.debit - dup.debit) / Math.max(Math.abs(keep.debit), Math.abs(dup.debit)) < 0.02;
          const sameC = keep.credit !== 0 && dup.credit !== 0 && Math.abs(keep.credit - dup.credit) / Math.max(Math.abs(keep.credit), Math.abs(dup.credit)) < 0.02;
          if (!sameD) keep.debit += dup.debit;
          if (!sameC) keep.credit += dup.credit;
          console.log('[DEDUP2] Merge ' + konto + ': D' + (sameD ? '(isti)' : '+=' + dup.debit.toFixed(2)) + ' C' + (sameC ? '(isti)' : '+=' + dup.credit.toFixed(2)) + ' → D=' + keep.debit.toFixed(2) + ' C=' + keep.credit.toFixed(2));
          toRemove.push(sorted[s].i);
        }
      }
    });
    toRemove.sort((a, b) => b - a).forEach(i => entries.splice(i, 1));
  }

  // FAZA 2: Konto-type validacija — proveri da li je iznos u ispravnoj koloni
  // Klasa 4 (obaveze): normalno C > D (kredit = kreiranje obaveze)
  // Klasa 5 (rashodi): normalno D > C (debit = trošak)
  // Klasa 6 (prihodi): normalno C > D (kredit = prihod)
  // Ako je obrnuto I fajl ima imbalans, zameni D↔P za taj konto
  {
    const preD = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
    const preC = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
    if (Math.abs(preD - preC) > 1) {
      let fixes = 0;
      entries.filter(e => e.detailLevel !== 'ANALYTIC').forEach(e => {
        const k = e.rsCode;
        if (k.length >= 5) return; // 5+ cifreni konte su interni centri troškova, ne menjaj
        const shouldBeCredit = k.startsWith('4') || k.startsWith('6'); // obaveze, prihodi
        const shouldBeDebit = k.startsWith('5') || k.startsWith('1'); // rashodi, zalihe (prijem)
        // Ako konto ima iznos samo u pogrešnoj koloni, zameni — ALI samo ako zamena
        // značajno smanjuje disbalans (>50%). Inače: nepotpuni OCR podaci, ne menjaj.
        if (shouldBeCredit && e.debit > 0 && e.credit === 0 && e.debit > 100) {
          const newDiff = preD - preC - 2 * e.debit;
          if (Math.abs(newDiff) < Math.abs(preD - preC) * 0.5) {
            console.log('[TYPE-FIX] ' + k + ' (' + (e.rsName||'').substring(0,25) + '): D=' + e.debit + '→C (obaveza/prihod treba C)');
            e.credit = e.debit; e.debit = 0; fixes++;
          }
        } else if (shouldBeDebit && e.credit > 0 && e.debit === 0 && e.credit > 100) {
          const newDiff = preD - preC + 2 * e.credit;
          if (Math.abs(newDiff) < Math.abs(preD - preC) * 0.5) {
            console.log('[TYPE-FIX] ' + k + ' (' + (e.rsName||'').substring(0,25) + '): C=' + e.credit + '→D (rashod/zalihe treba D)');
            e.debit = e.credit; e.credit = 0; fixes++;
          }
        }
      });
      // Popravi i ANALYTIC redove čiji PARENT je fixovan (ista D↔C zamena)
      if (fixes > 0) {
        const fixedKontos = new Set();
        entries.filter(e => e.detailLevel !== 'ANALYTIC').forEach(e => {
          const k = e.rsCode;
          const shouldBeCredit = k.startsWith('4') || k.startsWith('6');
          const shouldBeDebit = k.startsWith('5') || k.startsWith('1');
          if ((shouldBeCredit && e.credit > 0 && e.debit === 0) || (shouldBeDebit && e.debit > 0 && e.credit === 0)) {
            fixedKontos.add(k);
          }
        });
        entries.filter(e => e.detailLevel === 'ANALYTIC' && fixedKontos.has(e.rsCode)).forEach(a => {
          const k = a.rsCode;
          const shouldBeCredit = k.startsWith('4') || k.startsWith('6');
          const shouldBeDebit = k.startsWith('5') || k.startsWith('1');
          if (shouldBeCredit && a.debit > 0 && a.credit === 0) { a.credit = a.debit; a.debit = 0; }
          else if (shouldBeDebit && a.credit > 0 && a.debit === 0) { a.debit = a.credit; a.credit = 0; }
        });
        // Preračunaj PARENT iz popravljenih analitika
        entries.filter(e => e.detailLevel === 'PARENT').forEach(p => {
          const ch = entries.filter(e => e._group === p._group && e.detailLevel === 'ANALYTIC');
          if (ch.length) { p.debit = ch.reduce((s, e) => s + e.debit, 0); p.credit = ch.reduce((s, e) => s + e.credit, 0); }
        });
        console.log('[TYPE-FIX] ' + file.name + ': popravljeno ' + fixes + ' stavki po konto tipu');
      }
    }
  }

  // ── Heuristike koje FORSIRAJU balans izmišljanjem vrednosti — isključene osim ako FILL_GAPS ──
  if (FILL_GAPS) {
  // OCR-10x FIX: Konto sa D/C ratio ≈ 10 → OCR decimalna greška
  // Tipično: "54.262,99" pročitan kao "542.629,94" (pomeren decimalni zarez)
  // Posle fixa: ako PARENT C = novi disbalans, OCR propustio expense child → dodaj D
  {
    let ocrFixes = 0;
    entries.filter(e => e.detailLevel !== 'ANALYTIC').forEach(e => {
      if (e.debit > 0 && e.credit > 0) {
        const ratio = e.debit / e.credit;
        if (ratio > 9.5 && ratio < 10.5) {
          console.log('[OCR-10x] ' + e.rsCode + ': D=' + e.debit.toFixed(2) + ' je 10x od C=' + e.credit.toFixed(2) + ' → D=0 (decimalna greška)');
          e.debit = 0;
          ocrFixes++;
        }
      }
    });
    if (ocrFixes > 0) {
      const dF = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
      const cF = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
      const diffF = dF - cF;
      if (Math.abs(diffF) > 1) {
        for (const e of entries) {
          if (e.detailLevel === 'ANALYTIC') continue;
          if (diffF < 0 && e.debit === 0 && e.credit > 0 && Math.abs(e.credit - Math.abs(diffF)) < 1) {
            console.log('[OCR-10x] ' + e.rsCode + ': dodajem D=' + e.credit.toFixed(2) + ' (expense counterpart, disbalans bio ' + diffF.toFixed(2) + ')');
            e.debit = e.credit;
            break;
          } else if (diffF > 0 && e.credit === 0 && e.debit > 0 && Math.abs(e.debit - diffF) < 1) {
            console.log('[OCR-10x] ' + e.rsCode + ': dodajem C=' + e.debit.toFixed(2) + ' (expense counterpart, disbalans bio ' + diffF.toFixed(2) + ')');
            e.credit = e.debit;
            break;
          }
        }
      }
    }
  }

  // DEDUP 3: Auto-fix disbalans gde jedan konto sa jednom stranom = ceo disbalans
  {
    const d3 = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
    const c3 = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
    const diff3 = d3 - c3;
    const total3 = d3 + c3;

    if (Math.abs(diff3) > 0.5 && total3 > 0 && Math.abs(diff3) / total3 < 0.01) {
      for (const e of entries) {
        if (e.detailLevel === 'ANALYTIC') continue;
        if (diff3 < 0 && e.credit > 0 && e.debit === 0 && Math.abs(e.credit - Math.abs(diff3)) < 0.5) {
          console.log('[AUTO-FIX] ' + e.rsCode + ': dodajem D=' + e.credit.toFixed(2) + ' (OCR propustio counterpart, disbalans bio ' + diff3.toFixed(2) + ')');
          e.debit = e.credit;
          break;
        } else if (diff3 > 0 && e.debit > 0 && e.credit === 0 && Math.abs(e.debit - diff3) < 0.5) {
          console.log('[AUTO-FIX] ' + e.rsCode + ': dodajem C=' + e.debit.toFixed(2) + ' (OCR propustio counterpart, disbalans bio ' + diff3.toFixed(2) + ')');
          e.credit = e.debit;
          break;
        }
      }
    }
  }

  // PARENT-ANALYTIC gap fill: ako PARENT ima D/C koje analitike ne pokrivaju, dodeli praznoj analitici
  entries.filter(e => e.detailLevel === 'PARENT').forEach(p => {
    const ch = entries.filter(e => e._group === p._group && e.detailLevel === 'ANALYTIC');
    if (!ch.length) return;
    const aD = ch.reduce((s, e) => s + e.debit, 0);
    const aC = ch.reduce((s, e) => s + e.credit, 0);
    const gapD = p.debit - aD, gapC = p.credit - aC;
    if ((gapD > 1 || gapC > 1)) {
      console.log('[GAP-FILL] ' + p.rsCode + ': PARENT D=' + p.debit.toFixed(2) + ' C=' + p.credit.toFixed(2) + ' | analitike D=' + aD.toFixed(2) + ' C=' + aC.toFixed(2) + ' | gap D=' + gapD.toFixed(2) + ' C=' + gapC.toFixed(2));
    }
    if (gapD > 1) {
      const z = ch.find(e => Math.abs(e.debit) < 0.01 && Math.abs(e.credit) < 0.01) || ch.find(e => Math.abs(e.debit) < 0.01);
      if (z) { z.debit = gapD; console.log('[GAP-FILL] ' + p.rsCode + '/' + z.analyticCode + ': D=' + gapD.toFixed(2)); }
    }
    if (gapC > 1) {
      const z = ch.find(e => Math.abs(e.credit) < 0.01 && Math.abs(e.debit) < 0.01) || ch.find(e => Math.abs(e.credit) < 0.01);
      if (z) { z.credit = gapC; console.log('[GAP-FILL] ' + p.rsCode + '/' + z.analyticCode + ': C=' + gapC.toFixed(2)); }
    }
  });

  // BALANCE-HEADER: kad DEDUP1 izabere analitike sum ali PARENT/SUMMARY header je tačan, swap može poboljšati balans dokumenta.
  // Koristi se kad OCR ispusti analitiku (gap u sumi) ali header line pravilno pročitan.
  {
    const bNA0 = entries.filter(e => e.detailLevel !== 'ANALYTIC');
    let bD0 = bNA0.reduce((s, e) => s + e.debit, 0);
    let bC0 = bNA0.reduce((s, e) => s + e.credit, 0);
    if (Math.abs(bD0 - bC0) > 1) {
      for (const e of bNA0) {
        const curDiff = bD0 - bC0;
        if (Math.abs(curDiff) <= 1) break;
        if (typeof e._headerD === 'number' && Math.abs(e._headerD - e.debit) > 1) {
          const newDiff = curDiff - e.debit + e._headerD;
          if (Math.abs(newDiff) < Math.abs(curDiff) * 0.3) {
            console.log('[BALANCE-HEADER] ' + e.rsCode + ': D=' + e.debit.toFixed(2) + ' → ' + e._headerD.toFixed(2) + ' (poboljšanje balansa ' + Math.round((1 - Math.abs(newDiff)/Math.abs(curDiff))*100) + '%)');
            bD0 = bD0 - e.debit + e._headerD;
            e.debit = e._headerD;
          }
        }
        if (typeof e._headerC === 'number' && Math.abs(e._headerC - e.credit) > 1) {
          const curDiff2 = bD0 - bC0;
          const newDiff = curDiff2 + e.credit - e._headerC;
          if (Math.abs(newDiff) < Math.abs(curDiff2) * 0.3) {
            console.log('[BALANCE-HEADER] ' + e.rsCode + ': C=' + e.credit.toFixed(2) + ' → ' + e._headerC.toFixed(2) + ' (poboljšanje balansa ' + Math.round((1 - Math.abs(newDiff)/Math.abs(curDiff2))*100) + '%)');
            bC0 = bC0 - e.credit + e._headerC;
            e.credit = e._headerC;
          }
        }
      }
    }
  }

  // BALANCE-FIX: korekcija malih imbalansa
  {
    const bNA = entries.filter(e => e.detailLevel !== 'ANALYTIC');
    const bD = bNA.reduce((s, e) => s + e.debit, 0);
    const bC = bNA.reduce((s, e) => s + e.credit, 0);
    const bDiff = bD - bC;
    const bTotal = Math.abs(bD) + Math.abs(bC);
    // (BALANCE-SMALL uklonjen — ne nagađamo gde da dodamo diff)
    if (Math.abs(bDiff) > 1) {
      if (bDiff > 0) {
        const cE = bNA.filter(e => e.credit > 0 && e.debit === 0);
        if (cE.length === 1 && cE[0].credit < bD * 0.1) {
          console.log('[BALANCE-FIX] ' + cE[0].rsCode + ': C=' + cE[0].credit.toFixed(2) + ' → C=' + bD.toFixed(2));
          cE[0].credit = bD;
          entries.filter(e => e._group === cE[0]._group && e.detailLevel === 'ANALYTIC' && e.credit > 0).forEach(a => { a.credit = bD; });
        }
      } else {
        const dE = bNA.filter(e => e.debit > 0 && e.credit === 0);
        if (dE.length === 1 && dE[0].debit < bC * 0.1) {
          console.log('[BALANCE-FIX] ' + dE[0].rsCode + ': D=' + dE[0].debit.toFixed(2) + ' → D=' + bC.toFixed(2));
          dE[0].debit = bC;
          entries.filter(e => e._group === dE[0]._group && e.detailLevel === 'ANALYTIC' && e.debit > 0).forEach(a => { a.debit = bC; });
        }
      }
    }
  }
  } // end if (FILL_GAPS) — bez izmišljanja: čitamo, ne popunjavamo rupe

  // Post-parse per-FILE balance check (POSLE svih dedup koraka)
  const fileD = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
  const fileC = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
  if (entries.length > 0 && Math.abs(fileD - fileC) > 1) {
    kjImbalancedFiles.push({ name: file.name, d: fileD, c: fileC });
    console.group('[IMBALANS] ' + file.name + ' (' + pdf.numPages + ' strana)');
    entries.filter(e => e.detailLevel !== 'ANALYTIC').forEach(e => {
      console.log('  ' + e.detailLevel + ' ' + e.rsCode + ' ' + (e.analyticName || e.rsName || '').substring(0,30) + ' | D=' + e.debit.toFixed(2) + ' C=' + e.credit.toFixed(2));
    });
    console.log('  TOTAL: D=' + fileD.toFixed(2) + ' C=' + fileC.toFixed(2) + ' diff=' + (fileD - fileC).toFixed(2));
    console.groupEnd();
  }

  return entries;
}

// ============================================================
// PRECIZNI PARSER SA WORD POZICIJAMA
// Koristi X koordinate za tačno raspoređivanje Duguje/Potražuje
// PDF layout je fiksan: Duguje ~x1100-1210, Potražuje ~x1350-1430
// ============================================================
export function parseWithWordPositions(words, fileName, canvasWidth) {
  const entries = [];
  const detailedAccts = getDetailedAccounts();
  const cw = canvasWidth || 1500; // canvas width za proporcije

  // 1. Pronađi X pozicije kolona — HIBRIDNO: zaglavlje + klasterovanje iznosa
  let dugujeX = 0, potrazujeX = 0;

  // Metod A: traži "Duguje"/"Potrazuje"/"Dokument" u zaglavlju
  let dokumentX = 0; // pozicija Dokument kolone — gornja granica za iznose
  for (const w of words) {
    const t = w.text.toLowerCase().replace(/[^a-zšžčćđ]/g, '');
    if (t === 'duguje' || t === 'dugue') dugujeX = w.x;
    if (t === 'potrazuje' || t === 'potrazue' || t === 'potrauje' || t === 'potražuje') potrazujeX = w.x;
    if (t === 'dokument' && w.x > 100) dokumentX = w.x;
  }
  if (!dugujeX || !potrazujeX) {
    for (const w of words) {
      const t = w.text.toLowerCase().replace(/[^a-zšžčćđ]/g, '');
      if (!dugujeX && t.length <= 8 && (t.includes('duguj') || t === 'dugue')) dugujeX = w.x;
      if (!potrazujeX && t.length <= 12 && /potraz.*uj/.test(t)) potrazujeX = w.x;
    }
  }

  // Metod B: klasterovanje X pozicija svih iznosa (robustniji od zaglavlja)
  // Pronađi sve reči koje izgledaju kao iznosi i njihove X pozicije
  const amountXs = [];
  for (const w of words) {
    const cleaned = w.text.replace(/[|\s]/g, '');
    if (/^-?\d[\d.]*[.,]\d{2}$/.test(cleaned) && !isDateLike(cleaned) && w.x > cw * 0.25) {
      amountXs.push(w.x);
    }
  }

  if (amountXs.length >= 4) {
    // Sortiraj i pronađi najveći gap — to je granica između D i P kolona
    amountXs.sort((a, b) => a - b);
    let maxGap = 0, gapIdx = 0;
    for (let i = 1; i < amountXs.length; i++) {
      const gap = amountXs[i] - amountXs[i - 1];
      if (gap > maxGap) { maxGap = gap; gapIdx = i; }
    }
    // Kolone: leva grupa = Duguje, desna = Potrazuje
    if (maxGap > cw * 0.03) { // gap mora biti značajan (>3% canvas width)
      const leftXs = amountXs.slice(0, gapIdx);
      const rightXs = amountXs.slice(gapIdx);
      const clusterDugujeX = leftXs.reduce((s, x) => s + x, 0) / leftXs.length;
      const clusterPotrazujeX = rightXs.reduce((s, x) => s + x, 0) / rightXs.length;
      // Koristi klaster ako zaglavlje nije pronađeno, ili ako se klaster značajno razlikuje
      if (!dugujeX || !potrazujeX) {
        dugujeX = Math.round(clusterDugujeX);
        potrazujeX = Math.round(clusterPotrazujeX);
      } else if (Math.abs(clusterDugujeX - dugujeX) > cw * 0.05 || Math.abs(clusterPotrazujeX - potrazujeX) > cw * 0.05) {
        // Klaster se značajno razlikuje od zaglavlja — veruj klasteru
        console.warn('[PARSER] Klaster kolona razlikuje se od zaglavlja: header D=' + dugujeX + ' P=' + potrazujeX + ' | cluster D=' + Math.round(clusterDugujeX) + ' P=' + Math.round(clusterPotrazujeX));
        dugujeX = Math.round(clusterDugujeX);
        potrazujeX = Math.round(clusterPotrazujeX);
      }
    }
  }

  // Proporcionalni fallback
  if (!dugujeX) dugujeX = Math.round(cw * 0.449);
  if (!potrazujeX) potrazujeX = Math.round(cw * 0.546);
  if (potrazujeX <= dugujeX) potrazujeX = Math.round(cw * 0.546);
  if (dugujeX <= 0) dugujeX = Math.round(cw * 0.449);
  const boundary = Math.round((dugujeX + potrazujeX) / 2);
  // Gornja granica za iznose: Dokument kolona (ako detektovana) ili fallback
  const amountMaxX = dokumentX > potrazujeX ? dokumentX - 20 : potrazujeX + (potrazujeX - dugujeX) * 1.5;


  // 2. Grupiši words po linijama
  const sorted = [...words].sort((a, b) => a.y - b.y || a.x - b.x);
  // Threshold baziran na VISINI reči (robusnije od razmaka): grupiše OCR baseline-jitter
  // UNUTAR reda, a razdvaja redove (razmak redova >> visina reči). Ranije: prag iz razmaka
  // (~5px) je cepao konto od iznosa kad OCR baseline skoči ~7px (iznos završi u liniji bez konta).
  const heights = sorted.map(w => ((w.y1 || (w.y + 20)) - w.y)).filter(h => h > 2 && h < 80).sort((a, b) => a - b);
  const medianHeight = heights.length ? heights[Math.floor(heights.length / 2)] : 20;
  const lineThreshold = Math.max(8, Math.round(medianHeight * 0.9));
  console.log('[LINE-GROUP] ' + fileName.substring(20, 55) + ': medianHeight=' + medianHeight + ' threshold=' + lineThreshold + ' words=' + sorted.length);
  const lineMap = [];
  let curY = -1, curLine = [];
  for (const w of sorted) {
    if (curY < 0 || Math.abs(w.y - curY) > lineThreshold) {
      if (curLine.length) lineMap.push(curLine);
      curLine = [w]; curY = w.y;
    } else {
      curLine.push(w);
    }
  }
  if (curLine.length) lineMap.push(curLine);
  console.log('[LINE-GROUP] ' + fileName.substring(20, 55) + ': ' + lineMap.length + ' linija detektovano');

  // DIJAGNOSTIKA: ispiši SVE linije sa numeričkim tokenima (da vidim gde OCR stavlja "4522 52")
  if ((typeof window !== 'undefined' && window.__kjDebugParse) || /500196|IZVOD UNI 91 BR\. 90/.test(fileName)) {
    lineMap.forEach((line, li) => {
      const sl = [...line].sort((a, b) => a.x - b.x);
      const nums = sl.filter(w => /\d/.test(w.text));
      if (nums.length) console.log('[LINE ' + li + '] y=' + sl[0].y + ' | ' + sl.map(w => w.text + '@' + w.x).join(' ').slice(0, 170));
    });
  }

  // DEBUG: za fajlove sa AMORTIZAC, prikaži sve linije sa konto+iznos
  if (fileName.includes('AMORTIZAC')) {
    lineMap.forEach((line, li) => {
      const sortedL = [...line].sort((a, b) => a.x - b.x);
      const kontoW = sortedL.find(w => /^\d{2,5}$/.test(w.text) && w.x < 250);
      if (kontoW) {
        const allOnLine = sortedL.map(w => '"' + w.text + '"@x' + Math.round(w.x) + ',y' + Math.round(w.y)).join(' | ');
        console.log('[DEBUG-LINE ' + li + '] konto=' + kontoW.text + ' → ' + allOnLine);
      }
    });
  }

  // 3. Izvuci datum iz zaglavlja
  let bookingDate = '';
  for (const line of lineMap) {
    const text = line.map(w => w.text).join(' ');
    const dm = text.match(/OD\s+(\d{1,2})\.(\d{1,2})\.(\d{4})/i);
    if (dm) { bookingDate = dm[1].padStart(2, '0') + '.' + dm[2].padStart(2, '0') + '.' + dm[3]; break; }
  }

  // 4. Parsiraj svaku liniju
  // _group counter — isti konto može da se pojavi više puta (npr. 435 u Duguje i Potražuje)
  let currentParent = null;
  let groupCounter = 0;

  // Pomocna: zatvori currentParent — ubaci PARENT red sa sumom analitika
  // Ako nema analitika ali parent linija ima iznose, napravi SUMMARY umesto toga
  function closeParent(date) {
    if (!currentParent) return;
    const prevDetailed = true /* svaki parent sa analitikama je detaljan */;
    if (!prevDetailed) return;
    const children = entries.filter(e => e._group === currentParent.group && e.detailLevel === 'ANALYTIC');
    const mapped = lookupKonto(currentParent.konto);

    const totD = children.reduce((s, e) => s + e.debit, 0);
    const totC = children.reduce((s, e) => s + e.credit, 0);
    const ownD = currentParent.ownDebit || 0;
    const ownC = currentParent.ownCredit || 0;

    // Ako analitike imaju sumu 0 ali parent ima iznose — raspodeli na analitike (IZMIŠLJANJE — gated)
    if (FILL_GAPS && children.length && totD === 0 && totC === 0 && (ownD > 0 || ownC > 0)) {
      // Raspodeli ravnomerno na analitike (ili sve na jednu ako je samo jedna)
      const perChildD = ownD / children.length;
      const perChildC = ownC / children.length;
      children.forEach((ch, idx) => {
        if (children.length === 1) {
          ch.debit = ownD; ch.credit = ownC;
        } else {
          // Poslednja dobija ostatak da se izbegnu zaokruživanja
          if (idx < children.length - 1) {
            ch.debit = Math.round(perChildD * 100) / 100;
            ch.credit = Math.round(perChildC * 100) / 100;
          } else {
            ch.debit = Math.round((ownD - children.slice(0, -1).reduce((s, c) => s + c.debit, 0)) * 100) / 100;
            ch.credit = Math.round((ownC - children.slice(0, -1).reduce((s, c) => s + c.credit, 0)) * 100) / 100;
          }
        }
      });
    }

    // Debug: loguj children za konto 521
    if (currentParent.konto === '521' || currentParent.konto === '520') {
      console.log('[CLOSE-DBG] ' + currentParent.konto + ': ownD=' + ownD.toFixed(2) + ' ownC=' + ownC.toFixed(2) + ' children=' + children.length);
      children.forEach(ch => console.log('  child ' + ch.analyticCode + ' D=' + ch.debit.toFixed(2) + ' C=' + ch.credit.toFixed(2)));
    }
    // Finalni totali — koristi parent liniju ako je pouzdanija od analytics sume
    let finalD, finalC;
    if (children.length) {
      const sumD = children.reduce((s, e) => s + e.debit, 0);
      const sumC = children.reduce((s, e) => s + e.credit, 0);
      // Proveri da li neki cost center ima 0 iznos (OCR promašio)
      const zeroCcD = FILL_GAPS && children.find(e => e.analyticCode?.length === 5 && !state.kontniPlan?.some(k => k.rsKonto === e.analyticCode) && Math.abs(e.debit) < 0.01 && ownD > 0 && Math.abs(sumD - ownD) > 1);
      const zeroCcC = FILL_GAPS && children.find(e => e.analyticCode?.length === 5 && !state.kontniPlan?.some(k => k.rsKonto === e.analyticCode) && Math.abs(e.credit) < 0.01 && ownC > 0 && Math.abs(sumC - ownC) > 1);
      if (zeroCcD) {
        // Cost center sa D=0 — parent ownD je pouzdaniji, popuni prazan CC
        finalD = ownD;
        zeroCcD.debit = ownD - sumD;
        console.log('[CC-ZERO-FIX] ' + currentParent.konto + '/' + zeroCcD.analyticCode + ': D=' + zeroCcD.debit.toFixed(2) + ' (OCR nije pročitao)');
      } else {
        // Sintetička linija (ownD) je ZVANIČAN total konta — koristi je kad je pročitana (>0).
        // Zbir analitika može biti nepotpun ako OCR ispusti analitiku (npr. plata jednog radnika).
        finalD = ownD > 0 ? ownD : sumD;
      }
      if (zeroCcC) {
        finalC = ownC;
        zeroCcC.credit = ownC - sumC;
        console.log('[CC-ZERO-FIX] ' + currentParent.konto + '/' + zeroCcC.analyticCode + ': C=' + zeroCcC.credit.toFixed(2) + ' (OCR nije pročitao)');
      } else {
        finalC = ownC > 0 ? ownC : sumC;
      }
      // CC-ADD (balans-forsiranje, gated): dodaj male cost-center iznose na parent total.
      if (FILL_GAPS && finalD === ownD && sumD > 0 && sumD < ownD * 0.15) {
        const ccD = children.filter(e => e.analyticCode?.length === 5 && !state.kontniPlan?.some(k => k.rsKonto === e.analyticCode)).reduce((s, e) => s + e.debit, 0);
        if (ccD > 0 && ccD < ownD * 0.15) { finalD += ccD; console.log('[CC-ADD] ' + currentParent.konto + ': D+=' + ccD.toFixed(2) + ' (cost center dodatak)'); }
      }
      if (FILL_GAPS && finalC === ownC && sumC > 0 && sumC < ownC * 0.15) {
        const ccC = children.filter(e => e.analyticCode?.length === 5 && !state.kontniPlan?.some(k => k.rsKonto === e.analyticCode)).reduce((s, e) => s + e.credit, 0);
        if (ccC > 0 && ccC < ownC * 0.15) { finalC += ccC; console.log('[CC-ADD] ' + currentParent.konto + ': C+=' + ccC.toFixed(2) + ' (cost center dodatak)'); }
      }
    } else { finalD = ownD; finalC = ownC; }

    // Kreiraj PARENT kad ima analitike, SUMMARY kad nema
    const detailLvl = children.length ? 'PARENT' : 'SUMMARY';
    {
      // Ukloni prethodni jednostrani shadow za isti konto ako novi PARENT ima oba D i C
      if (finalD > 0 && finalC > 0) {
        const prevIdx = entries.findIndex(e =>
          e.rsCode === currentParent.konto &&
          (e.detailLevel === 'SUMMARY' || e.detailLevel === 'PARENT') &&
          ((e.debit === 0 && e.credit > 0 && Math.abs(e.credit - finalC) < 1) ||
           (e.credit === 0 && e.debit > 0 && Math.abs(e.debit - finalD) < 1))
        );
        if (prevIdx >= 0) entries.splice(prevIdx, 1);
      }

      // Da li prikazivati analitike? Samo za konte iz displayAccts liste
      const showAnalytics = children.length && detailedAccts.some(da => currentParent.konto.startsWith(da));

      if (children.length && showAnalytics) {
        // PARENT + ANALYTIC — prikazujemo analitike
        const firstChildIdx = entries.indexOf(children[0]);
        entries.splice(firstChildIdx >= 0 ? firstChildIdx : entries.length, 0, {
          sourceFile: fileName, bookingDate: date,
          rsCode: currentParent.konto, rsName: currentParent.name,
          enCode: mapped?.enKonto || '', enName: mapped?.enName || '',
          type: mapped?.type || '',
          debit: finalD, credit: finalC,
          detailLevel: 'PARENT', parentCode: '', analyticCode: '', analyticName: '',
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED', notes: '',
          _group: currentParent.group,
          // Sačuvaj header vrednost iz parent linije kao alternativu za BALANCE-HEADER post-pass
          _headerD: ownD, _headerC: ownC
        });
      } else {
        // SUMMARY — kolapsiran total (analitike su parsirane za tačan balans ali se ne prikazuju)
        if (children.length) {
          // Ukloni analitike — korisnik vidi samo total
          for (let ci = entries.length - 1; ci >= 0; ci--) {
            if (entries[ci]._group === currentParent.group && entries[ci].detailLevel === 'ANALYTIC') entries.splice(ci, 1);
          }
        }
        entries.push({
          sourceFile: fileName, bookingDate: date,
          rsCode: currentParent.konto, rsName: currentParent.name,
          enCode: mapped?.enKonto || '', enName: mapped?.enName || '',
          type: mapped?.type || '',
          debit: finalD, credit: finalC,
          detailLevel: 'SUMMARY', parentCode: '', analyticCode: '', analyticName: '',
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED', notes: '',
          _group: currentParent.group,
          // Sačuvaj header vrednost — može se razlikovati od finalD/finalC kada se izabralo analitike sum
          _headerD: ownD, _headerC: ownC
        });
      }
    }
  } // end closeParent

  for (const line of lineMap) {
    const sortedLine = [...line].sort((a, b) => a.x - b.x);
    const fullText = sortedLine.map(w => w.text).join(' ');

    // Skip zaglavlja
    if (/Konto|SinIAna|Sin\/Ana|Naziv|Duguje|Potrazuje|Dokument|Opis|Privredno|MORAVSKA|PIB|NALOG|FINANSIJSKO|TAVNIK|36204/i.test(fullText)) continue;

    // Pronađi konto (prva reč koja je broj, x < 250)
    const kontoWord = sortedLine.find(w => /^\d{1,5}$/.test(w.text) && w.x < 250);
    if (!kontoWord) continue;
    let konto = kontoWord.text;
    let kontoEndX = kontoWord.x1;

    // OCR fix: ako je šifra samo 1 cifra i sledeća reč je čist broj,
    // spoji ih u jednu šifru (OCR razdvojio "121" na "1" + "21")
    // NE radi za 2+ cifre — postoje nazivi koji kreću brojem (npr. "7 Juli doo")
    if (konto.length === 1) {
      const nextWord = sortedLine.find(w => w !== kontoWord && w.x >= kontoWord.x1 && w.x < kontoWord.x1 + 80 && /^\d+$/.test(w.text));
      if (nextWord) {
        konto = konto + nextWord.text;
        kontoEndX = nextWord.x1;
      }
    }

    // Izvuci naziv (reči između konta i kolone Duguje, preskoči reči koje su deo šifre)
    const usedWords = new Set([kontoWord]);
    if (konto !== kontoWord.text) { const nw = sortedLine.find(w => w !== kontoWord && w.x >= kontoWord.x1 && w.x < kontoWord.x1 + 80 && /^\d+$/.test(w.text)); if (nw) usedWords.add(nw); }
    const nameWords = sortedLine.filter(w => !usedWords.has(w) && w.x >= kontoEndX && w.x < dugujeX - 30);
    const name = nameWords.map(w => w.text).join(' ').replace(/\s{2,}/g, ' ').trim();

    // Skip totals liniju (samo iznosi, bez naziva)
    if (!name && konto.length <= 2) continue;

    // Izvuci iznose — per-word match + fallback za spojene/razdvojene OCR reči
    let debit = 0, credit = 0;
    const amountsOnLine = [];

    // Metod 1: per-word match — traži ceo word ili amount pattern unutar word-a
    for (const w of sortedLine) {
      if (w.x > amountMaxX) continue;
      if (w.x < dugujeX - 80) continue;
      const cleaned = w.text.replace(/[|]/g, '').replace(/\s/g, '');
      const isNeg = cleaned.startsWith('-'); // negativan iznos u PDF-u
      let matched = false;
      // Tačan match: ceo word je amount
      if (/^-?\d[\d.]*[.,]\d{2}$/.test(cleaned) && !isDateLike(cleaned.replace(/^-/,''))) {
        const val = parseAmount(cleaned);
        if (Math.abs(val) > 0.005) { amountsOnLine.push({ val, x: w.x }); matched = true; }
      }
      // Parcijalan match: amount spojen sa tekstom (npr. "31800.004RBMST4H...")
      if (!matched && w.x >= dugujeX - 30) {
        const m = cleaned.match(/^(-?\d[\d.]*[.,]\d{2})(.*)$/);
        // Ako iza 2 decimale slede SAMO cifre (npr. "32541,33028" = broj dokumenta), to NIJE iznos.
        // Prihvati samo ako je ostatak prazan ILI sadrži slovo (iznos zalepljen za šifru dokumenta).
        const restIsDigits = m && m[2] && /^\d+$/.test(m[2]);
        if (m && !restIsDigits && !isDateLike(m[1].replace(/^-/,''))) {
          const val = parseAmount(m[1]);
          if (Math.abs(val) > 0.005) amountsOnLine.push({ val, x: w.x });
        }
      }
    }

    // Metod 2 FALLBACK: kombinuj reči u amount zoni — SAMO za linije gde per-word ne nađe ništa
    // I samo u striknoj amount zoni (ne u name/dokument zonama)
    if (amountsOnLine.length === 0) {
      // Skeniraj amount-zonu po TOKENIMA (NE po D/C zoni — granica kolone može da preseče "4522" od "52").
      // Čita: (a) čist decimalni token; (b) split-decimalu = token cifara + SUSEDNI token od TAČNO 2 cifre
      // (OCR je decimalnu tačku pročitao kao razmak: "4522" "52" → 4522.52). D/C se dodeljuje kasnije po x.
      const upperX = (dokumentX > potrazujeX ? dokumentX - 20 : potrazujeX + (potrazujeX - dugujeX) * 1.5);
      const cand = sortedLine.filter(w => w.x >= dugujeX - 30 && w.x <= upperX
        && /^[\d.,|]+$/.test(w.text.replace(/\s/g, '')) && /\d/.test(w.text)
        && !isDateLike(w.text.replace(/[|]/g, '')));
      for (let i = 0; i < cand.length; i++) {
        const t = cand[i].text.replace(/[|\s]/g, '');
        if (/^-?\d[\d.]*[.,]\d{2}$/.test(t)) {
          const v = parseAmount(t);
          if (Math.abs(v) > 0.005) amountsOnLine.push({ val: v, x: cand[i].x });
        } else if (/^\d+$/.test(t) && i + 1 < cand.length && /^\d{2}$/.test(cand[i + 1].text.replace(/[|\s]/g, ''))) {
          const v = parseFloat(t + '.' + cand[i + 1].text.replace(/[|\s]/g, ''));
          if (v > 0.005) { amountsOnLine.push({ val: v, x: cand[i].x }); console.log('[SPLIT-DEC] "' + t + ' ' + cand[i + 1].text + '" → ' + v); i++; }
        }
      }
    }

    if (amountsOnLine.length >= 2) {
      amountsOnLine.sort((a, b) => a.x - b.x);
      debit = amountsOnLine[0].val; credit = amountsOnLine[1].val;
    } else if (amountsOnLine.length === 1) {
      const a = amountsOnLine[0];
      if (a.x >= boundary) credit = a.val;
      else debit = a.val;
    }


    // Izvuci datum iz linije i doc ref
    const lineDate = extractDateFromLine(fullText) || bookingDate;
    const docRef = extractDocRef(fullText);

    // Detektuj parent vs analitiku koristeci X POZICIJU konta
    const kontoXpct = kontoWord.x / cw;
    const isIndented = kontoXpct > 0.055;
    let knownKonto = lookupKonto(konto);
    let fixedKonto = konto;
    if (!knownKonto && !isIndented && konto.length >= 2) {
      for (let d = 0; d <= 9; d++) {
        const try1 = konto + d;
        const m = lookupKonto(try1);
        if (m) { fixedKonto = try1; knownKonto = m; break; }
      }
    }
    // Cost center analitike: 5-cifreni kod koji NIJE TAČAN konto u kontnom planu
    // Koristi EXACT match (ne prefix!) — lookupKonto radi prefix i našao bi 110 za 11000
    // Primeri cost centera: 10000-80000, 31000, 11000 (sektori)
    // Konti koji JESU u planu: 01280, 51230, 52904 itd.
    const isExactKonto = konto.length === 5 && state.kontniPlan?.some(k => k.rsKonto === konto);
    const is5digitCostCenter = konto.length === 5 && currentParent && !isExactKonto;
    // Ako parent je detailedAccts konto (430,435...) i trenutni kod NIJE poznat konto → analitika
    // (dobavljači nisu uvek uvučeni u PDF-u ali NISU u kontnom planu)
    const parentIsDetailed = currentParent && detailedAccts.some(da => currentParent.konto.startsWith(da));
    // 5591-obrazac: kod u ISTOJ (Sin) koloni kao roditelj je ZASEBAN konto, ne analitika-dobavljač.
    // Analitike (dobavljači) su uvučene desno; sintetički konto je poravnat sa roditeljem.
    const sameColumnAsParent = currentParent && currentParent.kontoX != null && Math.abs(kontoWord.x - currentParent.kontoX) <= cw * 0.02;
    const isUnknownUnderDetailed = parentIsDetailed && !knownKonto && !is5digitCostCenter && konto.length <= 4 && !sameColumnAsParent;
    const isParent = !isIndented && fixedKonto.length >= 3 && !is5digitCostCenter && !isUnknownUnderDetailed;
    const isAnalytic = !isParent && currentParent;
    if ((typeof window !== 'undefined' && window.__kjDebugParse) || /500196|IZVOD UNI 91 BR\. 90/.test(fileName)) {
      console.log('[CLASSIFY] k=' + konto + ' x=' + kontoWord.x + ' pct=' + kontoXpct.toFixed(3) + ' indent=' + isIndented + ' known=' + (knownKonto ? 'Y' : 'N') + ' sameCol=' + sameColumnAsParent + ' parent=' + (currentParent ? currentParent.konto : '-') + ' pX=' + (currentParent && currentParent.kontoX != null ? currentParent.kontoX : '-') + ' D=' + debit + ' C=' + credit + ' => ' + (isAnalytic ? 'ANALYTIC' : (isParent ? 'PARENT' : 'SKIP')));
    }
    if (isAnalytic) {
      // Čuvaj vendor ime SAMO za konta koja prikazuju analitike (dobavljači)
      if (name && window.__kjAllVendors && detailedAccts.some(da => currentParent.konto.startsWith(da))) {
        window.__kjAllVendors.push({ name, code: konto, konto: currentParent.konto, file: fileName });
      }
      {
        const mapped = lookupKonto(currentParent.konto);
        entries.push({
          sourceFile: fileName, bookingDate: lineDate,
          rsCode: currentParent.konto, rsName: currentParent.name,
          enCode: mapped?.enKonto || '', enName: mapped?.enName || '',
          type: mapped?.type || '',
          debit, credit,
          detailLevel: 'ANALYTIC', parentCode: currentParent.konto,
          analyticCode: konto, analyticName: name,
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED',
          notes: docRef,
          _group: currentParent.group
        });
      }
    } else if (isParent) {
      // Zatvori prethodni parent (ubaci PARENT red sa sumom analitika)
      closeParent(lineDate);

      groupCounter++;
      const mapped = lookupKonto(fixedKonto);
      const fullRsName = mapped?.rsName || name;
      currentParent = { konto: fixedKonto, name: fullRsName, group: groupCounter, ownDebit: debit, ownCredit: credit, kontoX: kontoWord.x };
      const isDetailed = true; // odloženo — kreiraj SUMMARY samo u closeParent ako nema analitika

      if (false) { // odloženo do closeParent
        entries.push({
          sourceFile: fileName, bookingDate: lineDate,
          rsCode: fixedKonto, rsName: fullRsName,
          enCode: mapped?.enKonto || '', enName: mapped?.enName || '',
          type: mapped?.type || '',
          debit, credit,
          detailLevel: 'SUMMARY', parentCode: '', analyticCode: '', analyticName: '',
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED',
          notes: docRef,
          _group: groupCounter
        });
      }
    }
  }

  // Zatvori poslednji parent
  closeParent(bookingDate);

  // TOTALS KOREKCIJA — pronađi totale sa dna i koriguj kolone ako treba
  let expectedD = 0, expectedC = 0;
  for (let li = lineMap.length - 1; li >= 0; li--) {
    const amts = [];
    for (const w of lineMap[li]) {
      if (w.x > amountMaxX) continue;
      const c = w.text.replace(/[|]/g, '').replace(/\s/g, '');
      if (!/^-?\d[\d.]*[.,]\d{2}$/.test(c)) continue;
      if (isDateLike(c)) continue;
      let v;
      if (c.includes(',') && c.includes('.')) v = parseFloat(c.replace(/\./g, '').replace(',', '.'));
      else if (c.includes(',')) v = parseFloat(c.replace(',', '.'));
      else v = parseFloat(c);
      if (v > 0.5) amts.push(v);
    }
    if (amts.length === 2) { expectedD = amts[0]; expectedC = amts[1]; break; }
    if (amts.length === 1 && li === lineMap.length - 1) {
      expectedD = amts[0]; expectedC = amts[0]; break;
    }
  }



  // Debug: loguj fajlove gde nijedan iznos nije pronađen
  const totalAll = entries.reduce((s, e) => s + Math.abs(e.debit) + Math.abs(e.credit), 0);
  if (totalAll === 0 && entries.length > 0) {
    console.warn('[PARSER WARN] ' + fileName + ': ' + entries.length + ' entries sa 0 iznosima.');
    console.warn('  dugujeX=' + dugujeX + ' potrazujeX=' + potrazujeX + ' boundary=' + boundary + ' amountMaxX=' + amountMaxX + ' canvasW=' + cw);
    // Loguj SVE linije (ne samo one sa konto), da vidimo gde su iznosi
    lineMap.forEach((line, idx) => {
      const sorted = [...line].sort((a, b) => a.x - b.x);
      console.log('  linija[' + idx + '] (' + sorted.length + ' reči):', sorted.map(w => '"' + w.text + '"@' + w.x).join(' | '));
    });
    // Loguj SVE OCR reči koje sadrže cifre u amount zoni
    const amtZoneWords = words.filter(w => w.x >= dugujeX - 100 && /\d/.test(w.text));
    if (amtZoneWords.length) {
      console.log('  [AMT-ZONE] Reči sa ciframa u zoni iznosa:', amtZoneWords.map(w => '"' + w.text + '"@' + w.x + ',y=' + w.y).join(' | '));
    } else {
      console.warn('  [AMT-ZONE] NEMA reči sa ciframa u zoni iznosa (x>=' + (dugujeX - 100) + ')!');
    }
  }

  // Ako NEKI konti imaju 0 iznosa, traži totals liniju i dodeli nedostajuće iznose
  const nonAnalEntries = entries.filter(e => e.detailLevel !== 'ANALYTIC');
  const hasZeroEntries = nonAnalEntries.some(e => Math.abs(e.debit) + Math.abs(e.credit) < 0.01);
  if (FILL_GAPS && hasZeroEntries && entries.length > 0) {
    // Skupi SVE amount-only linije, preferiraj onu sa OBA iznosa (D i P = totals linija)
    let bestTotD = 0, bestTotP = 0, bestScore = 0;
    for (const line of lineMap) {
      const sorted = [...line].sort((a, b) => a.x - b.x);
      const hasKonto = sorted.some(w => /^\d{1,5}$/.test(w.text) && w.x < 250);
      if (hasKonto) continue;
      const lineAmts = [];
      for (const w of sorted) {
        if (w.x < dugujeX - 80) continue;
        const cleaned = w.text.replace(/[|]/g, '').replace(/\s/g, '');
        if (/^-?\d[\d.]*[.,]\d{2}$/.test(cleaned) && !isDateLike(cleaned.replace(/^-/, ''))) {
          const val = parseAmount(cleaned);
          if (Math.abs(val) > 0.005) lineAmts.push({ val, x: w.x });
        }
      }
      if (lineAmts.length < 1) continue;
      lineAmts.sort((a, b) => a.x - b.x);
      const ld = lineAmts.find(a => a.x < boundary)?.val || 0;
      const lp = lineAmts.find(a => a.x >= boundary)?.val || 0;
      // Skor: linija sa oba D i P = 2, sa jednim = 1
      const score = (Math.abs(ld) > 0.01 ? 1 : 0) + (Math.abs(lp) > 0.01 ? 1 : 0);
      if (score > bestScore || (score === bestScore && Math.abs(ld) + Math.abs(lp) > Math.abs(bestTotD) + Math.abs(bestTotP))) {
        bestTotD = ld; bestTotP = lp; bestScore = score;
      }
    }
    const totD = bestTotD, totP = bestTotP;
    if (Math.abs(totD) > 0.01 || Math.abs(totP) > 0.01) {
      const assignedD = nonAnalEntries.reduce((s, e) => s + e.debit, 0);
      const assignedC = nonAnalEntries.reduce((s, e) => s + e.credit, 0);
      const remainD = totD - assignedD;
      const remainC = totP - assignedC;
      console.log('[ZERO-FIX] Totali: D=' + totD + ' P=' + totP + ' | Dodeljeno: D=' + assignedD.toFixed(2) + ' C=' + assignedC.toFixed(2) + ' | Ostatak: D=' + remainD.toFixed(2) + ' C=' + remainC.toFixed(2));

      const zeroEntries = nonAnalEntries.filter(e => Math.abs(e.debit) + Math.abs(e.credit) < 0.01);

      if (zeroEntries.length === 1) {
        if (Math.abs(remainD) > 0.01 || Math.abs(remainC) > 0.01) {
          zeroEntries[0].debit = Math.max(0, remainD); zeroEntries[0].credit = Math.max(0, remainC);
          console.log('[ZERO-FIX] ' + zeroEntries[0].rsCode + ' D=' + zeroEntries[0].debit.toFixed(2) + ' C=' + zeroEntries[0].credit.toFixed(2));
        }
      } else if (zeroEntries.length === 2 && Math.abs(Math.abs(totD) - Math.abs(totP)) < 1) {
        const dEntry = zeroEntries.find(e => /^[519]/.test(e.rsCode)) || zeroEntries[0];
        const pEntry = zeroEntries.find(e => e !== dEntry);
        dEntry.debit = totD; dEntry.credit = 0;
        pEntry.debit = 0; pEntry.credit = totP;
        console.log('[ZERO-FIX] ' + dEntry.rsCode + ' D=' + totD + ', ' + pEntry.rsCode + ' P=' + totP);
      } else if (zeroEntries.length > 2) {
        console.warn('[ZERO-FIX] ' + zeroEntries.length + ' konta sa 0 iznosima — ručna korekcija potrebna');
      }
    }
  }

  // Čišćenje OCR artefakata: ako entry ima i D i C ali je jedan <1% drugog i <100 RSD,
  // to je OCR greška (datum/referenca pročitana kao iznos)
  entries.forEach(e => {
    if (e.debit > 0 && e.credit > 0) console.log('[DUAL-AMT] ' + e.rsCode + '/' + (e.analyticCode||'') + ' ' + e.detailLevel + ' D=' + e.debit.toFixed(2) + ' C=' + e.credit.toFixed(2));
    if (e.debit > 0 && e.credit > 0) {
      if (e.credit < e.debit * 0.05 && e.credit < 100) {
        console.log('[ARTIFACT-FIX] ' + e.rsCode + '/' + (e.analyticCode || '') + ': uklanjam C=' + e.credit.toFixed(2) + ' (D=' + e.debit.toFixed(2) + ')');
        e.credit = 0;
      } else if (e.debit < e.credit * 0.05 && e.debit < 100) {
        console.log('[ARTIFACT-FIX] ' + e.rsCode + '/' + (e.analyticCode || '') + ': uklanjam D=' + e.debit.toFixed(2) + ' (C=' + e.credit.toFixed(2) + ')');
        e.debit = 0;
      }
    }
  });
  // Ako ARTIFACT-FIX obrisao vrednost sa ANALYTIC, umanji i PARENT za isti iznos
  // (NE raditi pun recalc — može da obriše PARENT vrednosti koje analitike ne pokrivaju)

  return entries;
}

// ============================================================
// EXCEL PARSER (.xlsx / .xls) — ERP eksport istog naloga koji stiže i kao PDF
//
// Layout (fiksan, provereno na 230 fajlova):
//   red 1  Naziv firme
//   red 2  "FINANSIJSKO KNJIŽENJE: <vrsta> -> <naziv vrste> BR. <broj>/<god>"
//   red 3  "NALOG BR. <nnnn> OD DD.MM.YYYY. GODINE"   → bookingDate (datum naloga)
//   red 5  zaglavlje: Rbr | Konto | Sin/Ana | Naziv konta | Duguje | Potražuje | Dokument | Opis
//   ...    stavke, pa kontrolni red "UKUPNO"
//
// Struktura stavki: red sa popunjenim Konto (kol. B) je sintetički konto; redovi sa
// popunjenim Sin/Ana (kol. C) su njegove analitike (komitent / magacin / troš. centar).
// Analitike NE prate uvek 1:1 svoj konto — jedan konto ume da ima 2 reda (Duguje i
// Potražuje) sa 2 analitike posle njih, a konto bez analitike ume da stoji tačno ispred
// konta koje ih ima (npr. 2418 pa 244 u deviznom izvodu). Zato blok analitika vezujemo
// za NAJKRAĆI SUFIKS prethodnih konto-redova čiji se zbir D/P poklapa sa zbirom analitika
// — na celom junskom skupu to reši svih 740 grupa bez ijednog promašaja.
//
// Datum: ni Excel ni PDF naloga NEMAJU datum knjiženja. Kolona Dokument nosi datum dokumenta
// ("R-0539/26VP od 06.06.26") = datum fakture, a zaglavlje nosi datum naloga. Pravi "Datum knjiž."
// postoji SAMO u Analitičkom dnevniku — vidi parseDnevnikDates() ispod. Ako se dnevnik ubaci uz
// naloge, datum se uzima iz njega; ako ne, pada se nazad na datum naloga.
// ============================================================

const XL_EPS = 0.02; // tolerancija za poređenje iznosa (zaokruživanje na 2 decimale)

function xlTxt(v) { return v == null ? '' : String(v).trim(); }
function xlNum(v) {
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const s = xlTxt(v);
  return s ? parseAmount(s) : 0;
}
// Napomena: broj/datum dokumenta + opis stavke (jedina mesta gde ERP čuva referencu)
function xlNotes(dok, opis) { return [xlTxt(dok), xlTxt(opis)].filter(Boolean).join(' | '); }

// ============================================================
// ANALITIČKI DNEVNIK — izvor pravog datuma knjiženja
//
// ERP mesec računa po "Datum knjiž.", NE po datumu naloga: dokument iz julskog naloga ume da ima
// junski datum knjiženja (i obrnuto). Bez ovoga se zaključni list u aplikaciji ne poklapa sa ERP-om.
// Ključ je vrsta+broj dokumenta — jedinstven u celom dnevniku (provereno: 0 kolizija na 1383 dok.).
// ============================================================

// Prepoznaj Analitički dnevnik (zaglavlje: ... | Datum knjiž. | Sin.konto | ...)
export function isDnevnikSheet(rows) {
  return (rows || []).slice(0, 20).some(r =>
    r && /datum\s*knji/i.test(xlTxt(r[5])) && /sin\.?\s*konto/i.test(xlTxt(r[6])));
}

// → Map "vrsta|broj" → "DD.MM.YYYY"
export function parseDnevnikDates(rows) {
  const hIdx = (rows || []).findIndex(r =>
    r && /datum\s*knji/i.test(xlTxt(r[5])) && /sin\.?\s*konto/i.test(xlTxt(r[6])));
  if (hIdx < 0) return null;
  const map = new Map();
  for (let i = hIdx + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    const vrsta = xlTxt(r[3]), broj = xlTxt(r[4]);
    // Datum knjiž. se piše samo na sintetičkom redu dokumenta; analitički redovi su prazni
    const m = xlTxt(r[5]).match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (!vrsta || !broj || !m) continue;
    const key = vrsta + '|' + broj;
    if (!map.has(key)) map.set(key, m[1].padStart(2, '0') + '.' + m[2].padStart(2, '0') + '.' + m[3]);
  }
  return map.size ? map : null;
}

// Vrsta+broj dokumenta iz imena fajla — radi i za Excel i za skenirani PDF:
//   FinKnjizenje_29_663_26.xlsx
//   FINANSIJSKO KNJIZENJE 29_TROSKOVI BR. 663_Privredno drustvo za.pdf
export function docKeyFromFileName(name) {
  let m = String(name).match(/FinKnjizenje_(\d+)_([^_]+)_/i);
  if (m) return m[1] + '|' + m[2];
  m = String(name).match(/KNJIZENJE\s+(\d+)_.*?BR\.\s*(\S+?)_/i);
  if (m) return m[1] + '|' + m[2];
  return '';
}

// Parsira jedan "Knjiženje" sheet (AoA — niz redova, svaki niz ćelija) u kjParsedEntries oblik.
// Čista funkcija (bez DOM/mreže) da bi bila testabilna.
export function parseKnjizenjeSheet(rows, fileName) {
  const hIdx = (rows || []).findIndex(r => r && xlTxt(r[0]).toLowerCase() === 'rbr' && /konto/i.test(xlTxt(r[1])));
  if (hIdx < 0) throw new Error('nije prepoznat format knjiženja (nema zaglavlja "Rbr | Konto | Sin/Ana | ...")');

  // Datum naloga iz zaglavlja: "NALOG BR. 6004 OD 30.06.2026. GODINE"
  // Ovo je ujedno i jedini pouzdan test da je fajl zaista NALOG: zaključni list (ZAKLIST_*.xlsx)
  // ima isto "Rbr | Konto | ..." zaglavlje pa bi inače prošao i bio parsiran kao knjiženje.
  let bookingDate = '';
  for (let i = 0; i < hIdx; i++) {
    const m = xlTxt(rows[i] && rows[i][0]).match(/NALOG\s+BR\.?\s*\S+\s+OD\s+(\d{1,2})\.(\d{1,2})\.(\d{4})/i);
    if (m) { bookingDate = m[1].padStart(2, '0') + '.' + m[2].padStart(2, '0') + '.' + m[3]; break; }
  }
  if (!bookingDate) throw new Error('nije nalog za knjiženje (nema reda "NALOG BR. ... OD DD.MM.GGGG")');

  // Vrsta + broj dokumenta iz naslova: "FINANSIJSKO KNJIŽENJE: 29 -> TROŠKOVI BR. 505/26"
  // Uzima se iz SADRŽAJA, ne iz imena fajla — ime ume da laže (npr. dokument 29/505 je stigao
  // snimljen kao "FinKnjizenje_4_25_26.xlsx").
  let docKey = '';
  for (let i = 0; i < hIdx; i++) {
    const m = xlTxt(rows[i] && rows[i][0]).match(/KNJIŽENJE:\s*(\d+)\s*->.*BR\.\s*(\S+?)\/\d+\s*$/i);
    if (m) { docKey = m[1] + '|' + m[2]; break; }
  }

  // Stavke do kontrolnog reda UKUPNO
  const body = [];
  let ukupnoD = null, ukupnoC = null;
  for (let i = hIdx + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    if (/^ukupno/i.test(xlTxt(r[0]))) { ukupnoD = xlNum(r[4]); ukupnoC = xlNum(r[5]); break; }
    if (!xlTxt(r[1]) && !xlTxt(r[2])) continue; // prazan/dekorativni red
    body.push(r);
  }

  const detailedAccts = getDetailedAccounts();
  const entries = [];
  const pending = [];   // konto redovi koji još čekaju svoj blok analitika
  let anaBuf = [];      // tekući blok analitika
  let groupCounter = 0;

  const sumD = rs => rs.reduce((s, r) => s + xlNum(r[4]), 0);
  const sumC = rs => rs.reduce((s, r) => s + xlNum(r[5]), 0);

  // Konto red bez (prikazanih) analitika → SUMMARY, kao i kod PDF-a
  function pushSummary(r, group, dok) {
    const konto = xlTxt(r[1]);
    const mapped = lookupKonto(konto);
    entries.push({
      sourceFile: fileName, bookingDate,
      rsCode: konto, rsName: mapped?.rsName || xlTxt(r[3]),
      enCode: mapped?.enKonto || '', enName: mapped?.enName || '', type: mapped?.type || '',
      debit: xlNum(r[4]), credit: xlNum(r[5]),
      detailLevel: 'SUMMARY', parentCode: '', analyticCode: '', analyticName: '',
      mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED',
      notes: xlNotes(dok || r[6], r[7]),
      _group: group, _headerD: xlNum(r[4]), _headerC: xlNum(r[5])
    });
  }

  // Zatvori tekući blok analitika: veži ga za najkraći sufiks pending konto-redova
  // čiji se zbir Duguje/Potražuje poklapa sa zbirom analitika.
  function flushAna() {
    if (!anaBuf.length) return;
    const ad = sumD(anaBuf), ap = sumC(anaBuf);
    let take = -1;
    for (let n = 1; n <= pending.length; n++) {
      const suf = pending.slice(pending.length - n);
      if (Math.abs(sumD(suf) - ad) < XL_EPS && Math.abs(sumC(suf) - ap) < XL_EPS) { take = n; break; }
    }
    if (take < 0) {
      // Ne bi trebalo da se desi (0/740 na junskom skupu) — vežemo za poslednji konto red
      // i prijavimo, umesto da tiho izgubimo analitike.
      console.warn('[XLSX] ' + fileName + ': analitike se ne poklapaju ni sa jednim kontom (D=' + ad + ' P=' + ap + ')');
      take = pending.length ? 1 : 0;
    }
    if (!take) { anaBuf = []; return; } // analitika bez konta — nemamo je gde vezati

    const konta = pending.splice(pending.length - take, take);
    // Konto redovi ispred grupe nemaju analitiku — idu kao SUMMARY, u originalnom redosledu
    pending.splice(0, pending.length).forEach(r => pushSummary(r, ++groupCounter, ''));

    const konto = xlTxt(konta[0][1]);
    const dok = anaBuf.map(a => xlTxt(a[6])).find(Boolean) || '';
    // Grupa treba da bude jedan konto (npr. 435 u Duguje i 435 u Potražuje redu). Ako sufiks
    // obuhvati dva različita konta, iznosi ostaju tačni (svaki red nosi svoj) — samo prijavi.
    if (konta.some(r => xlTxt(r[1]) !== konto)) {
      console.warn('[XLSX] ' + fileName + ': analitike obuhvataju više konta [' +
        [...new Set(konta.map(r => xlTxt(r[1])))].join(', ') + ']');
    }

    // Analitike se prikazuju samo za konta iz liste (dobavljači/kupci) — isto kao PDF
    if (!detailedAccts.some(da => konto.startsWith(da))) {
      konta.forEach(r => pushSummary(r, ++groupCounter, dok));
      anaBuf = [];
      return;
    }

    // Svaki konto red dobija svoju grupu i svoje analitike — _group mora da veže TAČNO jedan
    // PARENT sa njegovom decom, jer editKjEntry/deleteKjEntry rekalkulišu PARENT iz dece.
    // Analitiku vezujemo za konto red istog iznosa, pa za red iste strane (Duguje/Potražuje).
    const kids = konta.map(() => []);
    for (const a of anaBuf) {
      const ad = xlNum(a[4]), ac = xlNum(a[5]);
      let i = konta.findIndex(r => Math.abs(xlNum(r[4]) - ad) < XL_EPS && Math.abs(xlNum(r[5]) - ac) < XL_EPS);
      if (i < 0) i = konta.findIndex(r => (ad !== 0 && xlNum(r[4]) !== 0) || (ac !== 0 && xlNum(r[5]) !== 0));
      if (i < 0) i = 0;
      kids[i].push(a);
    }

    // Podela mora da bude tačna: analitike jednog konto reda moraju da daju baš njegov iznos.
    // Ako nije (nijedan pravi nalog to ne radi, ali ne pogađamo) — odustani od analitika i pusti
    // sve konto redove kao SUMMARY. Iznosi tada ostaju tačni i u aplikaciji i u QuickBooks
    // eksportu (koji izvozi analitike umesto PARENT-a), umesto da eksport tiho ode u minus.
    const partitionOk = konta.every((r, i) =>
      Math.abs(sumD(kids[i]) - xlNum(r[4])) < XL_EPS && Math.abs(sumC(kids[i]) - xlNum(r[5])) < XL_EPS);
    if (!partitionOk) {
      console.warn('[XLSX] ' + fileName + ': analitike konta ' + konto +
        ' se ne dele tačno po redovima — prikazujem zbirno (bez analitika)');
      konta.forEach(r => pushSummary(r, ++groupCounter, dok));
      anaBuf = [];
      return;
    }

    konta.forEach((r, i) => {
      const group = ++groupCounter;
      // Konto red bez ijedne analitike ide kao SUMMARY — PARENT se NE izvozi u QuickBooks
      // (izvoze se njegove analitike), pa bi PARENT bez dece izgubio iznos iz eksporta.
      if (!kids[i].length) { pushSummary(r, group, dok); return; }

      const kr = xlTxt(r[1]);
      const m = lookupKonto(kr);
      const rsName = m?.rsName || xlTxt(r[3]);
      entries.push({
        sourceFile: fileName, bookingDate,
        rsCode: kr, rsName,
        enCode: m?.enKonto || '', enName: m?.enName || '', type: m?.type || '',
        debit: xlNum(r[4]), credit: xlNum(r[5]),
        detailLevel: 'PARENT', parentCode: '', analyticCode: '', analyticName: '',
        mappingStatus: m ? 'MAPPED' : 'UNMAPPED',
        notes: xlNotes(dok || r[6], r[7]),
        _group: group, _headerD: xlNum(r[4]), _headerC: xlNum(r[5])
      });
      kids[i].forEach(a => entries.push({
        sourceFile: fileName, bookingDate,
        rsCode: kr, rsName,
        enCode: m?.enKonto || '', enName: m?.enName || '', type: m?.type || '',
        debit: xlNum(a[4]), credit: xlNum(a[5]),
        detailLevel: 'ANALYTIC', parentCode: kr,
        analyticCode: xlTxt(a[2]), analyticName: xlTxt(a[3]),
        mappingStatus: m ? 'MAPPED' : 'UNMAPPED',
        notes: xlNotes(a[6], a[7]),
        _group: group
      }));
    });
    anaBuf = [];
  }

  for (const r of body) {
    if (xlTxt(r[1])) { flushAna(); pending.push(r); }  // konto red
    else anaBuf.push(r);                               // analitika red
  }
  flushAna();
  pending.splice(0, pending.length).forEach(r => pushSummary(r, ++groupCounter, ''));

  if (docKey) entries.forEach(e => { e._docKey = docKey; });

  // Kontrola: zbir konto-redova mora da se poklopi sa UKUPNO redom iz ERP-a
  if (ukupnoD != null) {
    const d = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
    const c = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
    if (Math.abs(d - ukupnoD) > XL_EPS || Math.abs(c - ukupnoC) > XL_EPS) {
      console.warn('[XLSX] ' + fileName + ': zbir stavki (D=' + d.toFixed(2) + ' P=' + c.toFixed(2) +
        ') ne odgovara UKUPNO redu (D=' + ukupnoD.toFixed(2) + ' P=' + ukupnoC.toFixed(2) + ')');
    }
  }
  return entries;
}

// Učitaj .xlsx fajl i parsiraj prvi sheet
async function parseSingleExcel(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('prazan Excel fajl');
  // raw:true — iznosi ostaju brojevi (bez lokalizovanog formatiranja), šifre konta ostaju
  // stringovi pa vodeće nule ("027", "0000") ne nestaju
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: true });
  const entries = parseKnjizenjeSheet(rows, file.name);
  // Šifre iz ERP-a su merodavne (za razliku od OCR-a) — vidi ANALYTIC-FIX u confirmSaveIzvod
  entries.forEach(e => { e._xlsx = true; });

  // Per-file balans (isto kao PDF put)
  const fileD = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.debit, 0);
  const fileC = entries.filter(e => e.detailLevel !== 'ANALYTIC').reduce((s, e) => s + e.credit, 0);
  if (entries.length > 0 && Math.abs(fileD - fileC) > 1) {
    kjImbalancedFiles.push({ name: file.name, d: fileD, c: fileC });
  }
  return entries;
}

// ============================================================
// OCR TEXT PARSER
// ============================================================
function parseOcrText(text, fileName) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const rawEntries = []; // privremeno, pre korekcije kolona
  const detailedAccts = getDetailedAccounts();

  // Izvuci datum knjizenja iz zaglavlja
  let bookingDate = '';
  for (const line of lines) {
    const dm = line.match(/OD\s+(\d{1,2})[.\s](\d{1,2})[.\s](\d{4})/i);
    if (dm) { bookingDate = dm[1].padStart(2, '0') + '.' + dm[2].padStart(2, '0') + '.' + dm[3]; break; }
  }

  // Pronađi TOTALE sa dna dokumenta (poslednji red sa 2 broja)
  let expectedDuguje = 0, expectedPotrazuje = 0;
  for (let i = lines.length - 1; i >= 0; i--) {
    const clean = lines[i].replace(/\d{1,2}\.\d{1,2}\.\d{4}/g, '');
    const nums = [];
    const pat = /(\d[\d\s]*\d|\d)[.,](\d{2})\b/g;
    let m;
    while ((m = pat.exec(clean)) !== null) {
      const val = parseFloat(m[1].replace(/\s/g, '') + '.' + m[2]);
      if (val > 1) nums.push(val);
    }
    if (nums.length === 2) { expectedDuguje = nums[0]; expectedPotrazuje = nums[1]; break; }
  }

  let currentParent = null;
  const numPat = /^(\d{1,5})\s+(.+)/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^(Konto|Sin|Naziv|Duguje|Potrazuje|Dokument|Opis)/i.test(line)) continue;
    if (/^(Privredno|MORAVSKA|PIB|NALOG|FINANSIJSKO|PR\s|36204)/i.test(line)) continue;
    // Skip ćirilica zaglavlja (OCR artefakti)
    if (/^[\u0400-\u04FF\s|]{3,}$/i.test(line) && !/\d/.test(line)) continue;
    if (/^\|?\s*\d{0,2}\s*[\u0400-\u04FF]/.test(line) && !/\d{3,}/.test(line)) continue;

    const m = line.match(numPat);
    if (!m) continue;

    const konto = m[1];
    const rest = m[2];
    const amounts = extractAmounts(rest);
    const name = extractName(rest);
    const lineDate = extractDateFromLine(rest) || bookingDate;
    const docRef = extractDocRef(rest);
    // Detektuj parent konto: konto >= 3 cifre, uppercase naziv, ili poznat u kontnom planu
    const knownKonto = lookupKonto(konto);
    const isParent = (konto.length >= 3 && name === name.toUpperCase() && name.length > 3) || (konto.length >= 3 && knownKonto);

    if (isParent) {
      // Ako prethodni parent je bio detaljan i nema summary, dodaj ga
      if (currentParent) {
        const prevIsDetailed = true /* svaki parent sa analitikama je detaljan */;
        if (prevIsDetailed && !rawEntries.some(e => e.rsCode === currentParent.konto && e.detailLevel === 'PARENT')) {
          const pm = lookupKonto(currentParent.konto);
          const parentTotal = rawEntries.filter(e => e.parentCode === currentParent.konto).reduce((s, e) => ({ d: s.d + e.debit, c: s.c + e.credit }), { d: 0, c: 0 });
          rawEntries.splice(
            rawEntries.findIndex(e => e.parentCode === currentParent.konto), 0,
            {
              sourceFile: fileName, bookingDate: lineDate,
              rsCode: currentParent.konto, rsName: currentParent.name,
              enCode: pm ? pm.enKonto : '', enName: pm ? pm.enName : '',
              type: pm ? pm.type : '',
              debit: parentTotal.d, credit: parentTotal.c,
              detailLevel: 'PARENT', parentCode: '', analyticCode: '', analyticName: '',
              mappingStatus: pm ? 'MAPPED' : 'UNMAPPED',
              notes: ''
            }
          );
        }
      }

      currentParent = { konto, name, ownDebit: amounts.debit, ownCredit: amounts.credit };
      const isDetailed = true; // odloženo do closeParent
      if (false) { // SUMMARY se kreira u closeParent ako nema analitika
        const mapped = lookupKonto(konto);
        rawEntries.push({
          sourceFile: fileName, bookingDate: lineDate,
          rsCode: konto, rsName: name,
          enCode: mapped ? mapped.enKonto : '', enName: mapped ? mapped.enName : '',
          type: mapped ? mapped.type : '',
          debit: amounts.debit, credit: amounts.credit,
          detailLevel: 'SUMMARY', parentCode: '', analyticCode: '', analyticName: '',
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED',
          notes: docRef
        });
      }
      // Za detaljne konte, parent red se dodaje kad se pojavi sledeći parent (gore)
    } else if (currentParent) {
      const parentIsDetailed = true /* svaki parent sa analitikama je detaljan */;
      if (parentIsDetailed) {
        // ANALYTIC — svaka analitika posebno
        const mapped = lookupKonto(currentParent.konto);
        rawEntries.push({
          sourceFile: fileName, bookingDate: lineDate,
          rsCode: currentParent.konto, rsName: currentParent.name,
          enCode: mapped ? mapped.enKonto : '', enName: mapped ? mapped.enName : '',
          type: mapped ? mapped.type : '',
          debit: amounts.debit, credit: amounts.credit,
          detailLevel: 'ANALYTIC', parentCode: currentParent.konto,
          analyticCode: konto,
          analyticName: name,
          mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED',
          notes: docRef
        });
      }
      // Analitike za ne-detaljne konte se ignorišu (samo summary)
    }
  }

  // Dodaj parent red za poslednji detaljan konto
  if (currentParent) {
    const lastIsDetailed = true /* svaki parent sa analitikama je detaljan */;
    if (lastIsDetailed && !rawEntries.some(e => e.rsCode === currentParent.konto && e.detailLevel === 'PARENT')) {
      const pm = lookupKonto(currentParent.konto);
      const parentTotal = rawEntries.filter(e => e.parentCode === currentParent.konto).reduce((s, e) => ({ d: s.d + e.debit, c: s.c + e.credit }), { d: 0, c: 0 });
      const insertIdx = rawEntries.findIndex(e => e.parentCode === currentParent.konto);
      if (insertIdx >= 0) {
        rawEntries.splice(insertIdx, 0, {
          sourceFile: fileName, bookingDate,
          rsCode: currentParent.konto, rsName: currentParent.name,
          enCode: pm ? pm.enKonto : '', enName: pm ? pm.enName : '',
          type: pm ? pm.type : '',
          debit: parentTotal.d, credit: parentTotal.c,
          detailLevel: 'PARENT', parentCode: '', analyticCode: '', analyticName: '',
          mappingStatus: pm ? 'MAPPED' : 'UNMAPPED', notes: ''
        });
      }
    }
  }
  // NEMA automatske korekcije — korisnik ručno popravlja preko "Zameni D↔P" dugmića
  const entries = [...rawEntries];
  return entries;
}

function extractAmounts(text) {
  const clean = text
    .replace(/\d{1,2}\.\d{1,2}\.\d{4}/g, '  ')       // DD.MM.YYYY
    .replace(/\d{1,2}\.\d{1,2}\.\d{2}(?!\d)/g, '  ')  // DD.MM.YY
    .replace(/\b(\d{1,2})\.(\d{2})\b/g, (m, d, mo) => // DD.MM (bez godine)
      (parseInt(d) >= 1 && parseInt(d) <= 31 && parseInt(mo) >= 1 && parseInt(mo) <= 12) ? '  ' : m
    );

  // Proveri da li OCR koristi | kao separator kolona (duguje | potrazuje)
  const parts = clean.split(/\|/);

  function findAmount(str) {
    const nums = [];
    const pat = /(\d[\d\s]*\d|\d)[.,](\d{2})\b/g;
    let m;
    while ((m = pat.exec(str)) !== null) {
      const whole = m[1].replace(/\s/g, '');
      const val = parseFloat(whole + '.' + m[2]);
      if (Math.abs(val) > 0.005) nums.push(val);
    }
    return nums;
  }

  if (parts.length >= 2) {
    // Ima | separator — levo je duguje, desno potražuje
    const leftNums = findAmount(parts[0]);
    const rightNums = findAmount(parts.slice(1).join(' '));
    return { debit: leftNums[0] || 0, credit: rightNums[0] || 0 };
  }

  // Nema | separator — uzmi sve iznose redom
  const all = findAmount(clean);
  return { debit: all[0] || 0, credit: all.length > 1 ? all[1] : 0 };
}

function extractName(text) {
  return text
    .replace(/(\d[\d\s]*\d|\d)[.,]\d{2}/g, '')
    .replace(/\d{10,}/g, '')
    .replace(/izvod\s+banke[^|]*/gi, '')
    .replace(/FAZ-\S+/g, '')
    .replace(/R-\S+/g, '')
    .replace(/[|]/g, '')
    .replace(/\d{1,2}\.\d{1,2}\.\d{4}/g, '')
    .trim()
    .replace(/\s{2,}/g, ' ')
    .replace(/[,.:;]+$/, '')
    .trim();
}

// Izvuci datum iz Opis kolone (npr. "izvod banke br. 1 od 05.01.2026")
function extractDateFromLine(text) {
  const m = text.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (m) return m[1].padStart(2, '0') + '.' + m[2].padStart(2, '0') + '.' + m[3];
  return '';
}

// Parsiraj iznos iz stringa (podržava srpski format sa tačkama i zarezom)
function parseAmount(s) {
  const cleaned = s.replace(/\s/g, '');
  let val;
  if (cleaned.includes(',') && cleaned.includes('.')) val = parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  else if (cleaned.includes(',')) val = parseFloat(cleaned.replace(',', '.')) || 0;
  else val = parseFloat(cleaned) || 0;
  return val; // negativni iznosi (storno) ostaju negativni
}

// Proveri da li tekst izgleda kao datum (DD.MM ili DD.MM.YY ili DD.MM.YYYY), a ne iznos
function isDateLike(text) {
  // Zahtevaj godinu — DD.MM bez godine je dvosmisleno (6.08 = datum ili iznos?)
  const m = text.match(/^(\d{1,2})\.(\d{2})\.(\d{2,4})$/);
  if (!m) return false;
  const d = parseInt(m[1]), mo = parseInt(m[2]);
  return d >= 1 && d <= 31 && mo >= 1 && mo <= 12;
}

// Izvuci referencu dokumenta
function extractDocRef(text) {
  const refs = [];
  const m1 = text.match(/izvod\s+banke[^|]*/i);
  if (m1) refs.push(m1[0].trim());
  const m2 = text.match(/\d{15,}/);
  if (m2) refs.push(m2[0]);
  const m3 = text.match(/FAZ-\S+/);
  if (m3) refs.push(m3[0]);
  const m4 = text.match(/R-\S+/);
  if (m4) refs.push(m4[0]);
  return refs.join(' | ');
}

// ============================================================
// VENDOR REVIEW PANEL — pregled svih uparivanja
// ============================================================
function showVendorReviewPanel() {
  const matched = window.__kjVendorMatched || [];
  const unmatched = window.__kjVendorUnmatched || [];
  if (!matched.length && !unmatched.length) return;
  const out = document.getElementById('kjOut');
  if (!out) return;
  const kfData = window.__ft_kfData || [];

  const panel = document.createElement('div');
  panel.className = 'card';
  panel.style.cssText = 'padding:18px;margin-bottom:16px;border:1px solid var(--acc);background:rgba(201,168,76,.06)';
  panel.id = 'vendorReviewPanel';

  const title = document.createElement('div'); title.style.cssText = 'font-size:15px;font-weight:700;color:var(--acc);margin-bottom:12px';
  title.textContent = '\uD83D\uDC65 Pregled dobavljača (' + (matched.length + unmatched.length) + ')';
  panel.appendChild(title);

  // Sekcija 1: Automatski upareno
  if (matched.length) {
    const h = document.createElement('div'); h.style.cssText = 'font-size:13px;font-weight:600;color:var(--suc);margin-bottom:6px';
    h.textContent = '\u2713 Automatski upareno po šifri (' + matched.length + '):';
    panel.appendChild(h);
    const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:12px;border-collapse:collapse;margin-bottom:12px';
    const thead = document.createElement('tr');
    ['Šifra', 'OCR ime', 'Upareno sa', 'Konto', ''].forEach(t => {
      const th = document.createElement('th'); th.style.cssText = 'text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);color:var(--tx3);font-size:11px'; th.textContent = t; thead.appendChild(th);
    });
    tbl.appendChild(thead);
    matched.forEach(m => {
      const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:6px 8px;font-family:var(--mn);color:var(--acc);font-weight:600'; td1.textContent = m.sifra;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:6px 8px;color:var(--tx3)'; td2.textContent = m.ocrName;
      const td3 = document.createElement('td'); td3.style.cssText = 'padding:6px 8px;color:var(--suc);font-weight:500'; td3.textContent = m.dbName;
      const td4 = document.createElement('td'); td4.style.cssText = 'padding:6px 8px;font-family:var(--mn);color:var(--tx3)'; td4.textContent = m.konto;
      const td5 = document.createElement('td'); td5.style.cssText = 'padding:6px 8px';
      const btnChange = document.createElement('button'); btnChange.className = 'bsm'; btnChange.style.cssText = 'font-size:10px;padding:2px 8px';
      btnChange.textContent = 'Promeni';
      btnChange.onclick = () => {
        // Otvori dropdown za re-assign
        const dropWrap = tr.nextElementSibling;
        if (dropWrap && dropWrap.classList.contains('vendor-drop')) { dropWrap.remove(); return; }
        const dtr = document.createElement('tr'); dtr.classList.add('vendor-drop');
        const dtd = document.createElement('td'); dtd.colSpan = 5; dtd.style.cssText = 'padding:4px 8px';
        const search = document.createElement('input'); search.type = 'text'; search.className = 'fi';
        search.style.cssText = 'width:100%;font-size:12px;padding:6px 10px;margin-bottom:4px;border-radius:6px';
        search.placeholder = 'Pretraži komitente...';
        const listDiv = document.createElement('div'); listDiv.style.cssText = 'max-height:150px;overflow-y:auto;border:1px solid var(--border);border-radius:6px;background:var(--surface)';
        function renderList(q) {
          listDiv.textContent = '';
          const filt = q ? kfData.filter(k => ((k.naziv||'').toLowerCase().includes(q.toLowerCase()) || String(k.sifra||'').includes(q))) : kfData.slice(0, 30);
          filt.slice(0, 30).forEach(k => {
            const opt = document.createElement('div'); opt.style.cssText = 'padding:5px 10px;cursor:pointer;font-size:12px;border-bottom:1px solid var(--border-lt)';
            opt.onmouseenter = () => opt.style.background = 'var(--hover)'; opt.onmouseleave = () => opt.style.background = '';
            opt.textContent = (k.sifra || '—') + ' — ' + (k.naziv || '');
            opt.onclick = () => {
              kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticCode === m.sifra).forEach(e => { e.analyticName = (k.naziv || '').trim(); });
              td3.textContent = (k.naziv || '').trim(); td3.style.color = 'var(--warn)';
              dtr.remove(); renderKjResults(); toast(m.ocrName + ' → ' + k.naziv);
            };
            listDiv.appendChild(opt);
          });
        }
        search.oninput = () => renderList(search.value); dtd.appendChild(search); dtd.appendChild(listDiv); dtr.appendChild(dtd);
        tr.after(dtr); renderList(''); setTimeout(() => search.focus(), 50);
      };
      td5.appendChild(btnChange);
      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tr.appendChild(td5);
      tbl.appendChild(tr);
    });
    panel.appendChild(tbl);
  }

  // Sekcija 2: Neupareno
  if (unmatched.length) {
    const h = document.createElement('div'); h.style.cssText = 'font-size:13px;font-weight:600;color:var(--dng);margin-bottom:6px';
    h.textContent = '\u26A0 Nije u bazi komitenata (' + unmatched.length + '):';
    panel.appendChild(h);
    unmatched.forEach(u => {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:6px 0;font-size:12px;flex-wrap:wrap';
      const sp = document.createElement('span'); sp.style.cssText = 'min-width:200px';
      const codeSpan = document.createElement('span'); codeSpan.style.cssText = 'font-family:var(--mn);color:var(--acc);font-weight:600;margin-right:6px'; codeSpan.textContent = u.code || '—';
      const nameSpan = document.createElement('span'); nameSpan.style.color = 'var(--tx2)'; nameSpan.textContent = u.ocrName;
      const kontoSpan = document.createElement('span'); kontoSpan.style.cssText = 'font-family:var(--mn);color:var(--tx3);margin-left:6px;font-size:10px'; kontoSpan.textContent = '(konto ' + u.konto + (u.file ? ' | ' + u.file : '') + ')';
      sp.appendChild(codeSpan); sp.appendChild(nameSpan); sp.appendChild(kontoSpan);
      // Dodeli
      const btnAssign = document.createElement('button'); btnAssign.className = 'bsm'; btnAssign.style.cssText = 'font-size:10px;padding:2px 8px';
      btnAssign.textContent = '\u2194 Dodeli';
      const dropWrap = document.createElement('div'); dropWrap.style.cssText = 'display:none;width:100%;margin-top:4px';
      btnAssign.onclick = () => {
        if (dropWrap.style.display !== 'none') { dropWrap.style.display = 'none'; return; }
        dropWrap.style.display = 'block'; dropWrap.textContent = '';
        const search = document.createElement('input'); search.type = 'text'; search.className = 'fi';
        search.style.cssText = 'width:100%;font-size:12px;padding:6px 10px;margin-bottom:4px;border-radius:6px';
        search.placeholder = 'Pretraži komitente...';
        const listDiv = document.createElement('div'); listDiv.style.cssText = 'max-height:150px;overflow-y:auto;border:1px solid var(--border);border-radius:6px;background:var(--surface)';
        function renderList(q) {
          listDiv.textContent = '';
          const filt = q ? kfData.filter(k => ((k.naziv||'').toLowerCase().includes(q.toLowerCase()) || String(k.sifra||'').includes(q))) : kfData.slice(0, 30);
          filt.slice(0, 30).forEach(k => {
            const opt = document.createElement('div'); opt.style.cssText = 'padding:5px 10px;cursor:pointer;font-size:12px;border-bottom:1px solid var(--border-lt)';
            opt.onmouseenter = () => opt.style.background = 'var(--hover)'; opt.onmouseleave = () => opt.style.background = '';
            opt.textContent = (k.sifra || '—') + ' — ' + (k.naziv || '');
            opt.onclick = () => {
              kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticCode === u.code).forEach(e => { e.analyticName = (k.naziv || '').trim(); e._komitentMatched = true; });
              row.innerHTML = '<span style="color:var(--suc);font-size:11px">\u2713 ' + u.code + ' ' + u.ocrName + ' \u2192 ' + k.naziv + '</span>';
              renderKjResults(); toast(u.ocrName + ' → ' + k.naziv);
            };
            listDiv.appendChild(opt);
          });
        }
        search.oninput = () => renderList(search.value); dropWrap.appendChild(search); dropWrap.appendChild(listDiv);
        renderList(''); setTimeout(() => search.focus(), 50);
      };
      // Novi
      const btnNew = document.createElement('button'); btnNew.className = 'bsm'; btnNew.style.cssText = 'font-size:10px;padding:2px 8px;background:var(--acc);color:#fff';
      btnNew.textContent = '+ Novi';
      btnNew.onclick = async () => {
        try {
          const clean = u.ocrName.replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim();
          await sb.from('komitenti_full').insert({ naziv: clean, sifra: parseInt(u.code) || 0 });
          if (window.__ft_kfData) window.__ft_kfData.push({ naziv: clean, sifra: parseInt(u.code) || 0 });
          row.innerHTML = '<span style="color:var(--suc);font-size:11px">\u2713 ' + u.code + ' ' + clean + ' dodat u bazu</span>';
        } catch (e) { toast('Greška: ' + e.message, 'err'); }
      };
      row.appendChild(sp); row.appendChild(btnAssign); row.appendChild(btnNew); row.appendChild(dropWrap);
      panel.appendChild(row);
    });
  }

  // Zatvori dugme
  const closeDiv = document.createElement('div'); closeDiv.style.cssText = 'text-align:right;margin-top:10px';
  const closeBtn = document.createElement('button'); closeBtn.className = 'bsm'; closeBtn.textContent = 'Zatvori';
  closeBtn.onclick = () => panel.remove();
  closeDiv.appendChild(closeBtn); panel.appendChild(closeDiv);

  // Dodaj na vrh rezultata
  const existing = document.getElementById('vendorReviewPanel');
  if (existing) existing.remove();
  out.insertBefore(panel, out.firstChild);
}

// ============================================================
// RESULTS RENDERING (EN only)
// ============================================================
function renderKjResults() {
  if (!kjParsedEntries.length) return;
  const cur = getCurrencyLabel();
  const kurs = getKurs();
  const isUSD = cur === 'USD';
  const convert = v => isUSD && kurs > 1 ? v / kurs : v;

  // Sabiraj samo SUMMARY + PARENT (analitike su već uračunate u PARENT)
  const nonAnalytic = kjParsedEntries.filter(e => e.detailLevel !== 'ANALYTIC');
  const totalDebit = nonAnalytic.reduce((s, e) => s + convert(e.debit), 0);
  const totalCredit = nonAnalytic.reduce((s, e) => s + convert(e.credit), 0);
  const mapped = kjParsedEntries.filter(e => e.mappingStatus === 'MAPPED').length;
  const unmapped = kjParsedEntries.filter(e => e.mappingStatus === 'UNMAPPED').length;
  const files = new Set(kjParsedEntries.map(e => e.sourceFile)).size;

  document.getElementById('kjStats').style.display = 'block';
  document.getElementById('kjExpBar').style.display = 'flex';
  const bar = document.getElementById('kjStBar'); bar.textContent = '';
  [
    { l: 'Fajlova', v: files },
    { l: 'Stavki', v: kjParsedEntries.length },
    { l: 'Mapirano', v: mapped, c: 'var(--suc)' },
    { l: 'Nemapirano', v: unmapped, c: unmapped ? 'var(--warn)' : undefined },
    { l: 'Duguje (' + cur + ')', v: fNum(totalDebit), c: 'var(--dng)', fs: '13px' },
    { l: 'Potra\u017Euje (' + cur + ')', v: fNum(totalCredit), c: 'var(--acc)', fs: '13px' },
    { l: 'Saldo (razlika)', v: fNum(Math.abs(totalDebit - totalCredit)), c: Math.abs(totalDebit - totalCredit) < 1 ? 'var(--suc)' : 'var(--dng)', fs: '13px' }
  ].forEach(s => {
    if (s.v === 0 && !s.c) return;
    const d = document.createElement('div'); d.className = 'sti';
    const sl = document.createElement('div'); sl.className = 'stl'; sl.textContent = s.l;
    const sv = document.createElement('div'); sv.className = 'stv'; sv.textContent = s.v; if (s.c) sv.style.color = s.c; if (s.fs) sv.style.fontSize = s.fs;
    d.appendChild(sl); d.appendChild(sv); bar.appendChild(d);
  });

  const out = document.getElementById('kjOut'); out.textContent = '';

  // Debit=Credit validation per source file (samo SUMMARY + PARENT)
  const fileGroups = {};
  kjParsedEntries.filter(e => e.detailLevel !== 'ANALYTIC').forEach(e => {
    const key = e.sourceFile;
    if (!fileGroups[key]) fileGroups[key] = { debit: 0, credit: 0 };
    fileGroups[key].debit += e.debit;
    fileGroups[key].credit += e.credit;
  });
  const imbalanced = Object.entries(fileGroups).filter(([k, v]) => Math.abs(v.debit - v.credit) > 0.01);
  if (imbalanced.length) {
    const warn = document.createElement('div'); warn.className = 'card'; warn.style.cssText = 'padding:14px 18px;background:rgba(255,181,71,.06);border:1px solid rgba(255,181,71,.15);margin-bottom:16px';
    const wt = document.createElement('div'); wt.style.cssText = 'font-size:13px;color:var(--warn);font-weight:600;margin-bottom:8px'; wt.textContent = '\u26A0 Duguje \u2260 Potra\u017Euje u ' + imbalanced.length + ' fajl(ova):';
    warn.appendChild(wt);
    imbalanced.forEach(([file, v]) => {
      const shortName = file.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '');
      const fileId = 'kj-file-' + shortName.replace(/[^a-zA-Z0-9]/g, '-');
      const row = document.createElement('div'); row.style.cssText = 'font-size:12px;color:var(--acc);padding:3px 0;cursor:pointer;text-decoration:underline';
      row.textContent = shortName + ': Duguje=' + fNum(v.debit) + ' Potra\u017Euje=' + fNum(v.credit) + ' (saldo: ' + fNum(Math.abs(v.debit - v.credit)) + ')';
      row.onclick = () => { const el = document.getElementById(fileId); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
      warn.appendChild(row);
    });
    out.appendChild(warn);
  }
  // Grupisanje po source fajlu
  const byFile = {};
  kjParsedEntries.forEach(e => {
    const key = e.sourceFile;
    if (!byFile[key]) byFile[key] = [];
    byFile[key].push(e);
  });

  for (const [file, entries] of Object.entries(byFile)) {
    const shortName = file.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '');
    const fileId = 'kj-file-' + shortName.replace(/[^a-zA-Z0-9]/g, '-');
    // File header
    const fhdr = document.createElement('div');
    fhdr.id = fileId;
    fhdr.style.cssText = 'padding:10px 16px;background:var(--surface);border:1px solid var(--border);border-radius:var(--rs);margin-bottom:2px;font-size:13px;font-weight:600;color:var(--tx);display:flex;justify-content:space-between';
    const fhdrL = document.createElement('span'); fhdrL.textContent = shortName;
    const fhdrDate = document.createElement('span'); fhdrDate.style.color = 'var(--tx3)'; fhdrDate.textContent = entries[0]?.bookingDate || '';
    fhdr.appendChild(fhdrL); fhdr.appendChild(fhdrDate);
    out.appendChild(fhdr);

    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto;margin-bottom:16px';
    const tbl = document.createElement('table'); tbl.className = 'dtbl'; tbl.style.fontSize = '12px';
    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    ['RS Konto', 'Naziv (RS)', 'EN Konto', 'Naziv (EN)', 'Analitika', 'Duguje (' + cur + ')', 'Potra\u017Euje (' + cur + ')', 'Napomena', 'Akcije'].forEach(h => {
      const th = document.createElement('th'); th.textContent = h; th.style.whiteSpace = 'nowrap'; htr.appendChild(th);
    });
    thead.appendChild(htr); tbl.appendChild(thead);

    const tbody = document.createElement('tbody');
    let fileDug = 0, filePot = 0;

    entries.forEach(e => {
      const tr = document.createElement('tr');
      const isA = e.detailLevel === 'ANALYTIC';
      const isP = e.detailLevel === 'PARENT';
      if (e.mappingStatus === 'UNMAPPED') { tr.style.background = 'rgba(255,181,71,.06)'; tr.style.cursor = 'pointer'; tr.title = 'Klikni za dodelu konta'; tr.onclick = () => openFixMap(kjParsedEntries.indexOf(e)); }
      if (isA) tr.style.borderLeft = '3px solid var(--acc)';
      if (isP) tr.style.cssText += ';background:var(--surface);font-weight:700;border-top:2px solid var(--border)';

      // RS Konto — za analitike prikazuje analitičku šifru uvučeno
      const td1 = document.createElement('td'); td1.style.cssText = 'font-family:var(--mn);font-weight:600';
      if (isA) { td1.style.cssText = 'font-family:var(--mn);padding-left:20px;color:var(--tx3);font-size:11px'; td1.textContent = '\u2514 ' + (e.analyticCode || e.rsCode); }
      else { td1.textContent = e.rsCode; }

      // Naziv (RS) — za parent bold RS naziv, za analitiku parent naziv (sivi)
      const td2 = document.createElement('td');
      if (isA) { td2.style.cssText = 'color:var(--tx3);font-size:11px'; td2.textContent = e.rsName; }
      else { td2.style.fontWeight = '600'; td2.textContent = e.rsName; }

      // EN Konto
      const td3 = document.createElement('td'); td3.style.cssText = 'font-family:var(--mn);color:var(--acc);font-size:11px';
      td3.textContent = e.enCode || '';

      // EN Naziv
      const td3b = document.createElement('td'); td3b.style.cssText = 'font-size:11px;color:var(--tx2)';
      td3b.textContent = e.enName || '';

      // Analitika (dobavljac ime + šifra) — samo za analitike
      const td4 = document.createElement('td');
      if (isA && e.analyticName) {
        td4.style.cssText = 'color:var(--tx);font-weight:500';
        td4.textContent = e.analyticName;
        if (e._komitentMatched) {
          const badge = document.createElement('span'); badge.style.cssText = 'margin-left:6px;font-size:9px;padding:1px 5px;border-radius:3px;background:var(--suc);color:#fff;font-weight:600';
          badge.textContent = '\u2713'; badge.title = 'Upareno sa bazom (šifra ' + e.analyticCode + ')';
          td4.appendChild(badge);
        } else if (isA && e.analyticCode) {
          const badge = document.createElement('span'); badge.style.cssText = 'margin-left:6px;font-size:9px;padding:1px 5px;border-radius:3px;background:var(--warn);color:#000;font-weight:600';
          badge.textContent = '?'; badge.title = 'Nije upareno — šifra ' + e.analyticCode;
          td4.appendChild(badge);
        }
      }

      // Duguje
      const td5 = document.createElement('td'); td5.style.cssText = 'text-align:right;font-family:var(--mn)';
      if (e.debit) { td5.textContent = fNum(convert(e.debit)); td5.style.color = 'var(--dng)'; if (e.detailLevel !== 'ANALYTIC') fileDug += e.debit; }

      // Potrazuje
      const td6 = document.createElement('td'); td6.style.cssText = 'text-align:right;font-family:var(--mn)';
      if (e.credit) { td6.textContent = fNum(convert(e.credit)); td6.style.color = 'var(--suc)'; if (e.detailLevel !== 'ANALYTIC') filePot += e.credit; }

      // Napomena
      const td7 = document.createElement('td'); td7.style.cssText = 'font-size:11px;color:var(--tx3);max-width:180px;overflow:hidden;text-overflow:ellipsis';
      td7.textContent = e.notes || '';

      // Akcije kolona — edit i delete dugmad sa inline SVG
      const tdAct = document.createElement('td'); tdAct.style.cssText = 'white-space:nowrap;text-align:center;min-width:70px';
      const idx = kjParsedEntries.indexOf(e);
      const mkSvgBtn = (pathD, title, cls, handler) => {
        const btn = document.createElement('button');
        btn.className = cls;
        btn.title = title;
        btn.setAttribute('data-tooltip', title);
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '14'); svg.setAttribute('height', '14');
        svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
        svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
        pathD.forEach(d => { const p = document.createElementNS('http://www.w3.org/2000/svg', 'path'); p.setAttribute('d', d); svg.appendChild(p); });
        btn.appendChild(svg);
        btn.onclick = (ev) => { ev.stopPropagation(); handler(); };
        return btn;
      };
      tdAct.appendChild(mkSvgBtn(
        ['M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z'],
        'Izmeni', 'bico', () => editKjEntry(idx)
      ));
      tdAct.appendChild(mkSvgBtn(
        ['M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6', 'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2'],
        'Obriši', 'bico del', () => deleteKjEntry(idx)
      ));
      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td3b); tr.appendChild(td4); tr.appendChild(td5); tr.appendChild(td6); tr.appendChild(td7); tr.appendChild(tdAct);
      tbody.appendChild(tr);
    });

    // Total red za fajl
    const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--border)';
    // Dugme za dodavanje reda
    const addRow = document.createElement('tr');
    const addTd = document.createElement('td'); addTd.colSpan = 9; addTd.style.cssText = 'padding:6px 16px';
    const addBtn = document.createElement('button'); addBtn.className = 'bsm'; addBtn.textContent = '+ Dodaj red';
    addBtn.onclick = () => addKjEntry(file);
    addTd.appendChild(addBtn); addRow.appendChild(addTd); tbody.appendChild(addRow);

    const fileSaldo = fileDug - filePot;
    const tt1 = document.createElement('td'); tt1.colSpan = 5; tt1.style.cssText = 'text-align:right;padding-right:12px;color:var(--tx2)'; tt1.textContent = 'UKUPNO:';
    const tt2 = document.createElement('td'); tt2.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--dng)'; tt2.textContent = fNum(convert(fileDug));
    const tt3 = document.createElement('td'); tt3.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--suc)'; tt3.textContent = fNum(convert(filePot));
    const ttS = document.createElement('td'); ttS.style.cssText = 'text-align:right;font-family:var(--mn);font-weight:700;color:' + (Math.abs(fileSaldo) < 1 ? 'var(--suc)' : 'var(--dng)');
    ttS.textContent = Math.abs(fileSaldo) < 1 ? '0' : 'saldo: ' + fNum(convert(fileSaldo));
    const tt4 = document.createElement('td');
    ttr.appendChild(tt1); ttr.appendChild(tt2); ttr.appendChild(tt3); ttr.appendChild(ttS); ttr.appendChild(tt4);
    tbody.appendChild(ttr);

    tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
  }

  // DEBUG: Raw OCR tekst
  if (kjRawOcrTexts.length) {
    const dbg = document.createElement('div'); dbg.style.cssText = 'margin-top:24px';
    const dbgTitle = document.createElement('div'); dbgTitle.style.cssText = 'padding:12px 16px;background:var(--surface);border:1px solid var(--border);border-radius:var(--rs) var(--rs) 0 0;font-size:13px;font-weight:600;color:var(--warn);cursor:pointer;display:flex;justify-content:space-between;align-items:center';
    dbgTitle.textContent = 'DEBUG: Raw OCR tekst (' + kjRawOcrTexts.length + ' stranica)';
    const arrow = document.createElement('span'); arrow.textContent = '\u25BC'; arrow.style.fontSize = '10px';
    dbgTitle.appendChild(arrow);
    const dbgBody = document.createElement('div'); dbgBody.style.cssText = 'display:none;max-height:500px;overflow-y:auto';
    dbgTitle.onclick = () => { const vis = dbgBody.style.display !== 'none'; dbgBody.style.display = vis ? 'none' : 'block'; arrow.textContent = vis ? '\u25BC' : '\u25B2'; };

    kjRawOcrTexts.forEach(r => {
      const sec = document.createElement('div'); sec.style.cssText = 'border:1px solid var(--border);border-top:none;padding:12px 16px';
      const hdr = document.createElement('div'); hdr.style.cssText = 'font-size:12px;font-weight:600;color:var(--acc);margin-bottom:8px';
      hdr.textContent = r.file.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '') + ' (str. ' + r.page + ')';
      const pre = document.createElement('pre'); pre.style.cssText = 'font-family:var(--mn);font-size:11px;color:var(--tx2);white-space:pre-wrap;word-break:break-all;margin:0;line-height:1.6';
      pre.textContent = r.text;
      sec.appendChild(hdr); sec.appendChild(pre); dbgBody.appendChild(sec);
    });
    dbg.appendChild(dbgTitle); dbg.appendChild(dbgBody); out.appendChild(dbg);
  }
}

// ============================================================
// EXCEL EXPORT (QuickBooks — EN only)
// ============================================================
// Vrati listu fajlova gde Duguje != Potražuje (verovatno OCR greška) — nad ne-analitičkim redovima
function getImbalancedFiles() {
  const byFile = {};
  for (const e of kjParsedEntries) {
    if (e.detailLevel === 'ANALYTIC') continue;
    const f = e.sourceFile || '?';
    if (!byFile[f]) byFile[f] = { d: 0, c: 0 };
    byFile[f].d += e.debit || 0;
    byFile[f].c += e.credit || 0;
  }
  const out = [];
  for (const f in byFile) {
    const diff = Math.abs(byFile[f].d - byFile[f].c);
    if (diff > 0.5) out.push({ file: f, diff });
  }
  return out;
}
function shortKjFileName(f) { return (f || '').replace('FINANSIJSKO KNJIZENJE ', '').replace(/_Privredno.*/, ''); }

export function expKjXlsx() {
  if (!kjParsedEntries.length) { toast('Nema podataka za export', 'err'); return; }
  const unmappedCount = kjParsedEntries.filter(e => e.mappingStatus === 'UNMAPPED').length;
  const emptyCode = kjParsedEntries.filter(e => e.mappingStatus === 'MAPPED' && !e.enCode).length;
  const imbalanced = getImbalancedFiles();
  const warnings = [];
  if (imbalanced.length) warnings.push('⚠ ' + imbalanced.length + ' dokument(a) NE balansira (Duguje ≠ Potražuje): ' + imbalanced.map(x => shortKjFileName(x.file) + ' Δ' + x.diff.toFixed(2)).join(', '));
  if (unmappedCount) warnings.push(unmappedCount + ' stavki nije mapirano');
  if (emptyCode) warnings.push(emptyCode + ' stavki nema Account Code');
  if (warnings.length) {
    return showConfirm('Upozorenja pre exporta:\n' + warnings.join('\n') + '\n\nNastaviti?', () => doKjExport());
  }
  doKjExport();
}

function doKjExport() {
  const cur = getCurrencyLabel();
  const kurs = getKurs();
  const isUSD = cur === 'USD';
  const convert = v => isUSD && kurs > 1 ? v / kurs : v;
  const wb = XLSX.utils.book_new();
  function borderAll(c) { const b = { style: 'thin', color: { rgb: c } }; return { top: b, bottom: b, left: b, right: b }; }
  function cs(v, s) { return { v, s, t: typeof v === 'number' ? 'n' : 's' }; }
  const stH = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } }, alignment: { horizontal: 'center' }, border: borderAll('CCCCCC') };
  const stN = { font: { sz: 10 }, border: borderAll('E2E8F0') };
  const stNm = { ...stN, alignment: { horizontal: 'right' }, numFmt: '#,##0.00' };
  const stU = { ...stN, font: { sz: 10, color: { rgb: 'CC7700' } }, fill: { fgColor: { rgb: 'FFF8E1' } } };
  const stE = { ...stN, font: { sz: 10, color: { rgb: 'CC0000' } }, fill: { fgColor: { rgb: 'FFF0F0' } } };

  // Napravi supplier name cache iz komitenti_full za fuzzy match
  const supplierCache = (window.__ft_kfData || []).map(k => ({ name: (k.naziv || '').trim(), pib: (k.pib || '').trim() }));
  // Dodaj i aliases iz baze (ako postoje)
  function matchSupplier(ocrName) {
    if (!ocrName || !supplierCache.length) return ocrName;
    const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const ocrN = norm(ocrName);
    if (!ocrN) return ocrName;
    // Exact
    let m = supplierCache.find(s => norm(s.name) === ocrN);
    if (m) return m.name;
    // Contains
    m = supplierCache.find(s => { const sn = norm(s.name); return sn.length > 4 && (sn.includes(ocrN) || ocrN.includes(sn)); });
    if (m) return m.name;
    // Prefix (60%+ chars match)
    const prefix = ocrN.substring(0, Math.max(5, Math.floor(ocrN.length * 0.6)));
    m = supplierCache.find(s => { const sn = norm(s.name); return sn.length > 3 && (sn.startsWith(prefix) || prefix.startsWith(sn.substring(0, prefix.length))); });
    if (m) return m.name;
    return ocrName;
  }

  // QuickBooks Journal Entry import format
  const headers = ['*JournalNo', '*JournalDate', 'AccountCode', '*AccountName', 'Debits', 'Credits', 'Description', 'Name'];
  const data = [];
  data.push(headers.map(h => cs(h, stH)));

  // Grupiši po source fajlu — svaki fajl je jedan Journal Entry
  const exportEntries = kjParsedEntries.filter(e => e.detailLevel !== 'PARENT' && e.detailLevel !== 'ERROR');
  const byFile = {};
  exportEntries.forEach(e => {
    if (!byFile[e.sourceFile]) byFile[e.sourceFile] = [];
    byFile[e.sourceFile].push(e);
  });

  let jnr = 1;
  for (const [file, entries] of Object.entries(byFile)) {
    const shortName = file.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '');
    entries.forEach(e => {
      const st = e.mappingStatus === 'UNMAPPED' ? stU : stN;
      const stAmt = e.mappingStatus !== 'MAPPED' ? { ...stNm, fill: st.fill } : stNm;
      const acctName = e.enName || e.rsName || '';
      // Fuzzy match vendor name against supplier database
      const rawVendor = e.detailLevel === 'ANALYTIC' ? (e.analyticName || '') : '';
      const vendorName = rawVendor ? matchSupplier(rawVendor) : '';
      const desc = shortName;
      // Datum u MM/DD/YYYY format (QuickBooks standard)
      const parts = e.bookingDate.split('.');
      const qbDate = parts.length === 3 ? parts[1] + '/' + parts[0] + '/' + parts[2] : e.bookingDate;

      data.push([
        cs(jnr, stN),
        cs(qbDate, stN),
        cs(e.enCode || '', stN),
        cs(acctName, st),
        cs(e.debit ? convert(e.debit) : '', e.debit ? stAmt : stN),
        cs(e.credit ? convert(e.credit) : '', e.credit ? stAmt : stN),
        cs(desc, stN),
        cs(vendorName, stN)
      ]);
    });
    jnr++;
  }

  const ws = XLSX.utils.aoa_to_sheet(data.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 42 }, { wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 30 }];
  data.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (!ws[addr]) ws[addr] = { v: cell.v, t: cell.t || 's' }; ws[addr].s = cell.s; }); });
  XLSX.utils.book_append_sheet(wb, ws, 'Knjizenja');

  const d = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, 'knjizenja_' + cur + '_' + d + '.xlsx', { bookSST: false, cellStyles: true });
  try { if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'knjizenja', p_entity_name: 'knjizenja_' + cur + '_' + d + '.xlsx' }); } catch(_) {}
  toast('Excel eksportovan (' + cur + ')');
}

// FIX UNMAPPED ENTRIES
export function openFixMap(idx) {
  const e = kjParsedEntries[idx]; if (!e) return;
  document.getElementById('fmInfo').textContent = (e.detailLevel === 'ANALYTIC' ? e.analyticName : e.rsName || 'Unknown') + ' (' + e.sourceFile.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '') + ')';
  document.getElementById('fmEnCode').value = e.enCode;
  document.getElementById('fmEnName').value = e.enName;
  document.getElementById('fmType').value = e.type || 'Expenses';
  document.getElementById('fmIdx').value = idx;
  const sel = document.getElementById('fmKPSelect');
  sel.length = 1;
  state.kontniPlan.forEach(k => {
    const o = document.createElement('option'); o.value = k.rsKonto; o.textContent = k.rsKonto + ' \u2014 ' + k.enName + ' (' + k.type + ')'; sel.appendChild(o);
  });
  document.getElementById('mdlFixMap').classList.add('active');
}

export function fmSelectFromKP() {
  const code = document.getElementById('fmKPSelect').value; if (!code) return;
  const k = lookupKonto(code);
  if (k) { document.getElementById('fmEnCode').value = k.enKonto; document.getElementById('fmEnName').value = k.enName; document.getElementById('fmType').value = k.type; }
}

export function saveFixMap() {
  const idx = parseInt(document.getElementById('fmIdx').value);
  const enCode = document.getElementById('fmEnCode').value.trim();
  const enName = document.getElementById('fmEnName').value.trim();
  const type = document.getElementById('fmType').value;
  if (!enCode) { toast('Unesite Account Code', 'err'); return; }
  kjParsedEntries[idx].enCode = enCode;
  kjParsedEntries[idx].enName = enName;
  kjParsedEntries[idx].type = type;
  kjParsedEntries[idx].mappingStatus = 'MAPPED';
  kjParsedEntries[idx].notes = 'Ru\u010dno mapirano';
  clMdl('mdlFixMap');
  renderKjResults();
  toast('\u2705 Konto dodeljen');
}

// PDF PREVIEW
async function previewPdf(file) {
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
  const page = await pdf.getPage(1);
  const vp = page.getViewport({ scale: 2.0 });
  const canvas = document.createElement('canvas'); canvas.width = vp.width; canvas.height = vp.height;
  const ctx = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  const w = window.open('', '_blank', 'width=' + Math.min(vp.width, 900) + ',height=' + Math.min(vp.height, 700));
  if (w) { w.document.title = file.name; w.document.body.style.cssText = 'margin:0;background:#111;display:flex;justify-content:center'; w.document.body.appendChild(canvas); canvas.style.maxWidth = '100%'; canvas.style.height = 'auto'; }
}

// Cleanup Tesseract worker on page unload to prevent memory leak
// INLINE EDIT/DELETE/ADD za knjiženja stavke
export function editKjEntry(idx) {
  const e = kjParsedEntries[idx]; if (!e) return;
  const isParentOrSummary = e.detailLevel === 'PARENT' || e.detailLevel === 'SUMMARY';

  const mdl = document.getElementById('mdlEdit');
  document.getElementById('meTitle').textContent = 'Izmeni stavku: ' + (e.rsCode || '');
  const body = document.getElementById('meBody');
  body.textContent = '';

  // Duguje
  const fgD = document.createElement('div'); fgD.className = 'afg';
  const lblD = document.createElement('label'); lblD.textContent = 'Duguje';
  const inpD = document.createElement('input'); inpD.type = 'number'; inpD.className = 'ain'; inpD.step = '0.01'; inpD.value = e.debit || 0; inpD.id = '_ed_debit';
  fgD.appendChild(lblD); fgD.appendChild(inpD); body.appendChild(fgD);

  // Potražuje
  const fgC = document.createElement('div'); fgC.className = 'afg';
  const lblC = document.createElement('label'); lblC.textContent = 'Potražuje';
  const inpC = document.createElement('input'); inpC.type = 'number'; inpC.className = 'ain'; inpC.step = '0.01'; inpC.value = e.credit || 0; inpC.id = '_ed_credit';
  fgC.appendChild(lblC); fgC.appendChild(inpC); body.appendChild(fgC);

  if (e.detailLevel === 'ANALYTIC') {
    // Šifra analitike
    const fgAC = document.createElement('div'); fgAC.className = 'afg';
    const lblAC = document.createElement('label'); lblAC.textContent = 'Šifra analitike';
    const inpAC = document.createElement('input'); inpAC.className = 'ain'; inpAC.value = e.analyticCode || ''; inpAC.id = '_ed_acode';
    fgAC.appendChild(lblAC); fgAC.appendChild(inpAC); body.appendChild(fgAC);
    // Naziv analitike
    const fgAN = document.createElement('div'); fgAN.className = 'afg';
    const lblAN = document.createElement('label'); lblAN.textContent = 'Naziv analitike';
    const inpAN = document.createElement('input'); inpAN.className = 'ain'; inpAN.value = e.analyticName || ''; inpAN.id = '_ed_aname';
    fgAN.appendChild(lblAN); fgAN.appendChild(inpAN); body.appendChild(fgAN);
  }

  // Checkbox: Dodaj analitiku (samo za PARENT/SUMMARY)
  if (isParentOrSummary) {
    const chkWrap = document.createElement('div'); chkWrap.style.cssText = 'margin:16px 0 8px;display:flex;align-items:center;gap:8px';
    const chk = document.createElement('input'); chk.type = 'checkbox'; chk.id = '_ed_addAnal';
    const chkLbl = document.createElement('label'); chkLbl.textContent = 'Dodaj analitiku'; chkLbl.style.cssText = 'font-size:14px;font-weight:600;color:var(--acc);cursor:pointer'; chkLbl.htmlFor = '_ed_addAnal';
    chkWrap.appendChild(chk); chkWrap.appendChild(chkLbl); body.appendChild(chkWrap);

    const analFields = document.createElement('div'); analFields.id = '_ed_analFields'; analFields.style.display = 'none';

    const fg1 = document.createElement('div'); fg1.className = 'afg';
    const lbl1 = document.createElement('label'); lbl1.innerHTML = 'Šifra analitike <span style="color:var(--dng)">*</span>';
    const inp1 = document.createElement('input'); inp1.className = 'ain'; inp1.id = '_ed_newCode'; inp1.placeholder = 'npr. 001'; inp1.required = true;
    fg1.appendChild(lbl1); fg1.appendChild(inp1); analFields.appendChild(fg1);

    const fg2 = document.createElement('div'); fg2.className = 'afg';
    const lbl2 = document.createElement('label'); lbl2.innerHTML = 'Naziv analitike <span style="color:var(--dng)">*</span>';
    const inp2 = document.createElement('input'); inp2.className = 'ain'; inp2.id = '_ed_newName'; inp2.placeholder = 'npr. Dobavljač XY'; inp2.required = true;
    fg2.appendChild(lbl2); fg2.appendChild(inp2); analFields.appendChild(fg2);

    const fg3 = document.createElement('div'); fg3.className = 'afg';
    const lbl3 = document.createElement('label'); lbl3.textContent = 'Duguje (analitika)';
    const inp3 = document.createElement('input'); inp3.type = 'number'; inp3.className = 'ain'; inp3.step = '0.01'; inp3.value = '0'; inp3.id = '_ed_newDebit';
    fg3.appendChild(lbl3); fg3.appendChild(inp3); analFields.appendChild(fg3);

    const fg4 = document.createElement('div'); fg4.className = 'afg';
    const lbl4 = document.createElement('label'); lbl4.textContent = 'Potražuje (analitika)';
    const inp4 = document.createElement('input'); inp4.type = 'number'; inp4.className = 'ain'; inp4.step = '0.01'; inp4.value = '0'; inp4.id = '_ed_newCredit';
    fg4.appendChild(lbl4); fg4.appendChild(inp4); analFields.appendChild(fg4);

    body.appendChild(analFields);
    chk.onchange = () => { analFields.style.display = chk.checked ? 'block' : 'none'; };
  }

  // Save handler
  const saveBtn = document.getElementById('meSave');
  const newSave = saveBtn.cloneNode(true); saveBtn.parentNode.replaceChild(newSave, saveBtn);
  newSave.id = 'meSave';
  newSave.onclick = () => {
    e.debit = parseFloat(document.getElementById('_ed_debit').value) || 0;
    e.credit = parseFloat(document.getElementById('_ed_credit').value) || 0;

    if (e.detailLevel === 'ANALYTIC') {
      const ac = document.getElementById('_ed_acode'); if (ac) e.analyticCode = ac.value;
      const an = document.getElementById('_ed_aname'); if (an) e.analyticName = an.value;
    }

    // Kreiranje nove analitike
    const chk = document.getElementById('_ed_addAnal');
    if (chk && chk.checked) {
      const code = document.getElementById('_ed_newCode')?.value || '';
      const name = document.getElementById('_ed_newName')?.value || '';
      const aD = parseFloat(document.getElementById('_ed_newDebit')?.value) || 0;
      const aC = parseFloat(document.getElementById('_ed_newCredit')?.value) || 0;
      // Polja su opciona — kreira se analitika i sa praznim poljima

      const mapped = lookupKonto(e.rsCode);
      kjParsedEntries.splice(idx + 1, 0, {
        sourceFile: e.sourceFile, bookingDate: e.bookingDate,
        rsCode: e.rsCode, rsName: e.rsName,
        enCode: mapped?.enKonto || e.enCode, enName: mapped?.enName || e.enName,
        type: mapped?.type || e.type,
        debit: aD, credit: aC,
        detailLevel: 'ANALYTIC', parentCode: e.rsCode,
        analyticCode: code, analyticName: name,
        mappingStatus: e.mappingStatus, notes: 'Ručno dodato',
        _group: e._group
      });
      if (e.detailLevel === 'SUMMARY') e.detailLevel = 'PARENT';
      toast('Analitika "' + name + '" kreirana');
    } else {
      toast('Stavka izmenjena');
    }

    clMdl('mdlEdit');
    renderKjResults();
  };

  mdl.classList.add('active');
  requestAnimationFrame(() => inpD.focus());
}

export function deleteKjEntry(idx) {
  const e = kjParsedEntries[idx]; if (!e) return;
  showConfirm('Obrisati stavku "' + (e.analyticName || e.rsName || e.rsCode) + '"?', () => {
    kjParsedEntries.splice(idx, 1);
    // Rekalkuliši PARENT totale — SAMO unutar istog fajla i grupe
    kjParsedEntries.filter(p => p.detailLevel === 'PARENT').forEach(p => {
      const ch = kjParsedEntries.filter(c =>
        c._group === p._group && c.sourceFile === p.sourceFile && c.detailLevel === 'ANALYTIC'
      );
      const chD = ch.reduce((s, c) => s + c.debit, 0);
      const chC = ch.reduce((s, c) => s + c.credit, 0);
      if (ch.length && (chD > 0 || chC > 0)) { p.debit = chD; p.credit = chC; }
    });
    renderKjResults();
    toast('Stavka obrisana');
  });
}

function addKjEntry(sourceFile) {
  openEditModal('Dodaj stavku', [
    { label: 'RS Konto (šifra)', value: '', key: 'rsCode' },
    { label: 'Duguje', value: '0', key: 'debit', type: 'number', step: '0.01' },
    { label: 'Potražuje', value: '0', key: 'credit', type: 'number', step: '0.01' },
    { label: 'Analitika (opciono)', value: '', key: 'analyticName' }
  ], (vals) => {
    if (!vals.rsCode) { toast('Unesite konto', 'err'); return; }
    const mapped = lookupKonto(vals.rsCode);
    const maxGroup = kjParsedEntries.reduce((mx, e) => Math.max(mx, e._group || 0), 0);
    kjParsedEntries.push({
      sourceFile, bookingDate: '', rsCode: vals.rsCode, rsName: mapped?.rsName || vals.rsCode,
      enCode: mapped?.enKonto || '', enName: mapped?.enName || '', type: mapped?.type || '',
      debit: parseFloat(vals.debit) || 0, credit: parseFloat(vals.credit) || 0,
      detailLevel: vals.analyticName ? 'ANALYTIC' : 'SUMMARY',
      parentCode: vals.analyticName ? vals.rsCode : '', analyticCode: '', analyticName: vals.analyticName || '',
      mappingStatus: mapped ? 'MAPPED' : 'UNMAPPED', notes: 'Ručno dodato',
      _group: maxGroup + 1
    });
    renderKjResults();
    toast('Stavka dodana');
  });
}

// ============================================================
// VIEW TOGGLE: Parsiranje vs Sačuvani izvodi
// ============================================================
export function showKjView(view) {
  const parseBox = document.getElementById('kjParseBox');
  const savedBox = document.getElementById('kjSavedBox');
  const btnParse = document.getElementById('btnKjParse');
  const btnSaved = document.getElementById('btnKjSaved');
  if (view === 'saved') {
    if (parseBox) parseBox.style.display = 'none';
    if (savedBox) savedBox.style.display = 'block';
    if (btnParse) { btnParse.style.background = ''; btnParse.style.color = ''; }
    if (btnSaved) { btnSaved.style.background = 'var(--acc)'; btnSaved.style.color = '#fff'; }
    loadSavedIzvodi();
  } else {
    if (parseBox) parseBox.style.display = '';
    if (savedBox) savedBox.style.display = 'none';
    if (btnParse) { btnParse.style.background = 'var(--acc)'; btnParse.style.color = '#fff'; }
    if (btnSaved) { btnSaved.style.background = ''; btnSaved.style.color = ''; }
  }
}

// ============================================================
// SAČUVAJ IZVOD U BAZU — modal + potvrda
// ============================================================
export async function openSaveIzvodModal() {
  if (!kjParsedEntries.length) { toast('Nema podataka za čuvanje', 'err'); return; }
  const nonAnalytic = kjParsedEntries.filter(e => e.detailLevel !== 'ANALYTIC');
  const totalD = nonAnalytic.reduce((s, e) => s + e.debit, 0);
  const totalC = nonAnalytic.reduce((s, e) => s + e.credit, 0);
  const files = new Set(kjParsedEntries.map(e => e.sourceFile)).size;
  const preview = document.getElementById('izvodPreview');
  if (preview) {
    preview.textContent = '';
    [files + ' fajl(ova)', kjParsedEntries.length + ' stavki', 'Duguje: ' + fNum(totalD) + ' RSD', 'Potražuje: ' + fNum(totalC) + ' RSD'].forEach(txt => {
      const d = document.createElement('div'); d.style.cssText = 'padding:2px 0'; d.textContent = txt; preview.appendChild(d);
    });
  }
  document.getElementById('izvodNumber').value = '';
  document.getElementById('izvodGapWarn').style.display = 'none';

  // Init date pickers for period fields
  const fromEl = document.getElementById('izvodFrom');
  const toEl = document.getElementById('izvodTo');
  fromEl.value = ''; toEl.value = '';
  bindDatePicker(fromEl);
  bindDatePicker(toEl);

  // Check for gaps with existing saved izvodi
  try {
    const { data } = await sb.rpc('get_saved_izvodi', { p_user_id: state.CU.id });
    if (data && data.length) {
      // Find the latest period_to among saved izvodi
      const periods = data.filter(iz => iz.period_to).map(iz => new Date(iz.period_to)).sort((a, b) => b - a);
      if (periods.length) {
        const lastEnd = periods[0];
        const nextStart = new Date(lastEnd); nextStart.setDate(nextStart.getDate() + 1);
        fromEl.value = formatDateForInput(nextStart);
        // Set change handler to check gap
        const checkGap = () => {
          const warn = document.getElementById('izvodGapWarn');
          const fromVal = fromEl.value.match(/(\d{2})\.(\d{2})\.(\d{4})/);
          if (fromVal) {
            const fromDate = new Date(+fromVal[3], +fromVal[2] - 1, +fromVal[1]);
            const expectedStart = new Date(lastEnd); expectedStart.setDate(expectedStart.getDate() + 1);
            const gapDays = Math.round((fromDate - expectedStart) / 86400000);
            if (gapDays > 0) {
              warn.textContent = '\u26A0 Gap od ' + gapDays + ' dana! Poslednje knjiženje je do ' + formatDateForInput(lastEnd) + '. Ovaj period počinje ' + fromEl.value + '.';
              warn.style.display = 'block';
            } else { warn.style.display = 'none'; }
          }
        };
        fromEl.addEventListener('change', checkGap);
        checkGap();
      }
    }
  } catch (e) {}

  document.getElementById('mdlIzvod').classList.add('active');
  setTimeout(() => document.getElementById('izvodNumber').focus(), 100);
}

export async function confirmSaveIzvod() {
  const izvodNumber = document.getElementById('izvodNumber').value.trim();
  if (!izvodNumber) { toast('Unesite broj izvoda', 'err'); return; }
  const fromVal = document.getElementById('izvodFrom').value.trim();
  const toVal = document.getElementById('izvodTo').value.trim();
  if (!fromVal || !toVal) { toast('Unesite period (od - do)', 'err'); return; }
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }

  // Parse dates to ISO
  const fmatch = fromVal.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  const tmatch = toVal.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (!fmatch || !tmatch) { toast('Neispravan format datuma', 'err'); return; }
  const pFrom = fmatch[3] + '-' + fmatch[2] + '-' + fmatch[1];
  const pTo = tmatch[3] + '-' + tmatch[2] + '-' + tmatch[1];

  // Balans-gard: ne dozvoli tiho čuvanje neuravnoteženog dokumenta (Duguje ≠ Potražuje = verovatno OCR greška)
  const imbalanced = getImbalancedFiles();
  if (imbalanced.length && !confirmSaveIzvod._forced) {
    const names = imbalanced.map(x => shortKjFileName(x.file) + ' (Δ ' + x.diff.toFixed(2) + ')').join(', ');
    showConfirm('⚠ ' + imbalanced.length + ' dokument(a) NE balansira (Duguje ≠ Potražuje): ' + names + '. Verovatno OCR greška — preporuka je ispraviti pre čuvanja. Ipak sačuvati?', () => {
      confirmSaveIzvod._forced = true;
      confirmSaveIzvod().finally(() => { confirmSaveIzvod._forced = false; });
    });
    return;
  }

  const btn = document.querySelector('#mdlIzvod .bsav');
  if (btn) { btn.disabled = true; btn.textContent = 'Čuvam...'; }

  try {
    // Analitički kod validacija: ako isti vendor name već postoji pod istim kontom
    // sa drugačijim kodom u bazi, OCR je pogrešno pročitao cifru — ispravi.
    // NE važi za Excel: tamo šifra dolazi iz ERP-a i merodavna je, a "istorijski" kod u bazi
    // može biti baš stara OCR greška — prepisivanjem bismo pokvarili tačan podatak.
    const analEntries = kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticCode && e.analyticName && !e._xlsx);
    if (analEntries.length) {
      const kontoCodes = [...new Set(analEntries.map(e => e.rsCode))];
      const { data: existing } = await sb.from('knjizenja_entries')
        .select('rs_code,analytic_code,analytic_name')
        .eq('user_id', state.CU.id).eq('is_final', true)
        .in('rs_code', kontoCodes)
        .eq('detail_level', 'ANALYTIC');
      if (existing && existing.length) {
        const nameToCode = new Map();
        existing.forEach(e => {
          const key = e.rs_code + '|' + (e.analytic_name || '').toLowerCase().trim();
          if (!nameToCode.has(key)) nameToCode.set(key, e.analytic_code);
        });
        analEntries.forEach(e => {
          const key = e.rsCode + '|' + (e.analyticName || '').toLowerCase().trim();
          const knownCode = nameToCode.get(key);
          if (knownCode && knownCode !== e.analyticCode) {
            console.log('[ANALYTIC-FIX] ' + e.rsCode + ': "' + e.analyticName + '" code ' + e.analyticCode + ' → ' + knownCode);
            e.analyticCode = knownCode;
          }
        });
      }
    }

    const { data, error } = await sb.rpc('save_knjizenja_final', {
      p_user_id: state.CU.id,
      p_izvod_number: izvodNumber,
      p_data: kjParsedEntries,
      p_period_from: pFrom,
      p_period_to: pTo
    });
    if (error) throw error;

    // Proveri nepronadjene konte i analitike (PS se NE ažurira automatski — BS računa PS + knjiženja)
    if (data) {
      const { data: psData } = await sb.from('pocetno_stanje').select('konto');
      const psKonta = new Set((psData || []).map(p => p.konto.trim()));
      const kjKonta = new Set(kjParsedEntries.filter(e => e.detailLevel === 'SUMMARY' || e.detailLevel === 'PARENT').map(e => e.rsCode));
      const newKonta = [...kjKonta].filter(k => !psKonta.has(k));

      // Proveri nematčovane analitike (dobavljači koji nisu u bazi)
      if (!window.__ft_kfData) {
        const { data: kfRows } = await sb.from('komitenti_full').select('naziv,pib,sifra').is('deleted_at', null);
        window.__ft_kfData = kfRows || [];
      }
      const kfData = window.__ft_kfData;
      // 0. Šifra match — najpozdaniji (OCR čita broj tačno, ime može biti pogrešno)
      const kfSifraSet = new Set(kfData.map(k => String(k.sifra || '').trim()).filter(Boolean));
      // Normalizacija: dijakritike + OCR greške ($→š, ¢→ć, é→ć itd.)
      const normName = n => n.replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim()
        .toLowerCase().replace(/[$šŠ§]/g, 's').replace(/[čćČĆ¢éÉ©]/g, 'c').replace(/[žŽ]/g, 'z').replace(/[đĐÐ]/g, 'd')
        .replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ');
      const compact = n => normName(n).replace(/[^a-z0-9]/g, '').replace(/(.)\1+/g, '$1');
      const kfNormMap = new Map();
      const kfCompactMap = new Map();
      kfData.forEach(k => {
        const naziv = (k.naziv || '').trim();
        const n = normName(naziv); if (n) kfNormMap.set(n, naziv);
        const c = compact(naziv); if (c) kfCompactMap.set(c, naziv);
      });
      // Samo vendori sa konta koja prikazuju analitike (150,153,204,205,430,435,436)
      const detAccts = getDetailedAccounts();
      const allVendors = (window.__kjAllVendors || []).map(v => ({ analyticName: v.name, analyticCode: v.code }));
      const fromEntries = kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticName && detAccts.some(da => e.rsCode.startsWith(da))).map(e => ({ analyticName: e.analyticName, analyticCode: e.analyticCode }));
      const analitike = [...allVendors, ...fromEntries];
      const unmatchedNames = new Map();
      analitike.forEach(a => {
        const raw = (a.analyticName || '').trim();
        const clean = raw.replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim();
        if (!clean) return;
        // 0. Šifra match — ako analyticCode postoji u bazi komitenata, ne prikazuj
        if (a.analyticCode && kfSifraSet.has(a.analyticCode)) return;
        const norm = normName(clean);
        // Deduplikacija — ako smo već proverili isti normalized name, preskoči
        if (unmatchedNames.has(norm) || norm.length < 2) return;
        // 1. Tačan match
        if (kfNormMap.has(norm)) return;
        // 2. Kompaktni match
        const comp = compact(clean);
        if (kfCompactMap.has(comp)) return;
        // 3. Fuzzy: bez sufiksa, substring
        const fuzzy = norm.replace(/\s+(doo|pg|ad|sr|szr|jkp|jp)\s*$/, '').replace(/\s+\d+$/, '');
        let found = false;
        for (const [dbNorm] of kfNormMap) {
          const dbFuzzy = dbNorm.replace(/\s+(doo|pg|ad|sr|szr|jkp|jp)\s*$/, '').replace(/\s+\d+$/, '');
          if (fuzzy === dbFuzzy || dbNorm.includes(fuzzy) || fuzzy.includes(dbNorm)) { found = true; break; }
        }
        // 4. Kompaktni substring + Levenshtein
        if (!found) {
          for (const [dbComp] of kfCompactMap) {
            if (dbComp.includes(comp) || comp.includes(dbComp)) { found = true; break; }
            if (comp.length > 4 && dbComp.length > 4 && Math.abs(comp.length - dbComp.length) <= 2) {
              let diff = 0;
              const shorter = comp.length <= dbComp.length ? comp : dbComp;
              const longer = comp.length > dbComp.length ? comp : dbComp;
              for (let i = 0; i < shorter.length; i++) { if (shorter[i] !== longer[i]) diff++; }
              diff += longer.length - shorter.length;
              if (diff <= 2) { found = true; break; }
            }
          }
        }
        if (!found) unmatchedNames.set(norm, { name: clean, code: a.analyticCode || '' });
      });
      const unmatchedList = [...unmatchedNames.values()]; // [{name, code}, ...]

      // Prikaži obaveštenja
      if (newKonta.length || unmatchedList.length) {
        showPostSaveNotifications(newKonta, unmatchedList);
      }
    }

    clMdl('mdlIzvod');
    toast('Izvod "' + izvodNumber + '" sačuvan (' + kjParsedEntries.length + ' stavki)');

    // Auto-kreiraj cashflow za mesec ovog izvoda (iz knjiženja) — čuvanje meseca → cashflow.
    try {
      const cfM = parseInt(pTo.slice(5, 7), 10), cfY = parseInt(pTo.slice(0, 4), 10);
      const MES = ['', 'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
      const cfEl = document.getElementById('cfKurs');
      const lastK = cfEl && cfEl.value ? cfEl.value : '108';
      const kursStr = prompt('Auto-cashflow za ' + MES[cfM] + ' ' + cfY + ' (iz knjiženja).\nUnesi kurs (RSD/USD) — ili Otkaži da preskočiš:', lastK);
      if (kursStr != null && kursStr.trim() !== '') {
        const kurs = parseFloat(kursStr.replace(',', '.'));
        if (kurs > 0) {
          const { autoCreateCashflow } = await import('./cashflow.js');
          await autoCreateCashflow(cfY, cfM, kurs);
          toast('Cashflow za ' + MES[cfM] + ' ' + cfY + ' automatski kreiran i sačuvan');
        }
      }
    } catch (e) { console.error('auto-cashflow:', e); toast('Cashflow nije auto-kreiran (' + (e.message || e) + ') — uradi ga ručno u Cashflow tabu', 'warn'); }
  } catch (e) {
    toast('Greška: ' + (e.message || e), 'err');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Sačuvaj'; }
  }
}

// ============================================================
// PREGLED SAČUVANIH IZVODA
// ============================================================
async function loadSavedIzvodi() {
  const list = document.getElementById('kjSavedList');
  const empty = document.getElementById('kjSavedEmpty');
  const detail = document.getElementById('kjSavedDetail');
  if (!list) return;
  list.textContent = '';
  if (detail) detail.style.display = 'none';

  if (!state.CU) { if (empty) { empty.style.display = 'block'; } return; }

  try {
    const { data, error } = await sb.rpc('get_saved_izvodi', { p_user_id: state.CU.id });
    if (error) throw error;
    if (!data || !data.length) {
      if (empty) empty.style.display = 'block';
      return;
    }
    if (empty) empty.style.display = 'none';

    // Tabela sa izvoda
    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
    const tbl = document.createElement('table'); tbl.className = 'dtbl'; tbl.style.fontSize = '12px';
    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    ['\u2610', '#', 'Broj izvoda', 'Datum čuvanja', 'Stavki', 'Duguje (RSD)', 'Potražuje (RSD)', 'PDF fajlovi', ''].forEach(h => {
      const th = document.createElement('th'); th.textContent = h; th.style.whiteSpace = 'nowrap'; htr.appendChild(th);
    });
    thead.appendChild(htr); tbl.appendChild(thead);

    const tbody = document.createElement('tbody');
    data.forEach((iz, i) => {
      const tr = document.createElement('tr');
      tr.style.cursor = 'pointer';
      tr.onmouseover = () => tr.style.background = 'var(--surface)';
      tr.onmouseout = () => tr.style.background = '';

      const tdCk = document.createElement('td'); const ck = document.createElement('input'); ck.type = 'checkbox'; ck.className = 'iz-ck'; ck.value = iz.batch_id; ck.dataset.name = iz.izvod_number; ck.onchange = updateCompareBtn; tdCk.appendChild(ck);
      const td0 = document.createElement('td'); td0.style.cssText = 'color:var(--tx3);font-size:11px'; td0.textContent = i + 1;
      const td1 = document.createElement('td'); td1.style.cssText = 'font-weight:600;color:var(--acc)'; td1.textContent = iz.izvod_number;
      td1.onclick = () => viewSavedIzvod(iz.batch_id, iz.izvod_number);
      const td2 = document.createElement('td'); td2.style.cssText = 'color:var(--tx2);font-size:11px';
      td2.textContent = new Date(iz.saved_at).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const td3 = document.createElement('td'); td3.textContent = iz.entry_count;
      const td4 = document.createElement('td'); td4.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--dng)'; td4.textContent = fNum(iz.total_debit);
      const td5 = document.createElement('td'); td5.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--suc)'; td5.textContent = fNum(iz.total_credit);
      const td6 = document.createElement('td'); td6.style.cssText = 'font-size:11px;color:var(--tx3);max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
      td6.textContent = (iz.source_files || '').replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno[^,]*/g, '');
      td6.title = iz.source_files || '';

      const td7 = document.createElement('td'); td7.style.cssText = 'white-space:nowrap;text-align:right';
      const btnView = document.createElement('button'); btnView.className = 'bico'; btnView.appendChild(svgIcon('eye', 14)); btnView.title = 'Prikaži';
      btnView.onclick = (ev) => { ev.stopPropagation(); viewSavedIzvod(iz.batch_id, iz.izvod_number); };
      const btnDel = document.createElement('button'); btnDel.className = 'bico del'; btnDel.appendChild(svgIcon('trash', 14)); btnDel.title = 'Obriši';
      btnDel.onclick = (ev) => { ev.stopPropagation(); deleteSavedIzvod(iz.batch_id, iz.izvod_number); };
      td7.appendChild(btnView); td7.appendChild(btnDel);

      tr.appendChild(tdCk); tr.appendChild(td0); tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3);
      tr.appendChild(td4); tr.appendChild(td5); tr.appendChild(td6); tr.appendChild(td7);
      tbody.appendChild(tr);
    });
    tbl.appendChild(tbody); card.appendChild(tbl); list.appendChild(card);

    // Compare button (hidden until 2 selected)
    const cmpBar = document.createElement('div'); cmpBar.id = 'izCompareBar';
    cmpBar.style.cssText = 'margin-top:12px;display:none';
    const cmpBtn = document.createElement('button'); cmpBtn.className = 'bsm'; cmpBtn.id = 'izCompareBtn';
    cmpBtn.appendChild(svgIcon('chart', 14)); cmpBtn.appendChild(document.createTextNode(' Uporedi odabrane izvode'));
    cmpBtn.onclick = compareSelectedIzvodi;
    cmpBar.appendChild(cmpBtn);
    list.appendChild(cmpBar);
  } catch (e) {
    list.textContent = 'Greška pri učitavanju: ' + (e.message || e);
    list.style.color = 'var(--dng)';
  }
}

function updateCompareBtn() {
  const checked = document.querySelectorAll('.iz-ck:checked');
  const bar = document.getElementById('izCompareBar');
  if (bar) bar.style.display = checked.length >= 2 ? 'block' : 'none';
}

async function compareSelectedIzvodi() {
  const checked = [...document.querySelectorAll('.iz-ck:checked')];
  if (checked.length < 2) { toast('Odaberite bar 2 izvoda', 'err'); return; }
  const sel = checked.slice(0, 2);
  const batchA = sel[0].value, nameA = sel[0].dataset.name;
  const batchB = sel[1].value, nameB = sel[1].dataset.name;

  const detail = document.getElementById('kjSavedDetail');
  const list = document.getElementById('kjSavedList');
  if (!detail) return;
  detail.style.display = 'block';
  detail.textContent = '';
  if (list) list.style.display = 'none';

  const backBar = document.createElement('div'); backBar.style.cssText = 'margin-bottom:12px;display:flex;align-items:center;gap:12px';
  const backBtn = document.createElement('button'); backBtn.className = 'bsm'; backBtn.textContent = '\u2190 Nazad';
  backBtn.onclick = () => { detail.style.display = 'none'; if (list) list.style.display = ''; };
  const title = document.createElement('span'); title.style.cssText = 'font-size:15px;font-weight:700;color:var(--acc)';
  title.textContent = nameA + ' vs ' + nameB;
  backBar.appendChild(backBtn); backBar.appendChild(title);
  detail.appendChild(backBar);

  showLoading();
  try {
    const [resA, resB] = await Promise.all([
      sb.rpc('get_izvod_entries', { p_batch_id: batchA }),
      sb.rpc('get_izvod_entries', { p_batch_id: batchB })
    ]);
    if (resA.error) throw resA.error;
    if (resB.error) throw resB.error;
    const entriesA = (resA.data || []).filter(e => e.detail_level !== 'ANALYTIC');
    const entriesB = (resB.data || []).filter(e => e.detail_level !== 'ANALYTIC');

    // Aggregate by rs_code
    const mapA = {}, mapB = {};
    entriesA.forEach(e => { const k = e.rs_code; if (!mapA[k]) mapA[k] = { name: e.rs_name, debit: 0, credit: 0 }; mapA[k].debit += parseFloat(e.debit || 0); mapA[k].credit += parseFloat(e.credit || 0); });
    entriesB.forEach(e => { const k = e.rs_code; if (!mapB[k]) mapB[k] = { name: e.rs_name, debit: 0, credit: 0 }; mapB[k].debit += parseFloat(e.debit || 0); mapB[k].credit += parseFloat(e.credit || 0); });
    const allKonta = [...new Set([...Object.keys(mapA), ...Object.keys(mapB)])].sort();

    // Summary stats
    const totalDA = entriesA.reduce((s, e) => s + parseFloat(e.debit || 0), 0);
    const totalCA = entriesA.reduce((s, e) => s + parseFloat(e.credit || 0), 0);
    const totalDB = entriesB.reduce((s, e) => s + parseFloat(e.debit || 0), 0);
    const totalCB = entriesB.reduce((s, e) => s + parseFloat(e.credit || 0), 0);

    const stBar = document.createElement('div'); stBar.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px';
    [{ name: nameA, d: totalDA, c: totalCA, cnt: entriesA.length }, { name: nameB, d: totalDB, c: totalCB, cnt: entriesB.length }].forEach(s => {
      const card = document.createElement('div'); card.className = 'card'; card.style.padding = '16px';
      const h = document.createElement('div'); h.style.cssText = 'font-weight:700;font-size:14px;color:var(--acc);margin-bottom:8px'; h.textContent = s.name;
      const info = document.createElement('div'); info.style.cssText = 'font-size:13px;color:var(--tx2)';
      info.innerHTML = ''; // use DOM
      [s.cnt + ' stavki', 'Duguje: <span style="color:var(--dng);font-weight:600">' + fNum(s.d) + '</span>', 'Potražuje: <span style="color:var(--suc);font-weight:600">' + fNum(s.c) + '</span>'].forEach(t => {
        const d = document.createElement('div'); d.style.padding = '2px 0';
        // Safe: fNum returns formatted numbers, not user data
        const tmpl = document.createElement('template'); tmpl.innerHTML = t; d.appendChild(tmpl.content);
        info.appendChild(d);
      });
      card.appendChild(h); card.appendChild(info); stBar.appendChild(card);
    });
    detail.appendChild(stBar);

    // Side-by-side table
    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
    const tbl = document.createElement('table'); tbl.className = 'dtbl'; tbl.style.fontSize = '12px';
    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    ['Konto', 'Naziv', nameA + ' Dug.', nameA + ' Pot.', nameB + ' Dug.', nameB + ' Pot.', '\u0394 Dug.', '\u0394 Pot.'].forEach(h => {
      const th = document.createElement('th'); th.textContent = h; th.style.whiteSpace = 'nowrap'; htr.appendChild(th);
    });
    thead.appendChild(htr); tbl.appendChild(thead);

    const tbody = document.createElement('tbody');
    allKonta.forEach(k => {
      const a = mapA[k] || { name: '', debit: 0, credit: 0 };
      const b = mapB[k] || { name: '', debit: 0, credit: 0 };
      const dd = b.debit - a.debit, dc = b.credit - a.credit;
      const tr = document.createElement('tr');
      if (!mapA[k]) tr.style.background = 'rgba(0,232,157,.04)';
      if (!mapB[k]) tr.style.background = 'rgba(255,92,106,.04)';

      const td1 = document.createElement('td'); td1.style.cssText = 'font-family:var(--mn);font-weight:600'; td1.textContent = k;
      const td2 = document.createElement('td'); td2.textContent = a.name || b.name;
      const td3 = document.createElement('td'); td3.style.cssText = 'text-align:right;font-family:var(--mn)'; td3.textContent = a.debit ? fNum(a.debit) : '';
      const td4 = document.createElement('td'); td4.style.cssText = 'text-align:right;font-family:var(--mn)'; td4.textContent = a.credit ? fNum(a.credit) : '';
      const td5 = document.createElement('td'); td5.style.cssText = 'text-align:right;font-family:var(--mn)'; td5.textContent = b.debit ? fNum(b.debit) : '';
      const td6 = document.createElement('td'); td6.style.cssText = 'text-align:right;font-family:var(--mn)'; td6.textContent = b.credit ? fNum(b.credit) : '';
      const td7 = document.createElement('td'); td7.style.cssText = 'text-align:right;font-family:var(--mn);font-weight:600;color:' + (dd > 0 ? 'var(--dng)' : dd < 0 ? 'var(--suc)' : 'var(--tx3)');
      td7.textContent = dd !== 0 ? (dd > 0 ? '+' : '') + fNum(dd) : '';
      const td8 = document.createElement('td'); td8.style.cssText = 'text-align:right;font-family:var(--mn);font-weight:600;color:' + (dc > 0 ? 'var(--suc)' : dc < 0 ? 'var(--dng)' : 'var(--tx3)');
      td8.textContent = dc !== 0 ? (dc > 0 ? '+' : '') + fNum(dc) : '';

      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4);
      tr.appendChild(td5); tr.appendChild(td6); tr.appendChild(td7); tr.appendChild(td8);
      tbody.appendChild(tr);
    });
    tbl.appendChild(tbody); card.appendChild(tbl); detail.appendChild(card);
  } catch (e) {
    const p = document.createElement('div'); p.style.color = 'var(--dng)'; p.textContent = 'Greška: ' + (e.message || e);
    detail.appendChild(p);
  }
  hideLoading();
}

// ============================================================
// PRIKAZ DETALJA SAČUVANOG IZVODA
// ============================================================
async function viewSavedIzvod(batchId, izvodNumber) {
  const detail = document.getElementById('kjSavedDetail');
  const list = document.getElementById('kjSavedList');
  if (!detail) return;
  detail.style.display = 'block';
  detail.textContent = '';
  if (list) list.style.display = 'none';

  // Back dugme + export
  const backBar = document.createElement('div'); backBar.style.cssText = 'margin-bottom:12px;display:flex;align-items:center;gap:12px';
  const backBtn = document.createElement('button'); backBtn.className = 'bsm'; backBtn.textContent = '\u2190 Nazad na listu';
  backBtn.onclick = () => { detail.style.display = 'none'; if (list) list.style.display = ''; };
  const title = document.createElement('span'); title.style.cssText = 'font-size:15px;font-weight:700;color:var(--acc);flex:1'; title.textContent = izvodNumber;
  const expBtn = document.createElement('button'); expBtn.className = 'bsm'; expBtn.appendChild(svgIcon('download', 14));
  expBtn.appendChild(document.createTextNode(' Export Excel')); expBtn.onclick = () => expSavedIzvodXlsx(batchId, izvodNumber);
  backBar.appendChild(backBtn); backBar.appendChild(title); backBar.appendChild(expBtn);
  detail.appendChild(backBar);

  try {
    const { data, error } = await sb.rpc('get_izvod_entries', { p_batch_id: batchId });
    if (error) throw error;
    if (!data || !data.length) {
      const p = document.createElement('div'); p.style.color = 'var(--tx3)'; p.textContent = 'Nema stavki.';
      detail.appendChild(p); return;
    }

    // Statistika
    const nonAnalytic = data.filter(e => e.detail_level !== 'ANALYTIC');
    const totalD = nonAnalytic.reduce((s, e) => s + parseFloat(e.debit || 0), 0);
    const totalC = nonAnalytic.reduce((s, e) => s + parseFloat(e.credit || 0), 0);
    const stBar = document.createElement('div'); stBar.className = 'stbar'; stBar.style.marginBottom = '16px';
    [
      { l: 'Stavki', v: data.length },
      { l: 'Duguje (RSD)', v: fNum(totalD), c: 'var(--dng)' },
      { l: 'Potražuje (RSD)', v: fNum(totalC), c: 'var(--suc)' },
      { l: 'Balans', v: fNum(Math.abs(totalD - totalC)), c: Math.abs(totalD - totalC) < 1 ? 'var(--suc)' : 'var(--warn)' }
    ].forEach(s => {
      const d = document.createElement('div'); d.className = 'sti';
      const sl = document.createElement('div'); sl.className = 'stl'; sl.textContent = s.l;
      const sv = document.createElement('div'); sv.className = 'stv'; sv.textContent = s.v; if (s.c) sv.style.color = s.c;
      d.appendChild(sl); d.appendChild(sv); stBar.appendChild(d);
    });
    detail.appendChild(stBar);

    // Grupiši po source_file
    const byFile = {};
    data.forEach(e => {
      const key = e.source_file || 'Nepoznat';
      if (!byFile[key]) byFile[key] = [];
      byFile[key].push(e);
    });

    for (const [file, entries] of Object.entries(byFile)) {
      const shortName = file.replace(/FINANSIJSKO KNJIZENJE /g, '').replace(/_Privredno.*/, '');
      const fhdr = document.createElement('div');
      fhdr.style.cssText = 'padding:10px 16px;background:var(--surface);border:1px solid var(--border);border-radius:var(--rs);margin-bottom:2px;font-size:13px;font-weight:600;color:var(--tx)';
      fhdr.textContent = shortName;
      detail.appendChild(fhdr);

      const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto;margin-bottom:16px';
      const tbl = document.createElement('table'); tbl.className = 'dtbl'; tbl.style.fontSize = '12px';
      const thead = document.createElement('thead');
      const htr = document.createElement('tr');
      ['RS Konto', 'Naziv (RS)', 'EN Konto', 'Naziv (EN)', 'Analitika', 'Duguje (RSD)', 'Potražuje (RSD)', 'Napomena'].forEach(h => {
        const th = document.createElement('th'); th.textContent = h; th.style.whiteSpace = 'nowrap'; htr.appendChild(th);
      });
      thead.appendChild(htr); tbl.appendChild(thead);

      const tbody = document.createElement('tbody');
      let fileDug = 0, filePot = 0;
      entries.forEach(e => {
        const tr = document.createElement('tr');
        const isA = e.detail_level === 'ANALYTIC';
        const isP = e.detail_level === 'PARENT';
        if (isA) tr.style.borderLeft = '3px solid var(--acc)';
        if (isP) tr.style.cssText += ';background:var(--surface);font-weight:700;border-top:2px solid var(--border)';

        const td1 = document.createElement('td'); td1.style.cssText = 'font-family:var(--mn);font-weight:600';
        if (isA) { td1.style.cssText = 'font-family:var(--mn);padding-left:20px;color:var(--tx3);font-size:11px'; td1.textContent = '└ ' + (e.analytic_code || e.rs_code); }
        else { td1.textContent = e.rs_code; }

        const td2 = document.createElement('td');
        if (isA) { td2.style.cssText = 'color:var(--tx3);font-size:11px'; td2.textContent = e.rs_name; }
        else { td2.style.fontWeight = '600'; td2.textContent = e.rs_name; }

        const td3 = document.createElement('td'); td3.style.cssText = 'font-family:var(--mn);color:var(--acc);font-size:11px'; td3.textContent = e.en_code || '';
        const td3b = document.createElement('td'); td3b.style.cssText = 'font-size:11px;color:var(--tx2)'; td3b.textContent = e.en_name || '';
        const td4 = document.createElement('td');
        if (isA && e.analytic_name) { td4.style.cssText = 'color:var(--tx);font-weight:500'; td4.textContent = e.analytic_name; }

        const deb = parseFloat(e.debit || 0);
        const cre = parseFloat(e.credit || 0);
        const td5 = document.createElement('td'); td5.style.cssText = 'text-align:right;font-family:var(--mn)';
        if (deb) { td5.textContent = fNum(deb); td5.style.color = 'var(--dng)'; if (e.detail_level !== 'ANALYTIC') fileDug += deb; }
        const td6 = document.createElement('td'); td6.style.cssText = 'text-align:right;font-family:var(--mn)';
        if (cre) { td6.textContent = fNum(cre); td6.style.color = 'var(--suc)'; if (e.detail_level !== 'ANALYTIC') filePot += cre; }
        const td7 = document.createElement('td'); td7.style.cssText = 'font-size:11px;color:var(--tx3);max-width:180px;overflow:hidden;text-overflow:ellipsis'; td7.textContent = e.notes || '';

        tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td3b); tr.appendChild(td4); tr.appendChild(td5); tr.appendChild(td6); tr.appendChild(td7);
        tbody.appendChild(tr);
      });

      // Total red
      const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--border)';
      const fSaldo = fileDug - filePot;
      const tt1 = document.createElement('td'); tt1.colSpan = 5; tt1.style.cssText = 'text-align:right;padding-right:12px;color:var(--tx2)'; tt1.textContent = 'UKUPNO:';
      const tt2 = document.createElement('td'); tt2.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--dng)'; tt2.textContent = fNum(fileDug);
      const tt3 = document.createElement('td'); tt3.style.cssText = 'text-align:right;font-family:var(--mn);color:var(--suc)'; tt3.textContent = fNum(filePot);
      const ttS = document.createElement('td'); ttS.style.cssText = 'text-align:right;font-family:var(--mn);font-weight:700;color:' + (Math.abs(fSaldo) < 1 ? 'var(--suc)' : 'var(--dng)');
      ttS.textContent = Math.abs(fSaldo) < 1 ? '0' : 'saldo: ' + fNum(fSaldo);
      const tt4 = document.createElement('td');
      ttr.appendChild(tt1); ttr.appendChild(tt2); ttr.appendChild(tt3); ttr.appendChild(ttS); ttr.appendChild(tt4);
      tbody.appendChild(ttr);

      tbl.appendChild(tbody); card.appendChild(tbl); detail.appendChild(card);
    }
  } catch (e) {
    const p = document.createElement('div'); p.style.color = 'var(--dng)'; p.textContent = 'Greška: ' + (e.message || e);
    detail.appendChild(p);
  }
}

// ============================================================
// BRISANJE SAČUVANOG IZVODA
// ============================================================
async function deleteSavedIzvod(batchId, izvodNumber) {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  showConfirm('Obrisati izvod "' + izvodNumber + '"? Ova akcija se ne može poništiti.', async () => {
    try {
      const { error } = await sb.rpc('delete_izvod', { p_user_id: state.CU.id, p_batch_id: batchId });
      if (error) throw error;
      toast('Izvod "' + izvodNumber + '" obrisan');
      loadSavedIzvodi();
    } catch (e) {
      toast('Greška: ' + (e.message || e), 'err');
    }
  });
}

// ============================================================
// POST-SAVE NOTIFICATIONS — nova konta i nematčovani dobavljači
// ============================================================
function showPostSaveNotifications(newKonta, unmatchedNames) {
  const out = document.getElementById('kjOut');
  if (!out) return;

  const panel = document.createElement('div');
  panel.className = 'card';
  panel.style.cssText = 'padding:18px;margin-bottom:16px;border:1px solid var(--warn);background:rgba(232,184,77,.06)';

  if (newKonta.length) {
    const h = document.createElement('div');
    h.style.cssText = 'font-size:13px;font-weight:600;color:var(--warn);margin-bottom:8px';
    h.textContent = 'Konta bez početnog stanja (' + newKonta.length + '):';
    panel.appendChild(h);
    const info = document.createElement('div');
    info.style.cssText = 'font-size:11px;color:var(--tx3);margin-bottom:8px';
    info.textContent = 'Da li ova konta trebaju početno stanje na 01.01? Izbor se pamti zauvek.';
    panel.appendChild(info);
    newKonta.forEach(k => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 0;font-size:12px';
      const sp = document.createElement('span');
      sp.style.cssText = 'font-family:var(--mn);color:var(--acc);font-weight:600;min-width:50px';
      sp.textContent = k;
      // Dugme: TREBA početno stanje
      const btnYes = document.createElement('button');
      btnYes.className = 'bsm';
      btnYes.style.cssText = 'font-size:10px;padding:2px 8px;background:var(--suc);color:#fff;border-color:var(--suc)';
      btnYes.textContent = 'Treba PS';
      btnYes.onclick = async () => {
        try {
          const mapped = window.__ft_kontniPlan?.find(kp => kp.konto === k);
          await sb.from('pocetno_stanje').insert({ konto: k, naziv_konta: mapped?.rsName || k, duguje: 0, potrazuje: 0, tip: 'S' });
          row.innerHTML = '<span style="font-family:var(--mn);color:var(--suc);font-weight:600">' + k + '</span> <span style="color:var(--suc);font-size:11px">✓ Kreirano — unesite iznos na stranici Početno stanje</span>';
          toast(k + ' dodato u početno stanje (D=0, P=0). Unesite iznos.');
        } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
      };
      // Dugme: NE TREBA početno stanje
      const btnNo = document.createElement('button');
      btnNo.className = 'bsm';
      btnNo.style.cssText = 'font-size:10px;padding:2px 8px';
      btnNo.textContent = 'Ne treba PS';
      btnNo.onclick = async () => {
        try {
          const mapped = window.__ft_kontniPlan?.find(kp => kp.konto === k);
          await sb.from('pocetno_stanje').insert({ konto: k, naziv_konta: mapped?.rsName || k, duguje: 0, potrazuje: 0, tip: 'X' });
          row.innerHTML = '<span style="font-family:var(--mn);color:var(--tx3)">' + k + '</span> <span style="color:var(--tx3);font-size:11px">— neće se više pitati</span>';
        } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
      };
      row.appendChild(sp);
      row.appendChild(btnYes);
      row.appendChild(btnNo);
      panel.appendChild(row);
    });
  }

  if (unmatchedNames.length) {
    if (newKonta.length) { const sep = document.createElement('hr'); sep.style.cssText = 'border:none;border-top:1px solid var(--border);margin:12px 0'; panel.appendChild(sep); }
    const h = document.createElement('div');
    h.style.cssText = 'font-size:13px;font-weight:600;color:var(--warn);margin-bottom:8px';
    h.textContent = 'Dobavljači nisu u bazi komitenata (' + unmatchedNames.length + '):';
    panel.appendChild(h);
    const kfData = window.__ft_kfData || [];
    unmatchedNames.forEach(item => {
      const name = typeof item === 'object' ? item.name : item;
      const code = typeof item === 'object' ? item.code : '';
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:6px 0;font-size:12px;flex-wrap:wrap';
      const sp = document.createElement('span');
      sp.style.cssText = 'color:var(--tx2);min-width:180px';
      if (code) { const codeSpan = document.createElement('span'); codeSpan.style.cssText = 'font-family:var(--mn);color:var(--acc);font-weight:600;margin-right:6px'; codeSpan.textContent = code; sp.appendChild(codeSpan); }
      sp.appendChild(document.createTextNode(name.replace(/^~+\s*/, '')));

      // Dugme: Dodaj kao novog
      const btnAdd = document.createElement('button');
      btnAdd.className = 'bsm';
      btnAdd.style.cssText = 'font-size:10px;padding:2px 8px;background:var(--acc);color:#fff;border-color:var(--acc)';
      btnAdd.textContent = '+ Novi';
      btnAdd.onclick = async () => {
        try {
          const cleanName = name.replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim();
          await sb.from('komitenti_full').insert({ naziv: cleanName, sifra: 0 });
          if (window.__ft_kfData) window.__ft_kfData.push({ naziv: cleanName, sifra: 0 });
          row.innerHTML = '<span style="color:var(--suc);font-size:11px">\u2713 ' + cleanName + ' dodat kao novi komitent</span>';
        } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
      };

      // Dugme: Dodeli postojećem — otvara searchable dropdown
      const btnAssign = document.createElement('button');
      btnAssign.className = 'bsm';
      btnAssign.style.cssText = 'font-size:10px;padding:2px 8px';
      btnAssign.textContent = '\u2194 Dodeli';
      const dropWrap = document.createElement('div');
      dropWrap.style.cssText = 'display:none;width:100%;margin-top:4px';
      btnAssign.onclick = () => {
        if (dropWrap.style.display === 'none') {
          dropWrap.style.display = 'block';
          dropWrap.textContent = '';
          const search = document.createElement('input'); search.type = 'text'; search.className = 'fi';
          search.style.cssText = 'width:100%;font-size:12px;padding:6px 10px;margin-bottom:4px;border-radius:6px';
          search.placeholder = 'Pretraži komitente...';
          const listDiv = document.createElement('div');
          listDiv.style.cssText = 'max-height:200px;overflow-y:auto;border:1px solid var(--border);border-radius:6px;background:var(--surface)';
          function renderList(q) {
            listDiv.textContent = '';
            const filt = q ? kfData.filter(k => {
              const n = (k.naziv || '').toLowerCase();
              const s = String(k.sifra || '');
              return n.includes(q.toLowerCase()) || s.includes(q);
            }) : kfData.slice(0, 50);
            filt.slice(0, 50).forEach(k => {
              const opt = document.createElement('div');
              opt.style.cssText = 'padding:6px 10px;cursor:pointer;font-size:12px;border-bottom:1px solid var(--border-lt);transition:background .15s';
              opt.onmouseenter = () => opt.style.background = 'var(--hover)';
              opt.onmouseleave = () => opt.style.background = '';
              const sifraSpan = document.createElement('span'); sifraSpan.style.cssText = 'font-family:var(--mn);color:var(--acc);font-weight:600;margin-right:8px'; sifraSpan.textContent = k.sifra || '—';
              const nameSpan = document.createElement('span'); nameSpan.style.color = 'var(--tx)'; nameSpan.textContent = k.naziv || '';
              opt.appendChild(sifraSpan); opt.appendChild(nameSpan);
              opt.onclick = () => {
                // Upari: ažuriraj sve analitike sa ovim imenom da koriste izabranog komitenta
                const ocrName = name.replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim();
                const dbName = (k.naziv || '').trim();
                const dbSifra = String(k.sifra || '');
                kjParsedEntries.filter(e => e.detailLevel === 'ANALYTIC' && e.analyticName).forEach(e => {
                  const aN = (e.analyticName || '').replace(/^~+\s*/, '').replace(/\s+[\d.,]+\s*$/, '').trim();
                  if (aN === ocrName) { e.analyticName = dbName; if (dbSifra && dbSifra !== '0') e.analyticCode = dbSifra; }
                });
                row.innerHTML = '<span style="color:var(--suc);font-size:11px">\u2713 ' + ocrName + ' \u2192 ' + dbName + ' (šifra ' + dbSifra + ')</span>';
                renderKjResults();
                toast(ocrName + ' \u2192 ' + dbName);
              };
              listDiv.appendChild(opt);
            });
            if (!filt.length) { const empty = document.createElement('div'); empty.style.cssText = 'padding:10px;color:var(--tx3);font-size:11px'; empty.textContent = 'Nema rezultata'; listDiv.appendChild(empty); }
          }
          search.oninput = () => renderList(search.value);
          dropWrap.appendChild(search); dropWrap.appendChild(listDiv);
          renderList('');
          setTimeout(() => search.focus(), 50);
        } else { dropWrap.style.display = 'none'; }
      };

      row.appendChild(sp); row.appendChild(btnAdd); row.appendChild(btnAssign); row.appendChild(dropWrap);
      panel.appendChild(row);
    });
  }

  // Dismiss dugme
  const dismiss = document.createElement('div');
  dismiss.style.cssText = 'text-align:right;margin-top:12px';
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'bsm';
  dismissBtn.textContent = 'Zatvori';
  dismissBtn.onclick = () => panel.remove();
  dismiss.appendChild(dismissBtn);
  panel.appendChild(dismiss);

  out.insertBefore(panel, out.firstChild);
}

// ============================================================
// CLEAR DATA — "Počni ponovo" za knjiženja
// ============================================================
export function clearKjData() {
  showConfirm('Obrisati sve učitane PDF-ove i parsirane podatke?', () => {
    kjPdfFiles = [];
    kjParsedEntries = [];
    document.getElementById('pdfList').textContent = '';
    document.getElementById('kjOut').textContent = '';
    document.getElementById('kjStats').style.display = 'none';
    document.getElementById('kjExpBar').style.display = 'none';
    const prog = document.getElementById('ocrProgress');
    if (prog) prog.style.display = 'none';
    updateKjBtnState();
    toast('Podaci obrisani');
  });
}

// ============================================================
// EXPORT SAČUVANOG IZVODA KAO EXCEL
// ============================================================
export async function expSavedIzvodXlsx(batchId, izvodNumber) {
  try {
    const { data, error } = await sb.rpc('get_izvod_entries', { p_batch_id: batchId });
    if (error) throw error;
    if (!data || !data.length) { toast('Nema podataka', 'err'); return; }

    const wb = XLSX.utils.book_new();
    const hdr = ['*JournalNo', '*JournalDate', 'AccountCode', '*AccountName', 'Debits', 'Credits', 'Description', 'RS Konto', 'RS Naziv'];
    const rows = [hdr];
    const entries = data.filter(e => e.detail_level !== 'PARENT' && e.detail_level !== 'ERROR');
    entries.forEach((e, i) => {
      rows.push([
        izvodNumber + '-' + (i + 1),
        e.booking_date || '',
        e.en_code || '', e.en_name || '',
        parseFloat(e.debit || 0) || '', parseFloat(e.credit || 0) || '',
        (e.analytic_name || e.rs_name || ''),
        e.rs_code || '', e.rs_name || ''
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 40 }, { wch: 10 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Journal Entries');
    XLSX.writeFile(wb, 'izvod_' + izvodNumber.replace(/[^a-zA-Z0-9]/g, '_') + '.xlsx');
    toast('Excel izvod eksportovan');
  } catch (e) {
    toast('Greška: ' + (e.message || e), 'err');
  }
}

window.addEventListener('beforeunload', () => { if (kjWorker) { kjWorker.terminate(); kjWorker = null; } });
